import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, ArrowDown, ChevronsUpDown, Filter } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { BACKEND_URL } from './backendConfig';
import './ProjectReport.css';
import { fmtDateTimeVN } from './timeZone';
// The Project Tracker's own card counters, so this report and that screen can never disagree.
import {
  projectKpiCounts, ColumnFilterMenu, rowPassesColumnFilters, distinctColumnValues,
} from './15_SampleTracker';
// One label table for the manager's three statuses, shared with Ticket Station so the two
// screens cannot call one value by two names.
import { taskStatusLabel } from './ticketStatus';
import { valueLabel } from './valueLabel';
import ModuleHelp from './ModuleHelp';

// Ported from docs/HEVN_Tracker_AppScript/ProjectReport.html (source re-exported 2026-08-01).
const API_BASE = `${BACKEND_URL}/api/development/sample-tracker`;
const PROJECT_API_BASE = `${BACKEND_URL}/api/development/project-tracker`;

// legacy `stages.<key>` names (Portal.html PT_STAGE_ORDER) -> our flattened project[<key>] names
const STAGE_KEY_MAP = {
  ldso: 'ldSoff', proto: 'proto', counter: 'counter', costing: 'costing',
  handoff: 'handOffMeeting', pps: 'ppsPpm', prod: 'production',
  inspection: 'finalInspection', exf: 'exfShipment', payment: 'payment', po: 'poStatus',
};

/* `title` stays English: it names the exported file (ASCII only). What is drawn, and the heading
   inside the workbook, is `labelKey` in the reader's language. */
const REPORT_TEAMS = {
  special: { title: 'Integrated Development Team', labelKey: 'xReport.team.special' },
  inline: { title: 'Inline Development Team', labelKey: 'xReport.team.inline' },
  rd: { title: 'R&D Team', labelKey: 'xReport.team.rd' },
  owner: { title: 'Report for Project Owner', labelKey: 'xReport.team.owner' },
  tickets: { title: 'Report for Ticket Station', labelKey: 'xReport.team.tickets' },
};
/* Max, 2026-09-09: plain "unassigned", not "— unassigned —". One constant, so the label in the
   dropdown and the value the filter matches on can never drift apart — and so the same word appears
   for an owner, a team and a status alike, which is the same idea in all three. */
const UNASSIGNED = 'unassigned';
const SPECIAL_REPORT_DEVELOPERS = ['Amy', 'Annie', 'Bicky', 'Brian', 'Chloe', 'Daisy', 'Jenny'];
const STATUS_FILTER_BASE = ['Priority', 'Ship Finished', 'On Track', 'Concern', 'Delayed', 'On Hold'];
const STATUS_FILTER_ROUND = ['In Progress', 'Advance', 'Rework', 'Confirmed', 'Dropped'];

/* ---------- the reader's language (2026-10-21) ----------
   A column definition keeps its English `t` as an IDENTIFIER — it is the React key and the 'Pin'
   test reads it — and what is DRAWN goes through this table. A label not listed is printed as it
   is. The Excel export reads the rendered table, so the file follows the screen. */
const HEADER_KEYS = {
  'Base ID': 'baseId', 'Style / Item': 'styleItem', Round: 'round', Location: 'location',
  'Project SKU': 'projectSku', 'Development Due': 'devDue', 'RM Ready': 'rmReady', 'Sample Finish': 'sampleFinish',
  Received: 'received', QTY: 'qty', Status: 'status', 'D-day': 'dday', Remarks: 'remarks', Category: 'category',
  Project: 'project', 'Special Project': 'specialProject', 'Assigned to': 'assignedTo', 'EXF Ship date': 'exfShipDate',
  'Weeks left': 'weeksLeft', Item: 'item', 'Est. QTY': 'estQty', 'Project Status': 'projectStatus',
  'L/D S/OFF': 'ldSoff', Proto: 'proto', Counter: 'counter', Costing: 'costing', 'Hand-off Meeting': 'handoffMeeting',
  Alloc: 'alloc', 'Ship Date': 'shipDate', 'Hand-off': 'handoff', 'Shipment Status': 'shipmentStatus',
  Payment: 'payment', 'Due days': 'dueDays', 'Sample QTY': 'sampleQty', 'R&D Round': 'rdRound', Remark: 'remark',
  Pin: 'pin', 'PRJ ID': 'prjId', 'SKU ID': 'skuId', 'PRJ owner': 'prjOwner', 'PIC assigned': 'picAssigned',
  'EXF date': 'exfDate',
};
const headerText = (label) => (HEADER_KEYS[label] ? i18n.t(`xReport.col.${HEADER_KEYS[label]}`) : label);
/* The two "nobody" placeholders the report writes into a VALUE (filters and groups match on them),
   shown in the reader's language only where they are drawn. */
const unassignedText = (v) => (v === UNASSIGNED ? i18n.t('xReport.unassigned') : v);
const devText = (v) => (v === 'Unassigned' ? i18n.t('xReport.unassignedDev') : v);
/* The manager's three ticket statuses, worded as Ticket Station's own cards word them. */
const TK_STATUS_KEYS = { Pending: 'st.tkPending', Done: 'st.tkDone', Cancelled: 'st.tkCancelled' };
const tkStatusText = (v) => {
  const s = String(v == null ? '' : v).trim();
  return TK_STATUS_KEYS[s] ? i18n.t(TK_STATUS_KEYS[s]) : unassignedText(taskStatusLabel(s));
};

const mapProject = (p) => {
  const stages = {};
  Object.entries(STAGE_KEY_MAP).forEach(([legacyKey, ourKey]) => { stages[legacyKey] = p[ourKey] || 'Pending'; });
  return {
    id: p.id,
    groupId: p.groupId || '',
    name: p.projectItem,
    item: p.subItem,
    category: p.category,
    groupCategory: p.groupCategory || '',
    itemCategory: p.productType || '',
    assigned: p.assignedTo,
    // Added for the Project Owner tab (Max, 2026-08-22). The bootstrap row has carried all three for a
    // while; the report simply never read them.
    designer: p.designerName || '',
    owner: p.ownerName || '',
    team: p.teamName || '',
    groupName: p.groupName || '',
    approvalStatus: p.approvalStatus || '',
    // Needed by the "approved this week" table (Max, 2026-08-24); the bootstrap already sent them.
    approvedAt: p.approvedAt || null,
    approvedBy: p.approvedBy || '',
    allocation: p.allocation,
    location: '',
    status: p.status,
    pct: Number.isFinite(p.completePct) ? p.completePct : 0,
    exfDate: p.exfShipDate,
    qty: p.estimateQty,
    typology: p.typology,
    samples: p.sampleId || '',
    // A repeat order makes no sample — the "no sample yet" rule below reads it (2026-10-09).
    repeatOrder: !!p.repeatOrder,
    stages,
  };
};

const mapRound = (r) => ({
  base: r.baseId || '',
  sid: r.sampleId || '',
  style: r.styleItem || '',
  rnd: r.round || '',
  opt: r.option || '',
  loc: r.location || '',
  dev: r.developer || '',
  dl: r.developmentDue || '',
  targetDue: r.roundDue || '',
  matReady: r.rmReadyDate || '',
  sent: r.sampleFinishDate || '',
  recv: r.receivedDate || '',
  cmt: r.commentDate || '',
  outcome: r.outcome || '',
  qty: r.qty,
  dday: r.dDay,
  remarks: r.remarks || '',
  trial: !!r.trialInternal,
  project: r.projectId || '',
  rowIndex: r.sampleRoundId || 0,
});

/* ---------- shared helpers ---------- */

const hasStageValue = (v) => {
  const s = String(v || '').trim();
  return !!s && s !== 'Pending' && s !== 'No' && s !== 'N/A' && s !== '-' && s !== '—' && s !== 'No Payment';
};
const normReportText = (v) => String(v || '').trim().toLowerCase();
// The developer lists below are written as first names, but Assigned To now follows the portal
// accounts, so some rows read "Amy Nguyen" (Max, 2026-08-17). Compare on the first name only,
// otherwise a rename silently drops that developer out of the team split.
const devFirstName = (v) => normReportText(v).split(/\s+/)[0] || '';
const reportProjectCategory = (p) => String((p && (p.category || p.groupCategory)) || '').trim();
const projectDeveloperName = (p) => String((p && (p.assigned || p.developer)) || '').trim();
const isRdAllocation = (p) => {
  const al = String((p && p.allocation) || '').trim().toUpperCase();
  return al === 'R&D' || al === 'RD' || al === 'HEVN R&D';
};
const projectMatchesReportTeam = (p, team) => {
  if (team === 'rd') return isRdAllocation(p);
  if (team !== 'inline') return true;
  const dev = devFirstName(projectDeveloperName(p));
  return SPECIAL_REPORT_DEVELOPERS.map(devFirstName).indexOf(dev) === -1;
};
const isShipFinishedProject = (p) => normReportText(p && p.status) === 'ship finished';
const isPostShipmentProject = (p) => hasStageValue(p && p.stages && p.stages.exf) && isShipFinishedProject(p);
const isSpecialPostHandoffCategory = (p) => {
  const c = normReportText(reportProjectCategory(p));
  return c === 'collaboration' || c === 'special inline';
};
const parseReportDate = (v) => {
  if (!v || v === 'TBA') return null;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
    const parts = v.split('/');
    return new Date(+parts[2], +parts[1] - 1, +parts[0]);
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};
// <input type="date"> speaks YYYY-MM-DD in LOCAL time; toISOString would shift the day in Vietnam.
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const defaultApprovedRange = () => {
  const from = new Date();
  from.setDate(from.getDate() - 6);
  return { from: isoDay(from), to: isoDay(new Date()) };
};

// The approval stamp is a full timestamp; the table only needs the day.
const fmtApprovedDay = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const weeksLeftText = (exfDate) => {
  const d = parseReportDate(exfDate);
  if (!d) return 'TBA';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return `${Math.ceil((d - today) / (7 * 86400000))}W`;
};
const dueDaysValue = (date) => {
  const d = parseReportDate(date);
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};
const uniq = (arr) => {
  const out = [];
  arr.forEach((v) => { if (v && out.indexOf(v) === -1) out.push(v); });
  return out;
};
const uniqReportValues = (items) => {
  const seen = {};
  return (items || []).map((v) => String(v || '').trim()).filter((v) => {
    if (!v || seen[v]) return false;
    seen[v] = true;
    return true;
  }).sort((a, b) => a.localeCompare(b));
};
const sortByProjectName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''))
  || String(a.item || '').localeCompare(String(b.item || ''))
  || String(a.exfDate || '9999-12-31').localeCompare(String(b.exfDate || '9999-12-31'));

// One SKU row per project record (our data model stores one item per project row).
const projectItems = (p) => {
  if (!p) return [{ item: '', itemCategory: '', typology: '', qty: 'TBA' }];
  return [{
    projectId: p.id || '',
    item: p.item || '',
    itemCategory: p.itemCategory || '',
    typology: p.typology || '',
    qty: p.qty || 'TBA',
    samples: p.samples || [],
  }];
};
const projectSkuRows = (projects) => {
  const rows = [];
  (projects || []).forEach((p) => {
    projectItems(p).forEach((it, idx) => rows.push({ p, it, itemIndex: idx }));
  });
  return rows;
};
// Project count = distinct project GROUPS; SKU count = distinct project rows.
const projectCount = (list) => uniq((list || []).map((p) => p.groupId || p.id || p.name)).length;
const skuCount = (list) => uniq((list || []).map((p) => p.id || p.name)).length;
const legacyProjectNameCount = (list) => uniq((list || []).map((p) => p.name || p.id || '')).length;

/* ---------- Est. QTY ----------
 * The project-level report tables show the PROJECT SKU's estimated quantity (`estimateQty`), not the
 * quantity of the samples linked to it — corrected 2026-08-06 on Max's point. A sample round's qty is how
 * many samples were made (2, 3, 4...), which has nothing to do with the order size these sections report on.
 * The sample-level helpers below are kept because Section 1-1 is genuinely sample-driven.
 */
const projectSkuQty = (p, it) => {
  const raw = (it && it.qty !== undefined && it.qty !== null && it.qty !== '') ? it.qty : (p && p.qty);
  if (raw === undefined || raw === null || String(raw).trim() === '') return 'TBA';
  const num = parseFloat(String(raw).replace(/,/g, ''));
  return Number.isNaN(num) ? String(raw) : num.toLocaleString('en-GB');
};

/* ---------- sample QTY (still used by the sample-level Section 1-1) ---------- */

// T?SMP: a trial sample raised outside a project is TSMP-0004-P1, and the SMP-only pattern matched
// none of it — it fell through to uppercasing the whole sample id, so the qty landed under a key no
// base ever has and quietly went missing from the report instead of failing.
const normSampleBaseId = (v) => {
  const s = String(v || '').trim();
  const m = s.match(/^(T?SMP-\d+)/i);
  return m ? m[1].toUpperCase() : s.toUpperCase();
};
let SAMPLE_QTY_BY_BASE = {};
let SAMPLE_QTY_BY_PROJECT = {};
const buildSampleQtyMaps = (rounds) => {
  const byBase = {};
  const byProject = {};
  (rounds || []).forEach((r) => {
    const base = normSampleBaseId(r.base || r.sid);
    if (!base) return;
    const qty = parseFloat(String(r.qty === null || r.qty === undefined ? '' : r.qty).replace(/,/g, ''));
    byBase[base] = Number.isNaN(qty) ? '' : qty;
    const projectId = String(r.project || '').trim();
    if (projectId) {
      if (!byProject[projectId]) byProject[projectId] = {};
      byProject[projectId][base] = byBase[base];
    }
  });
  SAMPLE_QTY_BY_BASE = byBase;
  SAMPLE_QTY_BY_PROJECT = byProject;
};
const projectLinkedSamples = (p, it) => {
  const list = [];
  const add = (v) => {
    if (Array.isArray(v)) { v.forEach(add); return; }
    String(v || '').split(',').map((s) => s.trim()).filter(Boolean).forEach((s) => {
      if (list.indexOf(s) === -1) list.push(s);
    });
  };
  add(it && it.samples);
  add(p && p.samples);
  return list;
};
const sampleQtyForProjectItem = (p, it) => {
  const seen = {};
  let total = 0;
  let hasQty = false;
  const take = (base, qty) => {
    if (!base || seen[base]) return;
    seen[base] = true;
    if (qty !== '' && qty !== undefined && qty !== null && !Number.isNaN(Number(qty))) {
      total += Number(qty);
      hasQty = true;
    }
  };
  projectLinkedSamples(p, it).forEach((s) => take(normSampleBaseId(s), SAMPLE_QTY_BY_BASE[normSampleBaseId(s)]));
  const projectQtyMap = SAMPLE_QTY_BY_PROJECT[String((it && it.projectId) || (p && p.id) || '').trim()] || {};
  Object.keys(projectQtyMap).forEach((base) => take(base, projectQtyMap[base]));
  return hasQty ? total : 'TBA';
};

/* ---------- section membership rules ---------- */

const specialPostHandoffProjects = (list) => (list || []).filter((p) => {
  const statusOk = p.status === 'Ship Finished' || p.status === 'On Track' || p.status === 'Concern';
  const costingOk = normReportText(p.stages && p.stages.costing) === 'complete';
  return isSpecialPostHandoffCategory(p) && statusOk && costingOk && hasStageValue(p.stages && p.stages.handoff);
});
const developmentProjects = (active) => (active || []).filter((p) => {
  const goodStatus = p.status === 'On Track' || p.status === 'Concern';
  const noHandoff = !hasStageValue(p.stages && p.stages.handoff);
  return p.status !== 'On Hold' && p.status !== 'Ship Finished' && (isRdAllocation(p) || (goodStatus && noHandoff));
});
const onHoldProjects = (active) => (active || []).filter((p) => (
  (p.status === 'On Hold' || p.status === 'Inactive') && !hasStageValue(p.stages && p.stages.handoff)
));
// Post-handoff Collaboration / Special-Inline work rolls up to Chloe unless the owner is Amy/Annie/Daisy.
const specialReportDeveloper = (p) => {
  const assigned = p.assigned || 'Unassigned';
  const keepOwnName = ['Amy', 'Annie', 'Daisy'].map(devFirstName).indexOf(devFirstName(assigned)) !== -1;
  return isSpecialPostHandoffCategory(p) && hasStageValue(p && p.stages && p.stages.handoff) && !keepOwnName
    ? 'Chloe'
    : assigned;
};
const teamSectionDeveloper = (p) => p.assigned || 'Unassigned';
const stackChartOwner = (p) => {
  const assigned = String((p && p.assigned) || '').trim();
  if (isSpecialPostHandoffCategory(p) && ['Amy', 'Annie', 'Daisy'].map(devFirstName).indexOf(devFirstName(assigned)) === -1) return 'Chloe';
  return assigned;
};

/* ---------- round accessors + the sample-level (Section 1-1) rows ---------- */

const roundBaseId = (r) => normSampleBaseId(r && (r.base || r.sid));
const roundSampleId = (r) => String((r && r.sid) || '').trim();
const roundDeveloper = (r) => String((r && r.dev) || '').trim() || 'Unassigned';
const roundSampleFinishDate = (r) => String((r && r.sent) || '').trim();
const roundStatus = (r) => String((r && r.outcome) || '').trim() || 'In Progress';
const roundDdayText = (r) => {
  const v = r && (r.dday !== undefined ? r.dday : r.dDay);
  if (v === undefined || v === null || v === '') return '';
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : (n > 0 ? `+${n}d` : `${n}d`);
};
const isClosedRoundOutcome = (outcome) => {
  const s = normReportText(outcome);
  return s === 'confirmed' || s === 'dropped';
};
// "Expected" RM/finish dates are placeholders, not an actual send.
const isActualDateText = (v) => {
  const s = String(v || '').trim();
  if (!s || s === '—' || s === '-') return false;
  return !/expected/i.test(s);
};
const reportRoundSortValue = (r, idx) => {
  const n = Number(r && r.rowIndex);
  return !Number.isNaN(n) && n > 0 ? n : idx + 1;
};
// Latest round per base ID (highest row index wins).
const latestRoundByBase = (rounds) => {
  const byBase = {};
  (rounds || []).forEach((r, idx) => {
    const base = roundBaseId(r);
    if (!base) return;
    const sortVal = reportRoundSortValue(r, idx);
    if (!byBase[base] || sortVal >= byBase[base]._sortVal) byBase[base] = { ...r, _sortVal: sortVal };
  });
  return byBase;
};
/* THE PROJECT BEFORE THE SKU — Max, 2026-09-24: "cho tôi tên project trước tên SKU nha". The
   Style / Item column prints the ROUND's own style text, which is the SKU and says nothing about
   which project it belongs to; "Speed Tool S LT" and "Costco 27S Tac. Chair" sat in one list with
   no way to see that the second is Costco's.

   It is a second LINE rather than a prefix: the column is 16% of a wide table and the project
   names run long, so joining them with a dash would have wrapped every row to three lines to gain
   a fact that reads better above than inside. A round with no project SKU — an orphan sample —
   has no project name, and prints the style alone.

   A one-SKU project is often named after the SKU — eight groups are — so the line is dropped when
   it merely repeats the style below it. Printing the same words twice is not extra information. */
const reportProjectLine = (projectNames, r) => {
  if (!projectNames) return '';
  const name = projectNames.get(String(r.project || '').trim()) || '';
  return normReportText(name) === normReportText(String(r.style || '')) ? '' : name;
};
const latestReportSampleRows = (rounds, projectNames) => {
  const byBase = latestRoundByBase(rounds);
  return Object.keys(byBase).map((base) => {
    const r = byBase[base];
    return {
      base,
      projectName: reportProjectLine(projectNames, r),
      style: String(r.style || '').trim(),
      round: String(r.rnd || '').trim(),
      location: String(r.loc || '').trim(),
      developer: roundDeveloper(r),
      project: String(r.project || '').trim(),
      due: String(r.dl || r.targetDue || '').trim(),
      rmReady: String(r.matReady || '').trim(),
      finish: roundSampleFinishDate(r),
      received: String(r.recv || '').trim(),
      qty: r.qty,
      status: roundStatus(r),
      dday: roundDdayText(r),
      remarks: String(r.remarks || '').trim(),
    };
  /* Ordered by PROJECT, so the rows of one project sit together — Max, 2026-09-24: "sắp xếp theo
     tên project giúp tui luôn nha". Developer stays first because these tables ARE the split by
     developer; base id drops to the tiebreak, since an id says nothing a reader is looking for.
     A row whose project line was dropped sorts under its own style, which is the name it prints —
     bunching every one of them at the top under an empty key would be sorting by our own omission. */
  }).sort((a, b) => a.developer.localeCompare(b.developer)
    || (a.projectName || a.style).localeCompare(b.projectName || b.style)
    || a.style.localeCompare(b.style)
    || a.base.localeCompare(b.base));
};

/* ---------- presentational primitives ---------- */

const PILL_STYLES = {
  'ship finished': { background: '#DBEAFE', color: '#1E40AF' },
  'on track': { background: '#C6EFCE', color: '#276221' },
  concern: { background: '#FFF0CE', color: '#9C5700' },
  delayed: { background: '#FFE0E0', color: '#C00000' },
  complete: { background: '#C6EFCE', color: '#276221' },
  confirmed: { background: '#D9EAD3', color: '#276221' },
  advance: { background: '#E2F0D9', color: '#276221' },
  'in progress': { background: '#DBEAFE', color: '#1E40AF' },
  'waiting feedback': { background: '#FFF2CC', color: '#9C5700' },
  rework: { background: '#FCE4D6', color: '#C55A11' },
  dropped: { background: '#F4CCCC', color: '#C00000' },
  'on hold': { background: '#F3F4F6', color: '#374151' },
};
const Pill = ({ status }) => {
  const v = String(status || '').trim();
  const style = PILL_STYLES[normReportText(v)] || { background: '#F3F4F6', color: '#374151' };
  return <span className="rp-pill" style={style}>{valueLabel(v)}</span>;
};

const StageCell = ({ value, note }) => {
  const v = value || 'Pending';
  if (v === 'Pending' || v === '' || v === 'N/A' || v === 'No Payment') return <td className="sp">—</td>;
  return (
    <td style={{ textAlign: 'center' }}>
      <Pill status={v} />
      {/* The round this state belongs to, when the caller knows it. "Internal Trial" on its own does not
          say which round is being held, and these tables have one line per SKU, not per round. */}
      {note ? <div className="rp-stage-note">{note}</div> : null}
    </td>
  );
};

const DueDaysCell = ({ date, emptyChar = '-' }) => {
  const diff = dueDaysValue(date);
  if (diff === null) return <td className="sp">{emptyChar}</td>;
  const col = diff < 0 ? '#C00000' : diff < 8 ? '#9C5700' : '#1F3864';
  return <td style={{ textAlign: 'center', fontWeight: 700, color: col }}>{diff > 0 ? `+${diff}` : diff}</td>;
};

/* THE SORTABLE HEADER ITSELF, so the two tables that have one cannot end up with two of them.
   `.rp-sort-th` is styled under `.rp-table th`, which is where the white-on-navy fix and the
   print rule live — a second button with its own class would have had to repeat both.
   A column is sortable when it declares a key. Without one the header stays plain text, because
   most of this report is print output where a clickable header would be a lie on paper. */
/* THE FUNNEL IS THE PORTAL'S OWN — Max, 2026-09-30: "thêm filter vào nhé". `ColumnFilterMenu` is
   the Excel-style menu the Project Tracker and Ticket Station already use: a sort pair, a find box,
   the values actually present with tick boxes, and a Year/Month/Day tree on date columns. Writing a
   second one would have given this table a filter that behaves almost like the one three other
   screens teach, which is worse than none.
   It was module-private; exporting it is the whole change on that side. Its styles (`.th-menu…`)
   were never scoped to that screen, so they arrive with the bundle. */
const SortHead = ({ label, note, sortKey, sort, onSort, filter }) => {
  const { t } = useTranslation();
  if (!sortKey || !onSort) return <>{label}{note}</>;
  const active = sort && sort.key === sortKey;
  const on = !!(filter && filter.selected && filter.selected.length);
  /* THE NOTE IS A SECOND LINE, NOT PART OF THE LABEL — Max, 2026-09-30: "format ko đồng nhất ở
     các ô của dòng header". With the note inside the label span, that span was 25px tall against
     14px everywhere else and the flex row centred the arrow against it: measured, the PIC column's
     arrow sat 5px below the other eight. The row holds the label and the arrow, the note sits
     under the row, so every arrow is on the same line whatever the label carries. */
  const head = (
    <button type="button" className="rp-sort-th" onClick={() => onSort(sortKey)}
      title={t('xReport.sortBy', { label })}>
      <span className="rp-sort-row">
        <span>{label}</span>
        {active
          ? (sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
          : <ChevronsUpDown size={12} />}
      </span>
      {note}
    </button>
  );
  if (!filter) return head;
  return (
    <span className="rp-th-wrap">
      {head}
      {/* FIXED-POSITIONED FROM THE BUTTON'S OWN RECT, and opened UPWARDS when there is no room
          below — Section 4 is the last thing on a long page, so a menu that always drops downwards
          puts its OK button past the bottom of the window where it cannot be clicked at all. The
          same arithmetic the tracker's own header does. */}
      <button
        type="button"
        className={`rp-th-funnel ${on ? 'on' : ''}`}
        title={label}
        onClick={(e) => {
          e.stopPropagation();
          const r = e.currentTarget.getBoundingClientRect();
          const MIN = 260;
          const below = window.innerHeight - r.bottom - 12;
          const above = r.top - 12;
          const up = below < MIN && above > below;
          filter.onOpen(sortKey, {
            left: Math.round(Math.max(8, Math.min(r.left - 6, window.innerWidth - 250))),
            top: up ? undefined : Math.round(r.bottom + 4),
            bottom: up ? Math.round(window.innerHeight - r.top + 4) : undefined,
            space: Math.round(Math.max(up ? above : below, 180)),
          });
        }}
      >
        <Filter size={11} />
      </button>
      {filter.open && filter.anchor && (
        <ColumnFilterMenu
          label={label}
          isDate={!!filter.isDate}
          values={filter.values || []}
          selected={filter.selected}
          sortDir={active ? sort.dir : ''}
          anchor={filter.anchor}
          onSort={(dir) => onSort(sortKey, dir)}
          onApply={(next, all) => filter.onApply(sortKey, next, all)}
          onClose={filter.onClose}
        />
      )}
    </span>
  );
};
/* One click toggles the direction on the column already sorted, and starts ascending on any other.
   A direction can also be given outright, which is what the filter menu's own Asc / Desc buttons
   do — they say which way rather than asking for the next one. */
const useSortToggle = () => {
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const toggle = useCallback((key, dir) => setSort((p) => (
    dir ? { key, dir }
      : (p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })
  )), []);
  return [sort, toggle];
};

const ReportTable = ({ cols, children, onToggleAll, sort, onSort }) => {
  const { t } = useTranslation();
  return (
  <table className="rp-table" style={{ tableLayout: 'fixed' }}>
    <thead>
      <tr>
        {cols.map((c) => (
          <th key={c.t} style={{ width: c.w }}
            className={c.k && onSort && sort && sort.key === c.k ? 'rp-sorted' : ''}>
            {c.t === 'Pin' && onToggleAll
              ? <button type="button" className="rp-pin-all" onClick={onToggleAll} title={t('xReport.pinAll')}>{t('xReport.col.pin')}</button>
              : <SortHead label={headerText(c.t)} sortKey={c.k} sort={sort} onSort={onSort} />}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>{children}</tbody>
  </table>
  );
};

const FILTER_NONE = '\u0000NONE';

const filterLabel = (label, selected, labelOf) => {
  if (!selected.length) return label;
  // The "nothing ticked" sentinel is an internal marker, never something to print.
  if (selected.length === 1 && selected[0] === FILTER_NONE) return i18n.t('xReport.filter.none');
  if (selected.length === 1) return labelOf ? labelOf(selected[0]) : selected[0];
  return i18n.t('xReport.filter.selected', { n: selected.length });
};

/** All ticked = no filter. Individual boxes follow All, so ticking All shows every box ticked. */
const filterAllChecked = (selected, values) => !selected.length || selected.length >= values.length;

/**
 * Next selection after one checkbox is toggled.
 *
 * An empty array means "no filter" — every row passes — and that is also how the All checkbox reads as
 * ticked. So unticking a single value while the array is empty used to do nothing at all: filtering out
 * `Amy` produced `[].filter(v => v !== 'Amy')`, still empty, still "show everyone". The fix is to
 * materialise the implicit all — the full option list minus the value just unticked.
 *
 * Unticking the last remaining value must land on the FILTER_NONE sentinel, not on `[]`, or "nothing
 * selected" would flip back to meaning "everything".
 */
const nextFilterSelection = (current, value, checked, values = []) => {
  const base = (current || []).filter((v) => v !== FILTER_NONE);
  const start = base.length ? base : (checked ? [] : values.slice());
  const next = checked
    ? (start.indexOf(value) === -1 ? [...start, value] : start)
    : start.filter((v) => v !== value);
  return next.length ? next : [FILTER_NONE];
};
const filterValueChecked = (selected, value) => !selected.length || selected.indexOf(value) !== -1;

/* `labelOf` renames what an option PRINTS and never what it filters by — the status list holds
   the stored values, so a box ticked as "Open" still narrows the page by `Pending`. */
const MultiFilter = ({ id, label, values, selected, open, onToggleOpen, onChange, onClear, labelOf }) => {
  const { t } = useTranslation();
  return (
  <div className={`rp-filter${open ? ' open' : ''}`} id={`rpFilter_${id}`}>
    <button type="button" className="rp-filter-toggle" onClick={() => onToggleOpen(id)}>
      {filterLabel(label, selected, labelOf)}
    </button>
    <div className="rp-filter-menu">
      {!!values.length && (
        <label className="rp-filter-opt rp-filter-all">
          <input
            type="checkbox"
            checked={filterAllChecked(selected, values)}
            onChange={(e) => onClear(id, e.target.checked)}
          />
          <span>{t('xReport.filter.all')}</span>
        </label>
      )}
      {values.map((v) => (
        <label className="rp-filter-opt" key={v}>
          <input
            type="checkbox"
            checked={filterValueChecked(selected, v)}
            onChange={(e) => onChange(id, v, e.target.checked, values)}
          />
          <span>{labelOf ? labelOf(v) : v}</span>
        </label>
      ))}
      {!values.length && <div className="rp-filter-empty">{t('xReport.filter.noOptions')}</div>}
    </div>
  </div>
  );
};

const NoteBox = ({ noteKey, names, notes, justSaved, onChange }) => {
  const { t } = useTranslation();
  const defaultText = names.map((n) => `${n}: `).join('\n');
  const value = notes[noteKey] !== undefined && notes[noteKey] !== '' ? notes[noteKey] : defaultText;
  return (
    <div className="rp-comment">
      <label>{t('xReport.note.comments')} {justSaved && <span className="saved-tag">{t('xReport.note.saved')}</span>}</label>
      <textarea
        rows={Math.max(4, names.length + 1)}
        value={value}
        onChange={(event) => onChange(noteKey, event.target.value)}
        placeholder={t('xReport.note.placeholder')}
      />
    </div>
  );
};

// splitTableWithNotes: keeps rows of the same project together across a page split so a
// comment box never orphans half of one project's SKUs.
const splitIntoChunks = (list, prefix, pageSize = 20) => {
  if (list.length < pageSize) return [{ key: `${prefix}1`, rows: list }];
  const chunkCount = list.length <= pageSize * 2 ? 2 : Math.ceil(list.length / pageSize);
  const boundaries = [];
  const canSplitAt = (idx) => idx > 0 && idx < list.length && list[idx - 1].name !== list[idx].name;
  for (let c = 1; c < chunkCount; c += 1) {
    const ideal = Math.round((list.length * c) / chunkCount);
    let splitIdx = -1;
    for (let offset = 0; offset <= pageSize; offset += 1) {
      const right = ideal + offset;
      const left = ideal - offset;
      if (canSplitAt(right) && boundaries.indexOf(right) === -1) { splitIdx = right; break; }
      if (canSplitAt(left) && boundaries.indexOf(left) === -1) { splitIdx = left; break; }
    }
    if (splitIdx === -1) splitIdx = ideal;
    if (splitIdx > 0 && splitIdx < list.length && boundaries.indexOf(splitIdx) === -1) boundaries.push(splitIdx);
  }
  boundaries.sort((a, b) => a - b);
  const chunks = [];
  let start = 0;
  boundaries.forEach((end) => { if (end > start) chunks.push(list.slice(start, end)); start = end; });
  if (start < list.length) chunks.push(list.slice(start));
  return chunks.map((rows, idx) => ({ key: `${prefix}${idx + 1}`, rows }));
};

// splitSampleRows: even slices, no note boxes (Section 1-1 has none).
const splitSampleRows = (rows, pageSize = 15) => {
  if (rows.length < pageSize) return [{ rows, start: 1, end: rows.length, total: rows.length }];
  const chunkCount = rows.length <= pageSize * 2 ? 2 : Math.ceil(rows.length / pageSize);
  const out = [];
  for (let i = 0; i < chunkCount; i += 1) {
    const start = Math.round((rows.length * i) / chunkCount);
    const end = Math.round((rows.length * (i + 1)) / chunkCount);
    out.push({ rows: rows.slice(start, end), start: start + 1, end, total: rows.length });
  }
  return out;
};

/* ---------- Section 1-1: Per Developer (sample level) ---------- */

/* Rebalanced when the sort icons arrived: every header lost ~17px to its control, and the columns
   with one-word labels had the least to spare. The width came out of Style and Remarks, which wrap
   onto a second line without looking broken. */
const PER_DEV_COLS = [
  { t: 'Base ID', w: '8%', k: 'base' }, { t: 'Style / Item', w: '16%', k: 'style' },
  { t: 'Round', w: '7%', k: 'round' }, { t: 'Location', w: '7%', k: 'location' },
  { t: 'Project SKU', w: '8%', k: 'project' }, { t: 'Development Due', w: '8%', k: 'due' },
  { t: 'RM Ready', w: '8%', k: 'rmReady' }, { t: 'Sample Finish', w: '8%', k: 'finish' },
  { t: 'Received', w: '8%', k: 'received' }, { t: 'QTY', w: '6%', k: 'qty' },
  { t: 'Status', w: '8%', k: 'status' }, { t: 'D-day', w: '6%', k: 'dday' },
  { t: 'Remarks', w: '13%', k: 'remarks' },
];

/* How each column compares. Dates are already yyyy-mm-dd so they sort as text; QTY and D-day are
   numbers wearing text ("+16d"), and sorting those as text puts +9d after +16d. */
const PER_DEV_NUM = new Set(['qty', 'dday']);
const perDevValue = (row, key) => {
  const raw = row[key];
  if (PER_DEV_NUM.has(key)) {
    const n = Number(String(raw ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  /* The Style / Item cell prints the PROJECT above the style, so its header sorts by the project —
     a column that sorts on half of what it shows reads as broken. The style within a project needs
     no second term here: sort() is stable and the rows arrive already ordered project, style, base. */
  if (key === 'style') {
    const text = String(row.projectName || '').trim() || String(raw ?? '').trim();
    return text || null;
  }
  const text = String(raw ?? '').trim();
  // 'TBA' and '-' are the report's way of writing "not known yet", and neither is a date.
  return text && text !== '-' && text !== 'TBA' ? text : null;
};
/* Blanks last in BOTH directions. A column sorted descending that leads with thirty dashes has not
   answered the question the click was asking.
   The rules are the table's, not one table's: blanks last, numbers numerically, everything else by
   locale with `numeric` so SKU-00099 precedes SKU-00100. What differs per table is only how a row
   answers for a key, which is the `valueOf` argument. */
const reportSort = (rows, sort, valueOf) => {
  if (!sort || !sort.key) return rows;
  const dir = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = valueOf(a, sort.key);
    const y = valueOf(b, sort.key);
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
    return String(x).localeCompare(String(y), undefined, { numeric: true }) * dir;
  });
};
const perDevSort = (rows, sort) => reportSort(rows, sort, perDevValue);

const PerDevTable = React.memo(({ rows, sort, onSort }) => (
  <ReportTable cols={PER_DEV_COLS} sort={sort} onSort={onSort}>
    {rows.map((row) => (
      <tr key={row.base}>
        <td style={{ fontWeight: 700 }}>{row.base}</td>
        <td style={{ fontWeight: 600 }}>
          {!!row.projectName && <em className="rp-projname">{row.projectName}</em>}
          {row.style || '-'}
        </td>
        <td style={{ textAlign: 'center' }}>{row.round || '-'}</td>
        <td style={{ textAlign: 'center' }}>{row.location || '-'}</td>
        <td style={{ textAlign: 'center' }}>{row.project || '-'}</td>
        <td style={{ textAlign: 'center' }}>{row.due || 'TBA'}</td>
        <td style={{ textAlign: 'center' }}>{row.rmReady || '-'}</td>
        <td style={{ textAlign: 'center' }}>{row.finish || '-'}</td>
        <td style={{ textAlign: 'center' }}>{row.received || '-'}</td>
        <td style={{ textAlign: 'center' }}>{row.qty || '-'}</td>
        <td><Pill status={row.status} /></td>
        <td style={{ textAlign: 'center', fontWeight: 700 }}>{row.dday || '-'}</td>
        <td>{row.remarks || '-'}</td>
      </tr>
    ))}
  </ReportTable>
));

const PerDeveloperSection = ({ rows }) => {
  const { t } = useTranslation();
  const [sort, toggleSort] = useSortToggle();
  /* Sorted BEFORE the grouping and before the 15-row page chunks, so one click reorders every
     developer's block and every page of it — not just the block whose header was clicked (Max,
     2026-09-10). The developer grouping stands: this section's whole point is whose work it is. */
  const sorted = useMemo(() => perDevSort(rows, sort), [rows, sort]);

  const groups = useMemo(() => {
    const g = {};
    sorted.forEach((row) => {
      const dev = row.developer || 'Unassigned';
      if (!g[dev]) g[dev] = [];
      g[dev].push(row);
    });
    return g;
  }, [sorted]);
  const devs = useMemo(() => Object.keys(groups).sort((a, b) => a.localeCompare(b)), [groups]);

  if (!rows.length) {
    return (
      <div>
        <div className="rp-sec">{t('xReport.dev.sec11')} <span style={{ fontWeight: 400, fontSize: 12 }}>{t('xReport.dev.samplesN', { n: 0 })}</span></div>
        <p className="rp-empty">{t('xReport.dev.noSamples')}</p>
      </div>
    );
  }
  return (
    <div>
      <div className="rp-sec">{t('xReport.dev.sec11')} <span style={{ fontWeight: 400, fontSize: 12 }}>{t('xReport.dev.samplesN', { n: rows.length })}</span></div>
      {devs.map((dev, devIdx) => (
        <div key={dev} style={{ marginTop: devIdx ? 14 : 0 }}>
          {splitSampleRows(groups[dev], 15).map((chunk, idx) => (
            <div key={`${dev}-${idx}`} style={{ marginTop: idx ? 14 : 0 }}>
              <div className="rp-subsec">
                {devText(dev)}
                <span style={{ fontWeight: 400 }}>
                  {' '}
                  {chunk.total > chunk.rows.length
                    ? t('xReport.dev.chunkOf', { start: chunk.start, end: chunk.end, total: chunk.total })
                    : t('xReport.dev.samplesN', { n: chunk.total })}
                </span>
              </div>
              <PerDevTable rows={chunk.rows} sort={sort} onSort={toggleSort} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/* ---------- Section 1-2: Per Vendor ---------- */

const devSubCols = (team) => {
  const projectColLabel = team === 'inline' ? 'Project' : 'Special Project';
  return [
    { t: 'Category', w: '7%' }, { t: projectColLabel, w: '11%' }, { t: 'Assigned to', w: '6%' },
    { t: 'EXF Ship date', w: '7%' }, { t: 'Weeks left', w: '5%' }, { t: 'Item', w: '13%' },
    { t: 'Est. QTY', w: '5%' }, { t: 'Project Status', w: '7%' }, { t: '%', w: '4%' },
    { t: 'L/D S/OFF', w: '7%' }, { t: 'Proto', w: '7%' }, { t: 'Counter', w: '7%' },
    { t: 'Costing', w: '7%' }, { t: 'Hand-off Meeting', w: '7%' },
  ];
};

const DevTable = React.memo(({ rows, cols }) => (
  <ReportTable cols={cols}>
    {projectSkuRows(rows).map(({ p, it }) => (
      <tr key={p.id}>
        <td>{reportProjectCategory(p)}</td>
        <td style={{ fontWeight: 600 }}>{p.name}</td>
        <td style={{ textAlign: 'center' }}>{p.assigned || '—'}</td>
        <td style={{ textAlign: 'center' }}>{p.exfDate || 'TBA'}</td>
        <td style={{ textAlign: 'center' }}>{weeksLeftText(p.exfDate)}</td>
        <td>{it.item || p.item}</td>
        <td style={{ textAlign: 'center' }}>{projectSkuQty(p, it)}</td>
        <td><Pill status={p.status} /></td>
        <td style={{ textAlign: 'center' }}>{p.pct || 0}%</td>
        <StageCell value={p.stages.ldso} />
        <StageCell value={p.stages.proto} />
        <StageCell value={p.stages.counter} />
        <StageCell value={p.stages.costing} />
        <StageCell value={p.stages.handoff} />
      </tr>
    ))}
  </ReportTable>
));

const DevSubSection = ({ title, notePrefix, list, team, notes, justSaved, onNoteChange }) => {
  const { t } = useTranslation();
  const sorted = useMemo(() => list.slice().sort(sortByProjectName), [list]);
  const chunks = useMemo(() => splitIntoChunks(sorted, notePrefix, 15), [sorted, notePrefix]);
  const cols = useMemo(() => devSubCols(team), [team]);
  if (!list.length) return null;
  return (
    <>
      {chunks.map((chunk, idx) => (
        <div key={chunk.key} style={{ marginBottom: 14, marginTop: idx ? 14 : 0 }}>
          <div className="rp-subsec">
            {title} <span style={{ fontWeight: 400 }}>{t('xReport.projSku', { projects: projectCount(chunk.rows), skus: projectSkuRows(chunk.rows).length })}</span>
          </div>
          <DevTable rows={chunk.rows} cols={cols} />
          <NoteBox noteKey={chunk.key} names={uniq(chunk.rows.map((p) => p.name))} notes={notes} justSaved={justSaved} onChange={onNoteChange} />
        </div>
      ))}
    </>
  );
};

const DevelopmentSection = ({ active, team, notes, justSaved, onNoteChange }) => {
  const { t } = useTranslation();
  const groups = useMemo(() => {
    const g = { MS: [], TS: [], RD: [], Others: [] };
    developmentProjects(active).forEach((p) => {
      const al = String(p.allocation || '').toUpperCase();
      if (al === 'MS') g.MS.push(p);
      else if (al === 'TS') g.TS.push(p);
      else if (al === 'R&D' || al === 'RD' || al === 'HEVN R&D') g.RD.push(p);
      else g.Others.push(p);
    });
    return g;
  }, [active]);

  const shared = { team, notes, justSaved, onNoteChange };
  return (
    <div>
      <div className="rp-sec">{t('xReport.dev.sec12')}</div>
      <DevSubSection title="Metric Supply (MS)" notePrefix="ms" list={groups.MS} {...shared} />
      <DevSubSection title="The Sang (TS)" notePrefix="ts" list={groups.TS} {...shared} />
      <DevSubSection title="HEVN R&D" notePrefix="rd" list={groups.RD} {...shared} />
      <DevSubSection title={t('xReport.dev.otherSuppliers')} notePrefix="oth" list={groups.Others} {...shared} />
    </div>
  );
};

/* ---------- Section 2: Post Handoff ---------- */

const POST_HANDOFF_COLS = [
  { t: 'Category', w: '8%' }, { t: 'Project', w: '13%' }, { t: 'Assigned to', w: '7%' }, { t: 'Alloc', w: '5%' },
  // Item and Est. QTY give up width so Hand-off and Payment stop wrapping "Complete" into "Complet e".
  { t: 'Ship Date', w: '7%' }, { t: 'Item', w: '13%' }, { t: 'Est. QTY', w: '5%' },
  { t: 'Project Status', w: '8%' }, { t: '%', w: '4%' }, { t: 'Hand-off', w: '9%' },
  { t: 'Shipment Status', w: '8%' }, { t: 'Payment', w: '9%' }, { t: 'Due days', w: '4%' },
];

const PostHandoffTable = React.memo(({ rows }) => (
  <ReportTable cols={POST_HANDOFF_COLS}>
    {projectSkuRows(rows).map(({ p, it }) => (
      <tr key={p.id}>
        <td>{reportProjectCategory(p)}</td>
        <td style={{ fontWeight: 600 }}>{p.name}</td>
        <td style={{ textAlign: 'center', fontWeight: 600 }}>{specialReportDeveloper(p)}</td>
        <td style={{ textAlign: 'center' }}>{p.allocation}</td>
        <td style={{ textAlign: 'center' }}>{p.exfDate || 'TBA'}</td>
        <td>{it.item || p.item}</td>
        <td style={{ textAlign: 'right' }}>{projectSkuQty(p, it)}</td>
        <td><Pill status={p.status} /></td>
        <td style={{ textAlign: 'center' }}>{p.pct || 0}%</td>
        <StageCell value={p.stages.handoff} />
        <StageCell value={p.stages.exf} />
        <StageCell value={p.stages.payment} />
        <DueDaysCell date={p.stages.payment} />
      </tr>
    ))}
  </ReportTable>
));

const PostHandoffSection = ({ list, notes, justSaved, onNoteChange }) => {
  const { t } = useTranslation();
  const chunks = useMemo(() => splitIntoChunks(list, 't', 20), [list]);
  return (
    <div>
      <div className="rp-sec">
        {t('xReport.dev.sec2')} <span style={{ fontWeight: 400, fontSize: 12 }}>{t('xReport.projSku', { projects: projectCount(list), skus: skuCount(list) })}</span>
      </div>
      {list.length ? chunks.map((chunk, idx) => (
        <div key={chunk.key} style={idx ? { marginTop: 14 } : undefined}>
          <PostHandoffTable rows={chunk.rows} />
          <NoteBox noteKey={chunk.key} names={uniq(chunk.rows.map((p) => p.name))} notes={notes} justSaved={justSaved} onChange={onNoteChange} />
        </div>
      )) : <p className="rp-empty">{t('xReport.dev.noProjects')}</p>}
    </div>
  );
};

const OnHoldSection = ({ active, team, notes, justSaved, onNoteChange }) => {
  const { t } = useTranslation();
  const list = useMemo(() => onHoldProjects(active), [active]);
  return (
    <div>
      <div className="rp-sec">{t('xReport.dev.onHoldSec')}</div>
      {list.length
        ? <DevSubSection title={t('xReport.dev.onHoldTitle')} notePrefix="oh" list={list} team={team} notes={notes} justSaved={justSaved} onNoteChange={onNoteChange} />
        : <p className="rp-empty">{t('xReport.dev.noOnHold')}</p>}
    </div>
  );
};

/* ================= Report for Ticket Station (Max, 2026-09-14) ==========================
   "thêm giúp tui phần báo cáo về ticket station" — the shape is mine, for him to correct.

   WHAT THIS REPORT IS FOR. Ticket Station already lists tickets; a list is not a report. The
   question a report has to answer is "where is work stuck and who is it waiting on", so the page is
   ordered by what somebody would act on: the things that are wrong first, then who is holding them,
   then who is raising them, then the shape of the queue.

   WHAT THE DATA SAYS TODAY, measured before any of this was drawn — because a report designed
   against imagined data is full of empty tables:
     45 tickets, 44 still open, exactly 1 Done. 26 have NEVER been emailed. 7 are past their due
     date. The oldest open one is 25 days. Tina raised 34 of the 45 and 25 of hers were never sent.
   So there is no turnaround to report on yet (one closed ticket is not a trend), and the thing worth
   putting at the top is the pile nobody can see: a ticket that was raised and never sent is not a
   slow ticket, it is a request the receiving team has never heard of. That is exactly what produced
   Sanghyun's mail this morning about TSK-0047/48/49.
   ====================================================================================== */

const TICKET_OPEN = (t) => !['Done', 'Cancelled'].includes(String(t.status || 'Pending'));
const ticketAgeDays = (t) => {
  if (!t.createdAt) return null;
  const ms = Date.now() - new Date(t.createdAt).getTime();
  return Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 86400000)) : null;
};
// Past its due date and still open. dDay is positive when a ticket is late (flipped 2026-08-12),
// and a ticket with no due date is not overdue — it is undated, which the report says separately.
const ticketOverdue = (t) => TICKET_OPEN(t) && Number.isFinite(t.dDay) && t.dDay > 0;
/* One attention list. Kept as a component because all three read the same way and only differ in
   what they are counting — three copies would drift the moment one of them got a column. */
const TicketFlagTable = ({ title, tone, rows, note, empty }) => {
  const { t } = useTranslation();
  return (
  <div className="rp-owner-sec">
    <div className="rp-sec">{title} <span style={{ fontWeight: 400, fontSize: 12 }}>({rows.length})</span></div>
    {note && <p className="rp-tk-note">{note}</p>}
    {!rows.length ? <p className="rp-empty">{empty}</p> : (
      <table className="rp-table rp-tk-table">
        <thead>
          <tr>
            <th style={{ width: 96 }}>{t('xReport.tk.colTicket')}</th>
            <th style={{ width: 104 }}>{t('xReport.tk.colProject')}</th>
            {/* Max, 2026-09-15. A code says which project only to somebody who already knows the
                codes; these three lists are read by people chasing a ticket, not by the person who
                filed it. Fixed width so it comes out of the description column, which is the one
                built to be elided — giving the table another flexible column would hand the slack
                back to every column and undo the widths above. */}
            <th style={{ width: 220 }}>{t('xReport.tk.colProjectName')}</th>
            <th>{t('xReport.tk.colAsked')}</th>
            <th style={{ width: 140 }}>{t('xReport.tk.colReceiving')}</th>
            <th style={{ width: 100 }}>{t('xReport.tk.colOwner')}</th>
            <th style={{ width: 96 }}>{t('xReport.tk.colDue')}</th>
            {/* Two statuses, because the ticket has two and they disagree — Max, 2026-09-15. The
                requester's is what OPEN counts; the assignee's is the receiving team's own reply.
                One column headed "Status" showed the first and read like the only one. */}
            <th style={{ width: 92 }}>{t('xReport.tk.colManager')}</th>
            <th style={{ width: 92 }}>{t('xReport.tk.colAssignee')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((tk) => (
            <tr key={tk.taskId}>
              <td className="rp-tk-id">{tk.taskId}</td>
              <td>{tk.projectId || '—'}</td>
              <td className="rp-tk-proj" title={tk.projectName || ''}>{tk.projectName || '—'}</td>
              <td className="rp-tk-name" title={tk.name}>{tk.taskType ? `${tk.taskType} · ` : ''}{tk.name}</td>
              <td>{tk.department || '—'}</td>
              <td>{tk.ownerName || '—'}</td>
              <td className={ticketOverdue(tk) ? 'rp-tk-late' : ''}>{tk.dueDate || '—'}</td>
              <td><span className={`rp-tk-pill ${tone}`}>{tkStatusText(tk.status || 'Pending')}</span></td>
              {/* Coloured by its own value, not by the section's tone: the section is about the
                  requester's status, and painting the assignee's the same colour would make two
                  different answers look like one. */}
              <td>{tk.assigneeStatus
                ? <span className={`rp-tk-pill ${tk.assigneeStatus === 'Done' ? 'ok' : 'mute'}`}>{tk.assigneeStatus}</span>
                : <span className="rp-tk-none">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
  );
};

/* A counted table over one grouping. Receiving team, requester and ticket code are the same four
   questions asked of three different columns, so they are one component and one set of rules. */
/* The counted columns are the ticket STATUSES plus a total — Max, 2026-09-15: "chỉ cần để cột theo
   trạng thái và total thui". Overdue and Never emailed came out of these tables; both already have a
   headline card and a whole section of their own, and repeating them here answered a question the
   table was not being read for.

   THREE, and all three are always drawn — Max, 2026-09-15: "có 3 trạng thái lận nha thiếu mất cái
   Cancelled". An earlier version printed only the statuses that had a count, which quietly dropped
   Cancelled the moment nothing was cancelled; a reader then has no way to tell "none" from "not
   counted". These are exactly the three the ticket form offers (TASK_STATUS_OPTIONS in
   15_SampleTracker.jsx), in the order 00_PORTAL_task_status_dim sorts them. The dim also holds
   Overdue, but it is is_active = false and nobody can pick it — overdue is worked out from the due
   date, and has its own card and section. */
/* `key` is the STATUS VALUE a ticket carries; `label` is what the report prints. They parted
   company on 2026-09-17 (Max: "PENDING 42 => open 42"), which is Jay's first point: "We already use
   Pending in Project Tracker for stages that haven't started yet, and here it means something wider.
   Same word, two meanings."

   `Done` parted company the same way on 2026-09-17, on Jay's second line about the same
   screenshot: "3 Dones are all same or different?" — the card and the column are the manager
   CLOSING the ticket, the legend's Done is the receiving team finishing the work. One label table
   in `ticketStatus.js` now serves this report AND Ticket Station, because the rename Jay asked for
   ("use term OPEN not Pending even from Ticket Station") has to reach both or the two screens print
   two names for one value.

   The key stays the stored value either way, or every count below would stop matching a row. */
const TK_STATUSES = [
  { key: 'Pending', label: 'Open', labelKey: 'st.tkPending', w: 88, fill: '#F59E0B' },
  /* BLUE, not green — Max, 2026-09-17: "done cho màu xanh dương nhé". #075985, chosen by measuring
     rather than by picking a blue: the first two candidates were ΔE 2.0 and 5.2 from the Cancelled
     purple beside it for a deutan reader. This one clears every colour the ticket charts paint
     (worst pair 11.4 deutan) and is changed everywhere in this report at once, so the card, the
     column and the segment cannot disagree.

     Green had already moved once that day — #15803D → #047857 — because it sat ΔE 5.9 from the
     Open ramp's middle step. That problem goes away with it. */
  { key: 'Done', label: 'Closed', labelKey: 'st.tkDone', w: 84, fill: '#075985' },
  { key: 'Cancelled', label: 'Cancelled', labelKey: 'st.tkCancelled', w: 96, fill: '#94A3B8' },
];
/* What a status column / segment / card PRINTS, in the reader's language — the same words Ticket
   Station's own cards use (`st.tk*`). `label` stays as the English it always was. */
const tkLabel = (st) => (st.labelKey ? i18n.t(st.labelKey) : (st.label || st.key));
const TK_TOTAL_W = 76;
/* The card that counts a status wears the same colour as the chart segment for it. */
const TK_CARD_CLASS = { Pending: 'kv-pend', Done: 'kv-tkdone', Cancelled: 'kv-drop' };
/* Each card says whose decision it is reporting. The receiving team's own four answers are
   further down, under a legend that names them as the team's — two facts about one ticket, and
   the screen has to say which is which where they sit side by side. */
const TK_CARD_SUB = {
  Pending: 'xReport.tk.subPending',
  Done: 'xReport.tk.subDone',
  Cancelled: 'xReport.tk.subCancelled',
};

const TicketBreakdown = ({ title, label, rows, empty, keyWidth = 200, statuses }) => {
  const { t } = useTranslation();
  return (
  <div className="rp-owner-sec">
    <div className="rp-sec">{title}</div>
    {!rows.length ? <p className="rp-empty">{empty}</p> : (
      <div className="rp-tk-split">
      {/* The table is given the EXACT sum of its columns. `table-layout: fixed` only honours a
          declared width when the table's own width is settled: stretched to 100% it hands the slack
          back out and the key column asked for 330 and took 425; left to size itself it went to 492
          instead, because an auto-width table measures content again. */}
      <table className="rp-table rp-tk-table"
        style={{ width: keyWidth + statuses.reduce((n, c) => n + c.w, 0) + TK_TOTAL_W }}>
        <thead>
          <tr>
            {/* The name column was taking half the table and pushing the numbers off to the right
                (Max, 2026-09-15). It is a team name or a code, not a sentence. */}
            <th style={{ width: keyWidth }}>{label}</th>
            {statuses.map((st) => (
              <th key={st.key} className="num" style={{ width: st.w }}>{tkLabel(st)}</th>
            ))}
            <th className="num" style={{ width: TK_TOTAL_W }}>{t('xReport.tk.total')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>
                {unassignedText(r.key)}
                {/* A code on its own says nothing to anyone who does not have the 41 memorised —
                    Max, 2026-09-15: "cột ticket code thì thêm diễn giải và team nhận vào". */}
                {!!r.sub && <span className="rp-tk-sub">{r.sub}</span>}
              </td>
              {statuses.map((st) => (
                <td key={st.key} className="num">{r.byStatus[st.key] || '—'}</td>
              ))}
              <td className="num rp-tk-total">{r.raised}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* The bar beside the numbers — Max, 2026-09-15. The table answers "how many"; the bar answers
          "compared with whom", which a column of figures makes you work out in your head. */}
      <TicketStatusChart rows={rows} statuses={statuses} label={t('xReport.tk.chartAria', { title: title.toLocaleLowerCase() })} />
      </div>
    )}
  </div>
  );
};

/* One COLUMN per group, stacked by manager status — Max, 2026-09-15: "cột chứ ko phải bar nha".
   The column is as tall as the row's Total, so the table and the chart cannot disagree, and the
   segments are the table's own columns read bottom to top.

   The manager's status, not the assignee's: the manager's is the one that closes a ticket and the
   one the OPEN card reads. The two disagree on 42 of 45 tickets, so which is drawn is not a detail.

   Plain SVG like every other chart here — no library, same box. Labels are rotated rather than
   truncated: "Engineering Team" and "Production Team" differ only at the start, so a cut label
   would leave two columns reading the same. */
const TicketStatusChart = ({ rows, statuses, label, limit = 10 }) => {
  useTranslation();   // redraw the segment titles and the legend when the language changes
  if (!rows.length || !statuses.length) return null;
  const top = rows.slice(0, limit);
  const max = Math.max(...top.map((r) => r.raised), 1);
  const padL = 34;
  const plotH = 150;
  const padT = 18;          // headroom for the total printed above the tallest column
  const colW = top.length > 8 ? 34 : 44;
  const gap = top.length > 8 ? 12 : 16;
  const labelH = 68;        // the rotated names live here
  const w = padL + top.length * (colW + gap) + 18;
  const h = padT + plotH + labelH;
  // Three gridlines, so a column can be read against a number instead of against the others.
  const ticks = [0, 0.5, 1].map((f) => Math.round(max * f));
  return (
    <div className="rp-chartbox rp-tk-chart">
      <svg width={w} height={h} role="img" aria-label={label}>
        {ticks.map((v, i) => {
          const y = padT + plotH - (v / max) * plotH;
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={w - 10} y2={y} stroke={i ? '#E2E8F0' : '#9CA3AF'} />
              <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="9.5" fill="#94A3B8">{v}</text>
            </g>
          );
        })}
        <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#9CA3AF" />
        {top.map((r, i) => {
          const x = padL + 10 + i * (colW + gap);
          // Stacked from the baseline UP, in the same order the table reads left to right.
          let yBottom = padT + plotH;
          /* ONE SEGMENT PER MANAGER STATUS — Max, 2026-09-17: "các chart sẽ bỏ qua trạng thái của
             receiving team nhé chỉ lấy trạng thái của manager thui". The Open column used to be
             painted in the receiving team's four answers, which is what put a second "Done" on a
             page that already had the manager's one and started Jay's question. The ANSWERS are not
             gone — the tables in Sections 1-4 still carry an Assignee status column per ticket, which
             is where a reader goes to look one up. What left is the chart trying to say two things
             about one column at once. */
          const segs = statuses.flatMap((st) => {
            const n = r.byStatus[st.key] || 0;
            if (!n) return null;
            const sh = (n / max) * plotH;
            yBottom -= sh;
            return (
              <rect key={st.key} x={x} y={yBottom} width={colW} height={sh} fill={st.fill} rx="2">
                <title>{`${unassignedText(r.key)} — ${tkLabel(st)}: ${n}`}</title>
              </rect>
            );
          });
          return (
            <g key={r.key}>
              {segs}
              <text x={x + colW / 2} y={yBottom - 5} textAnchor="middle" fontSize="10.5"
                fill="#1f2937" fontWeight="700">{r.raised}</text>
              <text x={x + colW / 2} y={padT + plotH + 12} fontSize="10" fill="#334155"
                textAnchor="end" transform={`rotate(-38 ${x + colW / 2} ${padT + plotH + 12})`}>{unassignedText(r.key)}</text>
            </g>
          );
        })}
      </svg>
      {/* TWO ROWS — Max, 2026-09-17: "dòng đầu là Open, Done, Cancelled. Dòng 2 thì làm giống chart
          trên cùng á". One row of five items was a long line that said nothing about which three of
          them were parts of the first. Both rows went on 2026-09-17 with the sub-states themselves:
          three plain swatches need no second row, and the banded Open swatch existed only to explain
          a stack that is no longer painted. */}
      <div className="rp-tk-legend">
        {statuses.map((st) => (
          <span key={st.key}><i style={{ background: st.fill }} />{tkLabel(st)}</span>
        ))}
      </div>
    </div>
  );
};

/* Tickets on the Project Owner tab — Max, 2026-09-14: "thêm phần này trong Project owner với bạn sao
   cho nó gọn nhất".

   NOT a second copy of the Ticket Station tab. That one is cut by receiving team, because it asks
   who is holding the work. This tab is cut by OWNER all the way down, so the ticket block is too:
   the question here is "what is outstanding on MY projects", and an owner reading their own row is
   the compact version of everything on the other tab.

   One table, three numbers, and only the projects already on screen — the tab's filters have
   narrowed them, and a ticket block that ignored that would contradict the tables above it. */
const OwnerTicketSection = ({ rows }) => {
  const { t } = useTranslation();
  const totals = rows.reduce((a, r) => ({
    open: a.open + r.open, overdue: a.overdue + r.overdue, neverSent: a.neverSent + r.neverSent,
  }), { open: 0, overdue: 0, neverSent: 0 });
  return (
    <div className="rp-owner-sec">
      <div className="rp-sec">
        {t('xReport.owner.sec3')}
        <span style={{ fontWeight: 400, fontSize: 12 }}>
          {' '}{t('xReport.owner.sec3Counts', { open: totals.open, overdue: totals.overdue, never: totals.neverSent })}
        </span>
      </div>
      {!rows.length ? <p className="rp-empty">{t('xReport.owner.noTickets')}</p> : (
        <table className="rp-table rp-tk-table" style={{ maxWidth: 760 }}>
          <thead>
            <tr>
              <th>{t('xReport.col.prjOwner')}</th>
              <th className="num" style={{ width: 96 }}>{t('xReport.owner.raised')}</th>
              <th className="num" style={{ width: 88 }}>{t('xReport.owner.open')}</th>
              <th className="num" style={{ width: 96 }}>{t('xReport.owner.overdue')}</th>
              <th className="num" style={{ width: 128 }}>{t('xReport.owner.neverEmailed')}</th>
              <th className="num" style={{ width: 108 }}>{t('xReport.owner.oldestOpen')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{unassignedText(r.key)}</td>
                <td className="num">{r.raised}</td>
                <td className="num">{r.open}</td>
                <td className={`num${r.overdue ? ' rp-tk-late' : ''}`}>{r.overdue || '—'}</td>
                <td className={`num${r.neverSent ? ' rp-tk-late' : ''}`}>{r.neverSent || '—'}</td>
                <td className="num">{r.oldest === null ? '—' : t('xReport.daysShort', { n: r.oldest })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ONE COLUMN PER CARD — Max, 2026-09-16, "số lượng ticket theo card không lấy card raised".
   RAISED is left out because it is the total every other card is a part of: drawn beside them it is
   always the tallest column and says nothing the header line does not.

   **These OVERLAP.** An overdue ticket is also open, so the columns do NOT add up to the number of
   tickets and a reader who assumes a breakdown would conclude the report had lost some.

   NEVER EMAILED came off the chart AND off the cards on 2026-09-17 (Max: "bỏ card với cột trong
   chart never emailed nhé"). It keeps its own section — Section 3 still lists every one of them —
   which is where somebody can act on the list rather than read a number.

   Colours are the CARDS' colours, which is a rule this file already keeps so a reader connects the
   two without a legend. That has a measured cost: run through the palette validator, #B91C1C
   (OVERDUE) against #15803D (the DONE of the day) was ΔE 4.2 for a deutan reader — those columns
   are the same colour to them. Changing the red would desync chart and cards, so identity is
   carried by things that are not colour: every column is named on the axis and shows its own
   number, and the red one is HATCHED. The hatch outlived the pair it used to group — OVERDUE is the
   only red column now — and it stays, because amber (OPEN) beside red is the pair a deutan reader
   has to separate today. */
/* The axis names are the Ticket Station cards' own words (`st.tk*`), drawn in capitals. */
const TK_CARD_BARS = [
  { key: 'Pending', labelKey: 'st.tkPending', fill: '#F59E0B' },
  { key: 'Done', labelKey: 'st.tkDone', fill: '#075985' },
  { key: 'Cancelled', labelKey: 'st.tkCancelled', fill: '#94A3B8' },
  { key: 'Overdue', labelKey: 'st.tkOverdue', fill: '#B91C1C', hatch: true },
];

/* WHAT IS INSIDE THE PENDING COLUMN — Max, 2026-09-17: "cột pending này đang lấy theo manager status
   và tôi muốn chia cột theo trạng thái của assignee status nhỏ ở trong nhé".

   The column counts the MANAGER's status, which only says the ticket is not closed. Jay's whole
   point is that the 42 inside it need opposite responses — one lot needs chasing and the other just
   needs waiting — and now that the receiving team has its own four states, the column can say which
   is which.

   SEQUENTIAL, NOT CATEGORICAL. These are stages of one process, so they are one hue getting darker
   as the work progresses; a rainbow here would imply five unrelated things. Measured with the
   palette validator rather than chosen by eye: as an ordinal ramp #F59E0B → #D97706 → #B45309 →
   #78350F passes all four checks (monotone lightness, adjacent ΔL ≥ 0.06, the light end clearing the
   surface at 2.09:1, hue spread 24°), and its LIGHTEST STEP IS THE CARD'S OWN AMBER, so the column
   still reads as the PENDING card it sits under.

   Cancelled is off that progression and keeps the CANCELLED card's grey — measured ΔE 17.8–30.8
   against every amber step under deutan, protan and tritan, so it is never mistaken for one.

   Identity is never colour alone: every segment is named with its figure in the legend below, and
   each carries a <title> so hovering says what it is. */
/* The receiving team's four answers used to be painted INSIDE the Open column of all four charts,
   each with its own measured colour (amber → dark amber for the two "still working" states, emerald
   for Done, purple for Cancelled). Removed on 2026-09-17 at Max's call: "các chart sẽ bỏ qua trạng
   thái của receiving team nhé chỉ lấy trạng thái của manager thui". It answered Jay's "3 Dones are
   all same or different?" at the root — one chart cannot report two people's decisions about one
   ticket without teaching the reader which is which first.

   The answers themselves are untouched: every Section 1-4 table still carries an Assignee status
   column per ticket, and Section 4 is still the list of tickets the team has finished and nobody has
   closed. What went is the chart, not the fact. */

const TicketCardChart = ({ counts }) => {
  const { t } = useTranslation();
  const bars = TK_CARD_BARS.map((b) => ({ ...b, label: t(b.labelKey).toUpperCase(), n: counts[b.key] || 0 }));
  const max = Math.max(...bars.map((b) => b.n), 1);
  const padL = 34;
  const padT = 20;          // headroom for the figure printed above the tallest column
  const plotH = 140;
  const colW = 64;
  const gap = 26;
  const labelH = 24;       // one line: the card's name. The second line came off 2026-09-16.
  const w = padL + bars.length * (colW + gap) + 18;
  const h = padT + plotH + labelH;
  const ticks = [0, 0.5, 1].map((f) => Math.round(max * f));
  return (
    <div className="rp-chartbox rp-tk-cardchart">
      <svg width={w} height={h} role="img"
        aria-label={t('xReport.tk.cardsAria', { list: bars.map((b) => `${b.label} ${b.n}`).join(', ') })}>
        <defs>
          {/* The 45° hatch that separates the two red columns from the green one for a reader who
              cannot tell those hues apart. */}
          <pattern id="tkHatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="#B91C1C" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#FEE2E2" strokeWidth="2" />
          </pattern>
        </defs>
        {ticks.map((v, i) => {
          const y = padT + plotH - (v / max) * plotH;
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={w - 12} y2={y} stroke="#E8EDF4" strokeWidth="1" />
              <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="#94A3B8">{v}</text>
            </g>
          );
        })}
        {bars.map((b, i) => {
          const x = padL + i * (colW + gap) + gap / 2;
          const barH = (b.n / max) * plotH;
          const y = padT + plotH - barH;
          return (
            <g key={b.key}>
              {/* ONE SOLID COLUMN PER CARD — Max, 2026-09-17: the charts carry the MANAGER's status
                  only. The Open column was painted in the receiving team's four answers, which is
                  what put a second "Done" beside the manager's on one screen. The answers still
                  exist per ticket in the Section 1-4 tables. */}
              {b.n > 0 && (
                <rect x={x} y={y} width={colW} height={barH} rx="4"
                  fill={b.hatch ? 'url(#tkHatch)' : b.fill} />
              )}
              {/* The figure above its own column: the contrast check requires visible labels when a
                  fill sits under 3:1 against the surface, and amber and grey both do. */}
              <text x={x + colW / 2} y={y - 6} textAnchor="middle" fontSize="13" fontWeight="700"
                fill="#1F3864">{b.n}</text>
              <text x={x + colW / 2} y={padT + plotH + 15} textAnchor="middle" fontSize="10"
                fontWeight="700" fill="#4B5563">{b.label}</text>
            </g>
          );
        })}
      </svg>
      {/* No legend: every column is named on the axis and prints its own figure, and with the
          sub-states gone there is nothing left that colour alone has to carry. */}
    </div>
  );
};

/* The whole tab. Filters at the top apply to everything below them, the same contract the other
   three tabs have. */
const TicketReportView = ({ tickets, filters, values, openFilter, onToggleOpen, onFilter, onClear, generated, range, onRange }) => {
  /* `t` is the translator here; the ticket lambdas below still name their argument `t` too, and
     none of them translates anything — the shadowing is harmless and kept to leave them untouched. */
  const { t } = useTranslation();
  const rows = tickets;
  const open = rows.filter(TICKET_OPEN);
  const overdue = rows.filter(ticketOverdue);
  /* Never emailed AND still open. A ticket somebody closed without ever sending it is finished
     business, and listing it as something to do would send people chasing nothing. */
  const unsent = open.filter((t) => !t.sentAt);
  /* Section 1 is every ticket the MANAGER still has open — Max, 2026-09-16. Same default the tables
     and the PENDING card use: a ticket with no status set is Pending, not blank. */
  const pending = rows.filter((t) => (t.status || 'Pending') === 'Pending');
  /* Section 4: the receiving team has answered Done and the ticket is still open. Not a
     contradiction and not an error — only the requester, project owner or team manager closes a
     ticket — but it is the pile where the work is finished and nobody has said so, and there is 
     nowhere else in the portal that shows it. */
  const answeredNotClosed = pending.filter((t) => t.assigneeStatus === 'Done');

  /* `subOf` is the second line under the key — what a ticket code means, and who receives it.
     Read off the tickets themselves rather than fetched: every ticket already carries its name and
     its receiving team, so there is nothing here to keep in step with the code master. */
  const group = (keyOf, subOf) => {
    const map = new Map();
    rows.forEach((t) => {
      const key = keyOf(t) || 'unassigned';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    return [...map.entries()].map(([key, list]) => {
      /* Counted by the MANAGER's status — the one that closes a ticket and the one the OPEN card
         reads. A ticket with no status set is Pending, the same default the tables use. */
      const byStatus = {};
      list.forEach((t) => {
        const st = t.status || 'Pending';
        byStatus[st] = (byStatus[st] || 0) + 1;
      });
      return {
        key,
        sub: subOf ? subOf(list) : '',
        raised: list.length,
        open: list.filter(TICKET_OPEN).length,
        byStatus,
      };
    }).sort((a, b) => b.raised - a.raised || b.open - a.open);
  };

  /* Still the sort for the attention lists, though the column itself came off on 2026-09-15:
     oldest first is the order somebody works down the list in, whether or not it is printed. */
  const byAge = (a, b) => (ticketAgeDays(b) ?? 0) - (ticketAgeDays(a) ?? 0);
  // Counted the same way the breakdown tables count, so a card and a Total column cannot disagree.
  const statusCount = rows.reduce((acc, t) => {
    const st = t.status || 'Pending';
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});


  return (
    <>
      <h1 className="rp-title">{t('xReport.team.tickets')}</h1>
      <div className="rp-sub">
        {t('xReport.subTickets', {
          date: generated, n: rows.length, open: open.length, overdue: overdue.length, unsent: unsent.length,
        })}
      </div>
      <div className="rp-filters">
        {/* The period and the two buttons take a ROW OF THEIR OWN, above the menus, and that is not
            decoration. With them below, a menu opened from the row above covered the Clear Filters
            button: the click that was meant to clear everything landed on a value in the open menu
            and silently toggled it instead. Menus now only ever open over page content. */}
        <div className="rp-filters-row rp-filters-top">
          <label className="rp-appr-l">{t('xReport.filter.raisedFrom')}
            <input type="date" className="rp-appr-d" value={range.from} max={range.to || undefined}
              onChange={(e) => onRange({ ...range, from: e.target.value })} />
          </label>
          <label className="rp-appr-l">{t('xReport.filter.to')}
            <input type="date" className="rp-appr-d" value={range.to} min={range.from || undefined}
              onChange={(e) => onRange({ ...range, to: e.target.value })} />
          </label>
          {/* Empty by default, unlike the same control on the Project Owner tab: that one answers
              "what came in this week", while this page exists to show a backlog, and opening it on
              the last seven days would hide most of the never-emailed pile that section 1 is for. */}
          <button type="button" className="rp-filter-btn" onClick={() => onRange(defaultApprovedRange())}>{t('xReport.filter.last7')}</button>
          <button type="button" className="rp-filter-btn" onClick={() => onFilter('__all__', [])}>{t('xReport.filter.clear')}</button>
        </div>
        <div className="rp-filters-row">
          <MultiFilter id="team" label={t('xReport.filter.allSopTeams')} values={values.team} selected={filters.team} open={openFilter === 'team'} onToggleOpen={onToggleOpen} onChange={onFilter} onClear={onClear} labelOf={unassignedText} />
          <MultiFilter id="owner" label={t('xReport.filter.allOwners')} values={values.owner} selected={filters.owner} open={openFilter === 'owner'} onToggleOpen={onToggleOpen} onChange={onFilter} onClear={onClear} labelOf={unassignedText} />
          <MultiFilter id="dept" label={t('xReport.filter.allReceiving')} values={values.dept} selected={filters.dept} open={openFilter === 'dept'} onToggleOpen={onToggleOpen} onChange={onFilter} onClear={onClear} labelOf={unassignedText} />
          <MultiFilter id="requester" label={t('xReport.filter.allRequesters')} values={values.requester} selected={filters.requester} open={openFilter === 'requester'} onToggleOpen={onToggleOpen} onChange={onFilter} onClear={onClear} labelOf={unassignedText} />
          <MultiFilter id="code" label={t('xReport.filter.allCodes')} values={values.code} selected={filters.code} open={openFilter === 'code'} onToggleOpen={onToggleOpen} onChange={onFilter} onClear={onClear} labelOf={unassignedText} />
          <MultiFilter id="status" label={t('xReport.filter.allStatuses')} values={values.status} selected={filters.status} open={openFilter === 'status'} onToggleOpen={onToggleOpen} onChange={onFilter} onClear={onClear} labelOf={tkStatusText} />
        </div>
      </div>

      {/* Raised and the three statuses first, then the three things that are wrong — Max,
          2026-09-15. The first four say what the queue IS; the last three say what to do about it.
          Never emailed sits among them rather than in a footnote: it is the only one of the seven
          that nobody can see anywhere else in the portal.

          The status cards carry the same colours as the chart segments below (amber, green, grey),
          so a reader connects the two without a legend telling them to.

          There is no OPEN card — Max, 2026-09-15. Open means "not Done and not Cancelled", and those
          are two of the only three statuses, so OPEN and PENDING were the same number by definition,
          for ever. Two cards side by side showing 44 send the reader looking for a difference that
          does not exist. PENDING stays because it names what it counts; `open` is still computed,
          because "never emailed" means never emailed AND still open. */}
      <div className="rp-owner-kpi-row">
        <div className="rp-owner-kpi">
          <div className="rp-owner-kpi-l">{t('xReport.owner.raised').toUpperCase()}</div>
          <div className="rp-owner-kpi-v">{rows.length}</div>
          <div className="rp-owner-kpi-s">{t('xReport.tk.raisedSub')}</div>
        </div>
        {TK_STATUSES.map((st) => (
          <div key={st.key} className={`rp-owner-kpi ${TK_CARD_CLASS[st.key]}`}>
            <div className="rp-owner-kpi-l">{tkLabel(st).toUpperCase()}</div>
            <div className="rp-owner-kpi-v">{statusCount[st.key] || 0}</div>
            {/* "manager status" was the sub-line on all three, which named the COLUMN and not the
                fact. Each one now says what its own word means — and all three say "the manager",
                which is the half Jay could not see: the chart's other Done belongs to the receiving
                team. */}
            <div className="rp-owner-kpi-s">{TK_CARD_SUB[st.key] ? t(TK_CARD_SUB[st.key]) : ''}</div>
          </div>
        ))}
        <div className="rp-owner-kpi kv-late">
          <div className="rp-owner-kpi-l">{t('st.tkOverdue').toUpperCase()}</div>
          <div className="rp-owner-kpi-v">{overdue.length}</div>
          <div className="rp-owner-kpi-s">{t('xReport.tk.overdueSub')}</div>
        </div>
      </div>

      {/* Above section 1, so the shape of the queue is read before the first list of it. */}
      <div className="rp-owner-sec rp-tk-cardsec">
        <div className="rp-sec">{t('xReport.tk.byCard')}</div>
        {/* The note that used to sit here came off — Max, 2026-09-16. It said what the comment on
            TK_CARD_BARS still says: the five overlap and do not add up to the ticket count. */}
        <TicketCardChart
          counts={{
            Pending: pending.length,
            Done: statusCount.Done || 0,
            Cancelled: statusCount.Cancelled || 0,
            Overdue: overdue.length,
          }}
        />
      </div>

      <TicketFlagTable
        title={t('xReport.tk.s1')}
        tone="tk-unsent"
        rows={[...pending].sort(byAge)}
        note={t('xReport.tk.s1note')}
        empty={t('xReport.tk.s1empty')}
      />
      <TicketFlagTable
        title={t('xReport.tk.s2')}
        tone="tk-late"
        rows={[...overdue].sort((a, b) => (b.dDay || 0) - (a.dDay || 0))}
        empty={t('xReport.tk.s2empty')}
      />
      <TicketFlagTable
        title={t('xReport.tk.s3')}
        tone="tk-unsent"
        rows={[...unsent].sort(byAge)}
        note={t('xReport.tk.s3note')}
        empty={t('xReport.tk.s3empty')}
      />
      <TicketFlagTable
        title={t('xReport.tk.s4')}
        tone="tk-unsent"
        rows={[...answeredNotClosed].sort(byAge)}
        note={t('xReport.tk.s4note')}
        empty={t('xReport.tk.s4empty')}
      />
      <TicketBreakdown
        title={t('xReport.tk.s5')}
        label={t('xReport.tk.keyReceiving')}
        keyWidth={180}
        rows={group((tk) => tk.department)}
        statuses={TK_STATUSES}
        empty={t('xReport.tk.noMatch')}
      />
      <TicketBreakdown
        title={t('xReport.tk.s6')}
        label={t('xReport.tk.keyOwner')}
        keyWidth={160}
        /* The OWNER of the project the ticket hangs on, not whoever typed the ticket — Max,
           2026-09-15. Tina raised 33 of the 45, so grouping by requester answered "who fills in the
           form" and drew one long column; the owner is the person the work belongs to. Same field
           the attention lists now show and the All Project Owners filter reads. */
        rows={group((tk) => tk.ownerName)}
        statuses={TK_STATUSES}
        empty={t('xReport.tk.noMatch')}
      />
      <TicketBreakdown
        title={t('xReport.tk.s7')}
        label={t('xReport.tk.keyCode')}
        keyWidth={300}
        rows={group(
          (tk) => tk.taskType,
          /* The code's own name and the team it goes to. Both come off the tickets in the group;
             where a group mixes them — the same code pointed at two teams over time — the teams
             are listed rather than one of them picked. */
          (list) => {
            const name = list.find((tk) => tk.name)?.name || '';
            const teams = [...new Set(list.map((tk) => tk.department).filter(Boolean))];
            return [name, teams.join(' / ')].filter(Boolean).join(' · ');
          },
        )}
        statuses={TK_STATUSES}
        empty={t('xReport.tk.noMatch')}
      />

    </>
  );
};

/* ---------- R&D Team view ---------- */

const RD_COLS = [
  { t: 'Category', w: '10%' }, { t: 'Special Project', w: '14%' }, { t: 'Assigned to', w: '7%' },
  { t: 'Item', w: '24%' }, { t: 'Sample QTY', w: '7%' }, { t: 'Project Status', w: '9%' }, { t: 'R&D Round', w: '9%' },
  { t: 'Remark', w: '20%' },
];

// The live table carries one extra column: a tick per row deciding whether the row goes into the next pinned
// snapshot. Pinned snapshots are frozen, so they render RD_COLS without it and stay identical to before.
const RD_COLS_LIVE = [
  { t: 'Category', w: '10%' }, { t: 'Special Project', w: '13%' }, { t: 'Assigned to', w: '7%' },
  { t: 'Item', w: '21%' }, { t: 'Sample QTY', w: '7%' }, { t: 'Project Status', w: '9%' }, { t: 'R&D Round', w: '9%' },
  { t: 'Remark', w: '19%' }, { t: 'Pin', w: '5%' },
];

// Stable identity for a row across re-renders and re-filters. `p.id` alone is not enough: a sample with no
// linked project has no project id.
// The option variant is part of the key: without it two rows of one SKU share a key, and the pin
// checkbox on one would tick the other.
const rdRowKey = ({ p, it }) => [p.id || '', p.sampleBase || '', p.variantOption || '',
  (it && it.item) || p.item || ''].join('|');

/*
 * Must return exactly what the R&D Round column renders, otherwise the filter list and the table disagree.
 *
 * It used to return the project's overall Proto stage, which is computed from the FURTHEST round wherever it
 * was made. On this report that was wrong in a way that read as plausible: SMP-0061's P5 is being sewn at MS,
 * so the column said "RM Waiting P5" while the round R&D actually has on the bench is a Trial / Mock-up. The
 * Remark column was already taking its text from the R&D round, so the two cells on one line described two
 * different rounds (Max, 2026-08-27).
 *
 * The R&D round wins when there is one. A project allocated to R&D with no sample yet still falls back to the
 * project stage, because there is no round to report.
 */
const rdProtoValue = (p) => {
  if (p && p.orphanSample) return String(p.roundStatus || '').trim() || 'Pending';
  if (p && p.rdRoundStatus) return p.rdRoundStatus;
  return String((p && p.stages && p.stages.proto) || '').trim() || 'Pending';
};
/* WHICH PROJECT STATUSES THIS REPORT CARRIES.

   'Delayed' JOINED ON 2026-10-08 — Max: "thêm trạng thái delayed của project nhé hiện chỉ lấy mỗi
   on track". It is a reversal of his own 2026-08-06 call, taken knowingly and recorded rather than
   quietly overwritten: the rule then was "only live projects", and Delayed was filed with On Hold
   and Dropped as not-R&D's-move. It is not the same kind of thing as those two. A dropped SKU is
   finished and a parked one is waiting on somebody else, but a DELAYED SKU is late and still being
   built — which is the one a report about what R&D is holding should be loudest about.

   'Concern' JOINED ON 2026-10-29 — Max, when Habitability Cot (SKU-00488, set to Concern by Amy on 15/09) was missing
   while its three sister SKUs showed: "concern vậy tính nhé". Until then it had been left out on purpose (he had named
   Delayed only); a Concern SKU is, like a Delayed one, still being built.

   On Hold, Dropped, Complete and Ship Finished stay out, each for the reason it always had.

   A NAMED LIST rather than a chain of `||`, because this is the third place in the file that picks
   a set of statuses and the other two already read as a sentence somebody has to parse. */
const RD_LIVE_STATUSES = ['On Track', 'Delayed', 'Concern'];

// Proto stages the R&D report hides: 'Complete' is done, 'Waiting Feedback' is on someone else.
const RD_HIDDEN_PROTO = ['Complete', 'Waiting Feedback'];
const rdItemValue = (p) => String((p && p.item) || '').trim();
// Location on this report comes from the SAMPLE, not the project's allocation: a sample can be made
// in the R&D room while the project sits under TS/MS. Falls back to the project when there is no
// linked sample.
const rdLocationValue = (p) => String((p && (p.sampleLoc || p.location || p.allocation)) || '').trim();

// Same derivation the Sample Tracker uses, so "RM Waiting" / "In Production" mean the same thing in
// both screens. The report's own roundStatus() only looked at `outcome` and could never yield these.
const reportRoundStatus = (r) => {
  if (r.trial) return 'Internal Trial';
  if (r.outcome) return r.outcome;
  if (r.sent) return 'Pending Outcome';
  if (r.matReady) return 'In Production';
  return 'RM Waiting';
};
/*
 * A sample only pulls its SKU into the R&D report when a round is in the R&D room AND still needs R&D's
 * attention. Pending Outcome / Advance / Confirmed are someone else's move or already done.
 *
 * 'Internal Trial' was missing from this list and it was an omission, not a decision — every other status
 * that is excluded has a reason written next to it, that one had none. An internal trial is a sample the
 * team built themselves rather than ordering from a vendor, so one made in the R&D room is the single most
 * unambiguously R&D-owned round there is.
 *
 * It kept real work off the report entirely: SMP-0061 and SMP-0062 have a P5 in the right STATUS but the
 * wrong room (MS), and a Trial / Mock-up in the right ROOM but the wrong status. Neither round satisfied
 * both halves at once, so the report showed nothing at all while R&D had two live builds. Adding it brings
 * in exactly SKU-242 and SKU-243 — measured, not assumed (Max, 2026-08-27).
 */
/* 'Dropped' came out on Max's call, 2026-09-09 ("không nhé"). A dropped round is finished — it is
   not R&D's move any more, which is the one question this list answers. It had been here since the
   list was written, without a reason beside it, unlike every status that is excluded. */
const RD_ROUND_STATUSES = ['RM Waiting', 'In Production', 'Rework', 'On Hold', 'Internal Trial'];

/* An orphan sample has no project behind it, so `status === 'On Track'` — the guard that keeps a
   parked project off this report — has nothing to test. The round's own status is the equivalent:
   a parked round is not R&D's move either. Without this the report carried nine On Hold rows,
   eight of them Helinox Lah samples detached by the PRJ-098 delete (Max, 2026-09-09). */
const RD_ORPHAN_STATUSES = RD_ROUND_STATUSES.filter((v) => v !== 'On Hold');

/* Is this round still R&D's move?

   The `sent` half is the fix for "sample đã xong mà vẫn hiện" (Max, 2026-09-09). Every other round
   leaves this report the moment it is submitted, because reportRoundStatus turns it into 'Pending
   Outcome' and that is not on the list. An internal trial never could: reportRoundStatus answers
   'Internal Trial' on the first line, before it ever looks at the finish date. SMP-0061 and SMP-0062
   were both submitted on 2026-09-04 and both were still being counted.

   Checked HERE rather than by reordering reportRoundStatus, because that function is deliberately the
   same derivation the Sample Tracker uses — reordering it would change what the Internal Trial badge
   means on another screen to fix a question that only this report asks. */
const rdRoundLive = (r, statuses) => statuses.indexOf(reportRoundStatus(r)) !== -1
  && !(r.trial && String(r.sent || '').trim());

const RdTeamSection = ({ list, totalCount, excluded, onToggleRow, onToggleAll }) => {
  const { t } = useTranslation();
  const sorted = useMemo(() => list.slice().sort((a, b) => (
    String(a.name || '').localeCompare(String(b.name || '')) || String(a.item || '').localeCompare(String(b.item || ''))
  )), [list]);
  const skuRows = projectSkuRows(sorted);
  const shown = projectCount(sorted);
  /* Rows and SKUs stopped being the same number when a round's option variants were split onto their
     own lines: two rows of "Light Shell Backpack" are still ONE SKU. Counting rows and calling them
     SKUs would have quietly reported 11 where there are 10. */
  const skuCount = new Set(skuRows.map(({ p, it }) => `${p.id || p.sampleBase || ''}|${(it && it.item) || p.item || ''}`)).size;
  return (
    <div>
      <div className="rp-sec">
        HEVN R&D <span style={{ fontWeight: 400, fontSize: 12 }}>
          {/* Three counts, each its own phrase in the reader's language, joined the way the line
              always joined them. */}
          ({[
            typeof totalCount === 'number' && totalCount !== shown
              ? t('xReport.projectsOf', { shown, total: totalCount })
              : t('xReport.projectsN', { n: shown }),
            t('xReport.skuN', { n: skuCount }),
            skuRows.length !== skuCount ? t('xReport.rowsN', { n: skuRows.length }) : '',
          ].filter(Boolean).join(' · ')})
        </span>
      </div>
      {sorted.length
        ? <RdRowsTable skuRows={skuRows} excluded={excluded} onToggleRow={onToggleRow} onToggleAll={onToggleAll} />
        : <p className="rp-empty">{t('xReport.rd.noRows')}</p>}
    </div>
  );
};

/** The R&D table body, shared by the live view and by pinned snapshots so both look identical. */
const RdRowsTable = ({ skuRows, excluded, onToggleRow, onToggleAll }) => {
  const { t } = useTranslation();
  return (
  <ReportTable cols={onToggleRow ? RD_COLS_LIVE : RD_COLS} onToggleAll={onToggleAll}>
    {skuRows.map(({ p, it, rowKey }) => (
      <tr key={rowKey || rdRowKey({ p, it })} className={excluded && excluded[rdRowKey({ p, it })] ? 'rp-row-off' : ''}>
        <td>{reportProjectCategory(p) || '—'}</td>
        {/* A sample with no linked project simply has no project name, category or status —
            the blanks say it, so no warning text or highlight. */}
        <td style={{ fontWeight: 600 }}>{p.name}</td>
        <td style={{ textAlign: 'center' }}>{p.assigned || '—'}</td>
        <td>
          {it.item || p.item}
          {p.variantOption && <span className="rp-variant">_Option: {p.variantOption}</span>}
          {p.orphanSample && <span className="rp-orphan-base"> ({p.sampleBase})</span>}
        </td>
        <td style={{ textAlign: 'center' }}>
          {p.sampleQtyOverride !== undefined ? p.sampleQtyOverride : sampleQtyForProjectItem(p, it)}
        </td>
        <td>{p.orphanSample ? '—' : <Pill status={p.status} />}</td>
        {/* Names the round as well as its state: "Internal Trial" alone does not say which of the sample's
            rounds R&D is holding, and this table has one line per SKU, not per round. */}
        <StageCell value={rdProtoValue(p)} note={p.orphanSample ? '' : p.rdRoundLabel} />
        <td className="rp-remark">{p.sampleRemark || '—'}</td>
        {onToggleRow && (
          <td style={{ textAlign: 'center' }}>
            <input
              type="checkbox"
              title={t('xReport.rd.untick')}
              checked={!(excluded && excluded[rdRowKey({ p, it })])}
              onChange={() => onToggleRow(rdRowKey({ p, it }))}
            />
          </td>
        )}
      </tr>
    ))}
  </ReportTable>
  );
};

/** Human-readable description of the filters that produced a pinned snapshot. It is written into
    the pin when it is made, so it reads in the language of that moment. */
const RD_FILTER_LABELS = {
  status: 'xReport.rd.fStatus', proto: 'xReport.rd.fProto', category: 'xReport.rd.fCategory',
  assigned: 'xReport.rd.fAssigned', item: 'xReport.rd.fItem', location: 'xReport.rd.fLocation',
};
const describeRdFilters = (f) => {
  const parts = [];
  Object.keys(RD_FILTER_LABELS).forEach((key) => {
    const picked = (f[key] || []).filter((v) => v !== FILTER_NONE);
    if (!picked.length) return;
    parts.push(i18n.t('xReport.rd.fPart', {
      label: i18n.t(RD_FILTER_LABELS[key]),
      values: picked.length > 3 ? i18n.t('xReport.filter.selected', { n: picked.length }) : picked.join(', '),
    }));
  });
  return parts.length ? parts.join(' · ') : i18n.t('xReport.rd.noFilter');
};

/* ---------- Section 4: Overview + charts + workload ---------- */

const CHART_COLORS = ['#2E75B6', '#70AD47', '#ED7D31', '#7030A0', '#00A6A6', '#C00000'];

const TypologyChart = ({ raw, sel }) => {
  const typPref = ['Type 1', 'Type 1-1', 'Type 2', 'Type 2-1', 'Type 3', 'Type 3-1', 'Type 4', 'Type 4-1', 'Type 5', 'Type 5-1', 'Type 6', 'Type 6-1', 'Type 7', 'Type 7-1'];
  const seen = {}; const counts = {};
  projectSkuRows(raw).forEach(({ p, it }) => {
    if (sel.indexOf(p.assigned) === -1) return;
    const t = String(it.typology || p.typology || 'Unspecified').trim() || 'Unspecified';
    seen[t] = true;
    counts[t] = counts[t] || {};
    counts[t][p.assigned] = (counts[t][p.assigned] || 0) + 1;
  });
  const types = typPref.filter((t) => seen[t]);
  Object.keys(seen).sort().forEach((t) => { if (types.indexOf(t) === -1) types.push(t); });
  if (!types.length) return <div className="rp-chartbox" style={{ marginTop: 12 }}>No SKU in current filter</div>;

  let max = 1;
  types.forEach((t) => sel.forEach((d) => { max = Math.max(max, counts[t][d] || 0); }));
  const left = 52; const top = 62; const chartH = 230; const bottom = 76;
  const barW = sel.length > 4 ? 13 : 20; const barGap = 4; const groupGap = 12;
  const groupW = Math.max(46, sel.length * barW + Math.max(0, sel.length - 1) * barGap);
  const w = Math.max(620, left + types.length * (groupW + groupGap) + 28);
  const h = top + chartH + bottom;
  const gridLines = [];
  for (let s = 0; s <= 5; s += 1) {
    const val = Math.round((max * s) / 5);
    const y = top + chartH - (val / max) * chartH;
    gridLines.push(
      <React.Fragment key={s}>
        <line x1={left} y1={y} x2={w - 28} y2={y} stroke="#D1D5DB" />
        <text x={left - 9} y={y + 4} textAnchor="end" fontSize="11">{val}</text>
      </React.Fragment>,
    );
  }
  const legendStart = Math.max(left, (w - sel.length * 74) / 2);
  return (
    <div className="rp-sec4-chart-section">
      <div className="rp-sec" style={{ marginBottom: 6 }}>SKU by Typology - Developer Chart</div>
      <div className="rp-chartbox">
        <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} style={{ display: 'block', margin: 0 }}>
          <text x={w / 2} y={20} textAnchor="middle" fontSize="15" fontWeight="700" fill="#6B7280">Developments Status by Typology</text>
          {sel.map((d, i) => (
            <React.Fragment key={d}>
              <rect x={legendStart + i * 74} y={28} width={10} height={10} rx={2} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              <text x={legendStart + i * 74 + 14} y={37} fontSize="11">{d}</text>
            </React.Fragment>
          ))}
          <line x1={left} y1={top} x2={left} y2={top + chartH} stroke="#9CA3AF" />
          <line x1={left} y1={top + chartH} x2={w - 28} y2={top + chartH} stroke="#9CA3AF" />
          {gridLines}
          {types.map((t, ti) => {
            const groupX = left + ti * (groupW + groupGap) + 6;
            const innerW = sel.length * barW + Math.max(0, sel.length - 1) * barGap;
            const startX = groupX + (groupW - innerW) / 2;
            return (
              <React.Fragment key={t}>
                {sel.map((d, di) => {
                  const val = counts[t][d] || 0;
                  if (!val) return null;
                  const bh = Math.max(2, (val / max) * chartH);
                  const x = startX + di * (barW + barGap); const y = top + chartH - bh;
                  return (
                    <React.Fragment key={d}>
                      <rect x={x} y={y} width={barW} height={bh} fill={CHART_COLORS[di % CHART_COLORS.length]} />
                      <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize="10">{val}</text>
                    </React.Fragment>
                  );
                })}
                <text transform={`translate(${groupX + groupW / 2},${top + chartH + 42}) rotate(-45)`} textAnchor="end" fontSize="11">{t}</text>
              </React.Fragment>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const STACK_CATS = [
  { key: 'inDev', labelKey: 'xReport.ov.inDev', color: '#0070C0' },
  { key: 'postHandoff', labelKey: 'xReport.ov.postHandoff', color: '#00A6D6' },
  { key: 'postShipment', labelKey: 'xReport.ov.postShipment', color: '#9DC3E6' },
  { key: 'onHold', labelKey: 'xReport.ov.onHold', color: '#BFBFBF' },
];
/* Roughly how wide a legend label draws at 10px: a Hangul / Han character takes about twice a
   Latin one, so the legend's spacing follows the language instead of overlapping in Korean or
   Chinese. */
const legendTextW = (text) => [...String(text)].reduce(
  (w, ch) => w + (/[\u1100-\u11ff\u2e80-\u9fff\uac00-\ud7af\uff00-\uffef]/.test(ch) ? 10 : 6), 0,
);

const StackChart = ({ title, mode, sel, raw, all }) => {
  const { t } = useTranslation();
  const inDevIds = {};
  raw.forEach((p) => { inDevIds[p.id] = true; });
  const postHandoffIds = {};
  specialPostHandoffProjects(all).forEach((p) => { postHandoffIds[p.id] = true; });
  const bucket = (p) => {
    const status = normReportText(p.status);
    if (status === 'complete' || status === 'dropped') return '';
    if (p.status === 'On Hold') return 'onHold';
    if (isPostShipmentProject(p)) return 'postShipment';
    if (postHandoffIds[p.id]) return 'postHandoff';
    if (inDevIds[p.id]) return 'inDev';
    return '';
  };
  const data = {}; let max = 1;
  sel.forEach((d) => { data[d] = {}; STACK_CATS.forEach((c) => { data[d][c.key] = mode === 'project' ? {} : 0; }); });
  all.forEach((p) => {
    const b = bucket(p);
    if (!b) return;
    const owner = b === 'postHandoff' ? stackChartOwner(p) : p.assigned;
    if (sel.indexOf(owner) === -1) return;
    if (mode === 'project') data[owner][b][p.name || p.id || ''] = true;
    else data[owner][b] += projectItems(p).length;
  });
  sel.forEach((d) => {
    let total = 0;
    STACK_CATS.forEach((c) => {
      const val = mode === 'project' ? Object.keys(data[d][c.key]).length : data[d][c.key];
      data[d][c.key] = val;
      total += val;
    });
    max = Math.max(max, total);
  });
  const left = 38; const top = 40; const chartH = 230; const bottom = 76; const barW = 50; const gap = 44;
  const w = Math.max(470, left + sel.length * (barW + gap) + 70);
  const h = top + chartH + bottom;
  const gridLines = [];
  for (let s = 0; s <= 5; s += 1) {
    const val = Math.round((max * s) / 5);
    const y = top + chartH - (val / max) * chartH;
    gridLines.push(
      <React.Fragment key={s}>
        <line x1={left} y1={y} x2={w - 12} y2={y} stroke="#D9E0EA" />
        <text x={left - 7} y={y + 4} textAnchor="end" fontSize="10">{val}</text>
      </React.Fragment>,
    );
  }
  const groupW = sel.length * barW + Math.max(0, sel.length - 1) * gap;
  const start = left + Math.max(0, (w - 12 - left - groupW) / 2);
  let legendW = 0;
  const legendPieces = STACK_CATS.map((c) => {
    const x = legendW;
    const label = t(c.labelKey);
    legendW += legendTextW(label) + 34;
    return (
      <React.Fragment key={c.key}>
        <rect x={x} y={0} width={9} height={9} fill={c.color} />
        <text x={x + 13} y={9} fontSize="10">{label}</text>
      </React.Fragment>
    );
  });
  return (
    <div className="rp-chartbox" style={{ padding: 10, minHeight: 370 }}>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }}>
        <text x={w / 2} y={18} textAnchor="middle" fontSize="15" fontWeight="800" fill="#595959">{title}</text>
        <g transform={`translate(${Math.max(left, (w - legendW) / 2)},${h - 22})`}>{legendPieces}</g>
        <line x1={left} y1={top} x2={left} y2={top + chartH} stroke="#9CA3AF" />
        <line x1={left} y1={top + chartH} x2={w - 12} y2={top + chartH} stroke="#9CA3AF" />
        {gridLines}
        {sel.map((d, i) => {
          const x = start + i * (barW + gap);
          let yCursor = top + chartH;
          const pieces = STACK_CATS.map((c) => {
            const val = data[d][c.key] || 0;
            if (!val) return null;
            const bh = Math.max(2, (val / max) * chartH);
            yCursor -= bh;
            const y = yCursor;
            return (
              <React.Fragment key={c.key}>
                <rect x={x} y={y} width={barW} height={bh} fill={c.color} />
                <text x={x + barW / 2} y={y + bh / 2 + 4} textAnchor="middle" fontSize="10" fontWeight="700">{val}</text>
              </React.Fragment>
            );
          });
          return (
            <React.Fragment key={d}>
              {pieces}
              <text x={x + barW / 2} y={top + chartH + 24} textAnchor="middle" fontSize="12" fontWeight="700">{d}</text>
            </React.Fragment>
          );
        })}
      </svg>
    </div>
  );
};

// Current Workload Snapshot: per developer, how many base IDs they still own (latest round not
// Confirmed/Dropped) and how many rounds are in the making (no outcome, not sent yet).
const currentWorkloadRows = (rounds, devs, sel) => {
  const selected = sel && sel.length ? sel : (devs && devs.length ? devs : uniq((rounds || []).map(roundDeveloper)));
  const byBase = latestRoundByBase(rounds);
  const inChargeByDev = {};
  Object.keys(byBase).forEach((base) => {
    const r = byBase[base];
    if (isClosedRoundOutcome(r.outcome)) return;
    const dev = roundDeveloper(r);
    if (selected.indexOf(dev) === -1) return;
    if (!inChargeByDev[dev]) inChargeByDev[dev] = {};
    inChargeByDev[dev][base] = true;
  });
  const inProgressByDev = {};
  (rounds || []).forEach((r) => {
    if (String((r && r.outcome) || '').trim()) return;
    if (isActualDateText(roundSampleFinishDate(r))) return;
    const sid = roundSampleId(r);
    if (!sid) return;
    const dev = roundDeveloper(r);
    if (selected.indexOf(dev) === -1) return;
    if (!inProgressByDev[dev]) inProgressByDev[dev] = {};
    inProgressByDev[dev][sid] = true;
  });
  return selected.map((dev) => ({
    dev,
    inCharge: Object.keys(inChargeByDev[dev] || {}).length,
    inProgress: Object.keys(inProgressByDev[dev] || {}).length,
  })).filter((row) => row.inCharge || row.inProgress)
    .sort((a, b) => (b.inCharge - a.inCharge) || (b.inProgress - a.inProgress) || a.dev.localeCompare(b.dev));
};

const WorkloadChart = ({ rows }) => {
  const { t } = useTranslation();
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => Math.max(r.inCharge, r.inProgress)), 1);
  const left = 94; const top = 42; const rowH = 34; const barH = 10; const gap = 5; const chartW = 420;
  const w = left + chartW + 70;
  const h = top + rows.length * rowH + 42;
  const grid = [];
  for (let s = 0; s <= 4; s += 1) {
    const val = Math.round((max * s) / 4);
    const x = left + (val / max) * chartW;
    grid.push(
      <React.Fragment key={s}>
        <line x1={x} y1={top - 8} x2={x} y2={h - 35} stroke="#E5EAF2" />
        <text x={x} y={h - 18} textAnchor="middle" fontSize="10" fill="#64748B">{val}</text>
      </React.Fragment>,
    );
  }
  return (
    <div className="rp-chartbox">
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }}>
        <text x={w / 2} y={14} textAnchor="middle" fontSize="14" fontWeight="800" fill="#595959">{t('xReport.ov.wlChart')}</text>
        <rect x={left} y={18} width={10} height={10} rx={2} fill="#276B49" />
        <text x={left + 14} y={27} fontSize="11">{t('xReport.ov.inChargeBase')}</text>
        <rect x={left + 160} y={18} width={10} height={10} rx={2} fill="#6AA7D8" />
        <text x={left + 174} y={27} fontSize="11">{t('xReport.ov.inProgressSample')}</text>
        {grid}
        {rows.map((r, i) => {
          const y = top + i * rowH;
          const inW = Math.max(0, (r.inCharge / max) * chartW);
          const mkW = Math.max(0, (r.inProgress / max) * chartW);
          return (
            <React.Fragment key={r.dev}>
              <text x={left - 12} y={y + 12} textAnchor="end" fontSize="12" fontWeight="700" fill="#1F2937">{devText(r.dev)}</text>
              <rect x={left} y={y} width={inW} height={barH} rx={2} fill="#276B49" />
              <text x={left + inW + 6} y={y + 9} fontSize="10" fill="#1F2937">{r.inCharge || ''}</text>
              <rect x={left} y={y + barH + gap} width={mkW} height={barH} rx={2} fill="#6AA7D8" />
              <text x={left + mkW + 6} y={y + barH + gap + 9} fontSize="10" fill="#1F2937">{r.inProgress || ''}</text>
            </React.Fragment>
          );
        })}
      </svg>
    </div>
  );
};

const CurrentWorkloadSection = ({ rounds, devs, sel }) => {
  const { t } = useTranslation();
  const rows = useMemo(() => currentWorkloadRows(rounds, devs, sel), [rounds, devs, sel]);
  return (
    <div style={{ marginTop: 12 }}>
      <div className="rp-sec" style={{ marginBottom: 6 }}>{t('xReport.ov.wlTitle')}</div>
      <div className="rp-note">
        {t('xReport.ov.wlIntro')} <b>{t('xReport.ov.wlInCharge')}</b>{t('xReport.ov.wlInChargeText')}
        {' '}<b>{t('xReport.ov.wlInProgress')}</b>{t('xReport.ov.wlInProgressText')}
      </div>
      <div className="rp-chartgrid">
        <div>
          {rows.length ? (
            <table className="rp-table" style={{ fontSize: 11.5, maxWidth: 520, tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: '34%', padding: '6px 7px' }}>{t('xReport.col.developer')}</th>
                  <th style={{ width: '33%', padding: '6px 7px' }}>{t('xReport.ov.inChargeBase')}</th>
                  <th style={{ width: '33%', padding: '6px 7px' }}>{t('xReport.ov.inProgressSample')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.dev}>
                    <td style={{ fontWeight: 700, padding: '5px 7px' }}>{devText(r.dev)}</td>
                    <td style={{ textAlign: 'center', fontWeight: 800, padding: '5px 7px' }}>{r.inCharge}</td>
                    <td style={{ textAlign: 'center', fontWeight: 800, padding: '5px 7px' }}>{r.inProgress}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="rp-empty">{t('xReport.ov.noWorkload')}</p>}
        </div>
        <WorkloadChart rows={rows} />
      </div>
    </div>
  );
};

const OverviewSection = React.memo(({ active, devs, selectedDevs, onToggleDev, rounds }) => {
  const { t } = useTranslation();
  const raw = useMemo(() => active.filter((p) => {
    const statusOk = p.status === 'On Track' || p.status === 'Concern' || p.status === 'Delayed';
    return statusOk && !hasStageValue(p.stages && p.stages.handoff);
  }), [active]);

  const allocs = useMemo(() => {
    const allocPref = ['MS', 'TS', 'R&D', 'Others'];
    const allocMap = {};
    raw.forEach((p) => { if (p.allocation) allocMap[p.allocation] = true; });
    const list = allocPref.filter((a) => allocMap[a]);
    Object.keys(allocMap).sort().forEach((a) => { if (list.indexOf(a) === -1) list.push(a); });
    return list;
  }, [raw]);

  const sel = selectedDevs.length ? selectedDevs : devs;

  // Totals are SUMS of the per-developer cells (a project shared by 2 developers counts in both).
  const cell = (al, dev) => {
    const ps = raw.filter((p) => p.allocation === al && p.assigned === dev);
    return { p: legacyProjectNameCount(ps), q: skuCount(ps) };
  };
  const rowTotal = (al) => sel.reduce((total, dev) => {
    const c = cell(al, dev);
    return { p: total.p + (Number(c.p) || 0), q: total.q + (Number(c.q) || 0) };
  }, { p: 0, q: 0 });
  const colTotal = (dev) => allocs.reduce((total, al) => {
    const c = cell(al, dev);
    return { p: total.p + (Number(c.p) || 0), q: total.q + (Number(c.q) || 0) };
  }, { p: 0, q: 0 });
  const grand = allocs.reduce((total, al) => {
    const c = rowTotal(al);
    return { p: total.p + (Number(c.p) || 0), q: total.q + (Number(c.q) || 0) };
  }, { p: 0, q: 0 });
  const tableW = Math.max(560, 120 + (sel.length + 1) * 78 * 2);

  return (
    <div>
      <div className="rp-sec">{t('xReport.ov.title')}</div>
      <p className="rp-note">{t('xReport.ov.note')}</p>
      <div className="rp-chipbar">
        <span style={{ fontSize: 11, color: '#374151', fontWeight: 600, marginRight: 4 }}>{t('xReport.filter.developer')}</span>
        {devs.map((d) => (
          <button
            key={d}
            type="button"
            className={`rp-devchip${sel.indexOf(d) === -1 ? ' off' : ''}`}
            onClick={() => onToggleDev(d, devs)}
          >
            {d}
          </button>
        ))}
      </div>
      <div className="rp-sec4-table-wrap">
        <table className="rp-table rp-sec4-table" style={{ fontSize: 11, width: tableW, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th rowSpan={2}>{t('xReport.col.location')}</th>
              <th colSpan={sel.length + 1}>{t('xReport.ov.numProject')}<br /><span style={{ fontWeight: 400, fontSize: 10 }}>{t('xReport.ov.numProjectNote')}</span></th>
              <th colSpan={sel.length + 1}>{t('xReport.ov.numSku')}<br /><span style={{ fontWeight: 400, fontSize: 10 }}>{t('xReport.ov.numSkuNote')}</span></th>
            </tr>
            <tr>
              {sel.map((d) => <th key={`p-${d}`}>{d}</th>)}
              <th>{t('xReport.col.total')}</th>
              {sel.map((d) => <th key={`q-${d}`}>{d}</th>)}
              <th>{t('xReport.col.total')}</th>
            </tr>
          </thead>
          <tbody>
            {allocs.map((al) => {
              const rt = rowTotal(al);
              return (
                <tr key={al}>
                  <td style={{ fontWeight: 700 }}>{al}</td>
                  {sel.map((d) => <td key={`p-${d}`} style={{ textAlign: 'center' }}>{cell(al, d).p || 0}</td>)}
                  <td style={{ textAlign: 'center', fontWeight: 800, background: '#C5DCF2', color: '#1F3864' }}>{rt.p || '—'}</td>
                  {sel.map((d) => <td key={`q-${d}`} style={{ textAlign: 'center' }}>{cell(al, d).q || '—'}</td>)}
                  <td style={{ textAlign: 'center', fontWeight: 800, background: '#C5DCF2', color: '#1F3864' }}>{rt.q || '—'}</td>
                </tr>
              );
            })}
            <tr style={{ background: '#EEF4FD' }}>
              <td style={{ fontWeight: 900 }}>{t('xReport.col.total')}</td>
              {sel.map((d) => <td key={`p-${d}`} style={{ textAlign: 'center', fontWeight: 800, color: '#C00000' }}>{colTotal(d).p || '—'}</td>)}
              <td style={{ textAlign: 'center', fontWeight: 900, background: '#C5DCF2', color: '#C00000' }}>{grand.p}</td>
              {sel.map((d) => <td key={`q-${d}`} style={{ textAlign: 'center', fontWeight: 800, color: '#C00000' }}>{colTotal(d).q || '—'}</td>)}
              <td style={{ textAlign: 'center', fontWeight: 900, background: '#C5DCF2', color: '#C00000' }}>{grand.q}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Typology was retired on 2026-08-14 (Max) — the chart is hidden rather than deleted so it can
          come back if the field is ever revived. Frame Color is a colour name, not a grouping, so it
          does not replace this chart. */}
      {false && <TypologyChart raw={raw} sel={sel} />}
      <div style={{ marginTop: 12 }}>
        <div className="rp-sec" style={{ marginBottom: 6 }}>{t('xReport.ov.stageSummary')}</div>
        <div className="rp-chartgrid">
          <StackChart title={t('xReport.ov.chartProject')} mode="project" sel={sel} raw={raw} all={active} />
          <StackChart title={t('xReport.ov.chartSku')} mode="sku" sel={sel} raw={raw} all={active} />
        </div>
      </div>
      <CurrentWorkloadSection rounds={rounds} devs={devs} sel={sel} />
    </div>
  );
});

/* ---------- Excel export: walk the rendered report, let the backend build a real .xlsx ---------- */

const cellText = (cell) => (cell ? (cell.innerText || cell.textContent || '') : '').replace(/\s+/g, ' ').trim();

const collectReportBlocks = (contentEl) => {
  const blocks = [];
  contentEl.querySelectorAll('.rp-sec,.rp-subsec,.rp-note,table.rp-table').forEach((node) => {
    if (node.matches('table.rp-table')) {
      const rows = Array.prototype.slice.call(node.rows || []).map((row) => (
        // {t: text, s: colSpan} — the backend merges spans so wide headers don't stretch one column
        Array.prototype.slice.call(row.cells || []).map((c) => ({
          t: cellText(c) || '-',
          s: Math.max(1, Number(c.colSpan) || 1),
        }))
      ));
      if (rows.length) blocks.push({ type: 'table', rows });
    } else if (node.classList.contains('rp-sec')) {
      blocks.push({ type: 'sec', text: cellText(node) });
    } else if (node.classList.contains('rp-subsec')) {
      blocks.push({ type: 'subsec', text: cellText(node) });
    } else if (node.classList.contains('rp-note')) {
      blocks.push({ type: 'note', text: cellText(node) });
    }
  });
  return blocks;
};

/* `title` is the heading inside the workbook (the reader's language); `fileTitle` names the file and
   stays English, because the file name keeps only ASCII letters and digits. */
const exportVisibleReportExcel = async (contentEl, title, fileTitle = title) => {
  if (!contentEl) return;
  const blocks = collectReportBlocks(contentEl);
  if (!blocks.some((b) => b.type === 'table')) {
    window.alert(i18n.t('xReport.exportNone'));
    return;
  }
  const stamp = fmtDateTimeVN(new Date(), '');
  const fileStamp = stamp.replace(/[: ]/g, '-');
  const filename = `HEVN_${fileTitle.replace(/[^a-zA-Z0-9]+/g, '_')}_${fileStamp}.xlsx`;
  try {
    const res = await fetch(`${PROJECT_API_BASE}/report-export-xlsx`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ title, generated: stamp, filename, blocks }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || i18n.t('xReport.exportFailedStatus', { status: res.status }));
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = match ? match[1] : filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  } catch (err) {
    window.alert(i18n.t('xReport.exportFailed', { error: err.message }));
  }
};


/* ================= Report for Project Owner (Max, 2026-08-22, from his notebook draft) ============
   Two sections, two filters (Sales OP team, Project Owner).

   Section 1 — "Project portfolio by owner & category": one row per owner, one column per category,
   counting PROJECTS, with a second table counting SKUs, and the two charts from the draft.

   The draft's columns read "Sp. In | Inline | NCV | others". The columns here are built from the
   categories actually present instead, because "others" would swallow Collaboration — 178 of 435 SKUs,
   the single biggest category. A hidden majority in a portfolio report is worse than a wide table.

   Section 2 — "Project status by owner": the two rows of Project Tracker cards, then one row per
   project. A project has many SKUs and the draft's columns are per project, so the aggregation is
   stated in the header: the EXF date (of the unfinished SKUs — TBA if any has none, else the nearest),
   the average progress
   the tracker's own group row shows, and the status of its most active SKU. PIC is Developer + Designer, which is
   why the draft's example reads "Brian, Minsu".
   ================================================================================================ */

/* THE ORDER MAX GAVE, 2026-09-18 — every SKU of the group is read, and the FIRST status in this
   list that any of them carries is the one the project is reported at:

     On Track · Concern · Delayed · On Hold · Ship Finished · Complete · Dropped

   It was worst-first until today, on the reasoning that the SKU needing attention should speak for
   the project; he has ruled otherwise, and the report already carries the attention elsewhere — the
   SKU DELAYED and SKU CONCERN cards count the SKUs themselves, so a project's own row no longer has
   to. Read down the list it is the LIVEST SKU that wins: work in progress before work in trouble,
   both before work that is finished, and Dropped only when there is nothing else at all.

   This is NOT the old list reversed, and the two ends are why. On Track leads, where reversing
   would have put Complete first and reported a project as finished on the strength of one finished
   SKU; and Dropped stays last, where reversing would have made it first and reported a live project
   as abandoned because one of its SKUs was. */
/* Priority (Max added it on 2026-10-15) leads: a SKU marked Priority is the most active there is. */
const OWNER_STATUS_RANK = ['Priority', 'On Track', 'Concern', 'Delayed', 'On Hold', 'Ship Finished', 'Complete', 'Dropped'];
const groupStatus = (statuses) => {
  for (const want of OWNER_STATUS_RANK) if (statuses.includes(want)) return want;
  return statuses[0] || '';
};
const ownerStatusClass = (status) => ({
  Priority: 'rp-pill-priority', 'On Track': 'rp-pill-track', Concern: 'rp-pill-concern', Delayed: 'rp-pill-delayed',
  'On Hold': 'rp-pill-hold', 'Ship Finished': 'rp-pill-ship', Complete: 'rp-pill-done',
  Dropped: 'rp-pill-drop',
}[status] || '');

// One project = one group row, folded from its SKUs.
const ownerProjectRows = (skus) => {
  const byGroup = new Map();
  (skus || []).forEach((p) => {
    const key = p.groupId || p.id || p.name;
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key).push(p);
  });
  return [...byGroup.entries()].map(([key, items]) => {
    const first = items[0];
    /* TBA FIRST, THEN THE NEAREST DATE — Max, 2026-09-18: "cột EXF thì ưu tiên project SKU có TBA
       rồi đến project SKU có ngày gần nhất nhé".

       It was the LAST date, on the reasoning that a project is done when its final SKU ships. The
       new rule reads the other way round and answers a different question — when does this project
       next need attention — so a SKU with no date at all outranks every date there is: nothing is
       more urgent than a ship date nobody has set. Only once every SKU has one does the soonest
       of them speak for the project.

       A SKU whose date is the string 'TBA' counts as having none: parseReportDate reads it as
       nothing, and filtering on Boolean alone would have let the word through as a date.

       AND A FINISHED SKU NO LONGER SPEAKS — Max, 2026-09-24, on PRJ-00044: the row read 2026-07-27
       and "đúng phải là 2026-11-16". Two of its three SKUs are Complete at 2026-07-27 and the live
       one ships 2026-11-16, so the nearest date was the soonest of the work ALREADY DONE and the
       project read eight weeks overdue while its only open SKU is ten weeks out. A date that
       answers "when does this need attention next" cannot be taken from work that needs none.
       The Status beside it has reported the most ACTIVE SKU since the same day; the two columns
       now describe the same SKUs.

       The fold drops Complete, Ship Finished and Dropped — and keeps EVERY other SKU rather than
       only the one the status came from. Measured, that is the whole difference on PRJ-00170:
       picking the status's own SKUs would report 2026-12-28 from an On Track SKU and throw away
       the CONCERN SKU's 2026-11-11, which is both the nearer date and the one in trouble. On
       PRJ-00057 it would drop an On Hold SKU that has no date at all, turning a TBA into a date.
       A project whose SKUs are ALL finished falls back to all of them: it still has a date to
       report, and blanking it would say something untrue about a project that simply ended. */
    const FINISHED_SKU = ['Complete', 'Ship Finished', 'Dropped'];
    const live = items.filter((x) => FINISHED_SKU.indexOf(String(x.status || '').trim()) === -1);
    const forExf = live.length ? live : items;
    const dated = forExf.map((x) => x.exfDate).filter((d) => parseReportDate(d));
    const anyUnset = dated.length < forExf.length;
    const lastExf = anyUnset ? ''
      : dated.sort((a, b) => (parseReportDate(a) || 0) - (parseReportDate(b) || 0))[0] || '';
    const pctValues = items.map((x) => Number(x.pct)).filter((n) => Number.isFinite(n));
    return {
      key,
      groupId: first.groupId || key,
      name: first.groupName || first.name || '',
      category: first.groupCategory || first.category || '',
      owner: first.owner || '',
      team: first.team || '',
      approvedAt: first.approvedAt || null,
      approvedBy: first.approvedBy || '',
      skuCount: items.length,
      exf: lastExf,
      // Same plain average the Project Tracker shows on a collapsed group row.
      pct: pctValues.length ? Math.round(pctValues.reduce((a, b) => a + b, 0) / pctValues.length) : 0,
      status: groupStatus(items.map((x) => x.status || '')),
      pic: uniq(items.flatMap((x) => [x.assigned, x.designer])).join(', '),
    };
  }).sort((a, b) => String(a.groupId).localeCompare(String(b.groupId), undefined, { numeric: true }));
};

/* Stacked columns, one column per owner (Max, 2026-08-23). The split is by CATEGORY (Max, 2026-08-24)
   — the same categories as the table's columns, so a column can be read against the row above it.
   The old split by approval state went with the Pending toggle: this report does not count Pending at
   all any more, so Active / Complete / Pending / Rejected had nothing left to say. */
/* HAS THIS SKU GOT A SAMPLE YET? — Max, 2026-09-29, wants the answer on the Section 1 chart, on a
   card in Section 2 and as a list in Section 4. One predicate feeds all three, so the chart segment,
   the card and the row count cannot say three different numbers.

   THE ROUND IS THE FACT, the SKU's `linked_samples` is only a pointer — and four of them point at
   somebody else's sample. Measured: SKU-00286 names SMP-0118, whose rounds belong to SKU-00287;
   SKU-00217 names SMP-0140 (SKU-00228/229); SKU-00630 names SMP-0239/0240 (SKU-00496/497); and
   SKU-00165 names SMP-0171, which has no round at all. Reading the link would report a sample for
   four SKUs that have none, so a SKU is sampled when a round really points at IT.

   The Home dashboard's card asks for both (`linked_samples = '' AND NOT EXISTS round`), which is why
   it counts four fewer: a wrong link hides a SKU that still needs one.

   Finished SKUs are NOT excluded here, unlike the Home card, because this list has a different job:
   the Home card invites somebody to go and raise a sample, while Section 1's chart must add up to the
   TTL SKU column standing directly above it. Seven SKUs are Complete or Dropped with no sample ever
   raised; they carry their status in Section 4 so they read as history rather than as work. */
const roundedSkuIds = (rounds) => new Set(
  (rounds || []).map((r) => String(r.project || '').trim()).filter(Boolean),
);
const skuHasSample = (p, rounded) => rounded.has(String(p.id || '').trim());
/* A SKU that is Complete, Dropped or Ship Finished and never had a sample is HISTORY, not work, and
   it is not a footnote: of the 265 SKUs with no round today, 105 are Complete, 20 Dropped and 8 Ship
   Finished — 133 of them, more than half. A card reading 265 beside ACTIVE SKU overstates what is
   left to do by double, and Section 4 would open on last year's finished collaborations.

   So the CARD and the LIST count the live ones, the same rule the Home dashboard's "no sample yet"
   card applies, and the card's sub-line says so.

   SECTION 1'S COLUMN AND CHART NOW USE THIS TOO — Max, 2026-10-08, reversing the paragraph that
   stood here and said "do not fix one into the other". That paragraph defended the chart counting
   EVERY SKU so its two segments added up to the TTL SKU column above it, and the price of the
   reversal is exactly that: they no longer add up, unfiltered 265 + 84 against a TTL of 490. He
   was shown the 141 before choosing, and chose it over scoping `Sample created` the same way.
   What is left is one rule for the words "no sample yet" everywhere on this report, which is what
   he actually asked for — the old arrangement had the same three words meaning two different
   things a hand's width apart on one screen. The finished-but-never-sampled SKUs are not lost:
   Section 4 lists them with their status, as history. */
const SKU_FINISHED = ['Complete', 'Dropped', 'Drop', 'Ship Finished'];
/* AND NOT A REPEAT ORDER — Max, 2026-10-09: "phần No sample cần loại trừ trường hợp là repeat
   order nhé". A repeat order is a style already developed, ordered again: it has no proto or counter
   to make, so it will never have a sample, and listing it here invites somebody to go and raise
   one. The SKU's own tick (is_repeat_order) says so. One predicate, so the Section 1 column, the
   chart's red segment, the NO SAMPLE YET card and Section 4 all lose it together. It is not counted
   under "Sample created" either — it was not sampled — so it is in neither column. */
const skuNeedsSample = (p, rounded) => !skuHasSample(p, rounded)
  && !SKU_FINISHED.includes(String(p.status || '').trim())
  && !p.repeatOrder;
const SAMPLE_YES = 'Sample created';
const SAMPLE_NO = 'No sample yet';
/* Both already in the palette below, so the report gains no new colour: #2E75B6 is the blue the SKU
   bars have always been and #C0504D is its index 6, which the category chart beside it never reaches
   at three categories. Measured with the validator rather than picked: every check passes, CVD
   separation ΔE 16.1 protan / 28.2 tritan, 24.9 in normal vision, both above 3:1 on the surface. */
const SAMPLE_SERIES = [
  { key: SAMPLE_YES, color: '#2E75B6' },
  { key: SAMPLE_NO, color: '#C0504D' },
];
/* The two sample keys and the chart's 'Other' are the report's own words, so they are drawn in the
   reader's language; a CATEGORY key is data and is drawn as it is. The key itself is what the chart
   matches rows on and is never changed. */
const SERIES_LABEL_KEYS = {
  [SAMPLE_YES]: 'xReport.owner.sampleCreated', [SAMPLE_NO]: 'xReport.owner.noSampleYet', Other: 'xReport.other',
};
const seriesText = (key) => (SERIES_LABEL_KEYS[key] ? i18n.t(SERIES_LABEL_KEYS[key]) : key);

const OWNER_CAT_COLORS = [
  '#2E75B6', '#70AD47', '#FFC000', '#7B61FF', '#FF8A3D', '#00A2A2', '#C0504D', '#8497B0', '#B07AA1', '#4E79A7',
];
const ownerCategorySeries = (categories) => (categories || []).map((c, i) => ({
  key: c, color: OWNER_CAT_COLORS[i % OWNER_CAT_COLORS.length],
}));
const OwnerStackChart = ({ title, note, owners, series, rowsFor }) => {
  const { t } = useTranslation();
  const cols = owners.map((owner) => {
    const keys = rowsFor(owner) || [];
    const parts = series
      .map((s) => ({ ...s, n: keys.filter((x) => (x || '') === s.key).length }))
      .filter((s) => s.n > 0);
    const other = keys.length - parts.reduce((a, b) => a + b.n, 0);
    // Anything with a key nobody listed still has to appear, or the column would understate the
    // owner's load and the chart would disagree with the table above it.
    if (other > 0) parts.push({ key: 'Other', color: '#C9D3E0', n: other });
    return { owner, parts, total: keys.length };
  });
  /* No sort. `owners` arrives in the table's own order and the charts sit directly under it, so
     reordering them by size means the reader has to find each name again to compare the two (Max,
     2026-09-10). The bars carry their numbers, which is what size was doing here. */
  const max = Math.max(...cols.map((c) => c.total), 1);
  const used = series.concat([{ key: 'Other', color: '#C9D3E0' }])
    .filter((s) => cols.some((c) => c.parts.some((p) => p.key === s.key)));
  return (
    <div className="rp-owner-chart">
      <div className="rp-owner-chart-t">{title}{!!note && <span className="rp-chart-note">{note}</span>}</div>
      {!cols.length ? <div className="rp-owner-empty">{t('xReport.owner.noData')}</div> : (
        <>
          <div className="rp-stack-plot">
            {cols.map((c) => (
              <div className="rp-stack-col" key={c.owner}>
                {/* No running total above the column (Max, 2026-08-23) — the table above already gives
                    it, and the per-segment numbers are what the chart is for.
                    Heights are a share of the tallest column, so the columns stay comparable. */}
                <div className="rp-stack-bar" style={{ height: `${Math.max(3, (c.total / max) * 100)}%` }}>
                  {c.parts.map((p) => (
                    <div
                      key={p.key}
                      className="rp-stack-seg"
                      style={{ height: `${(p.n / c.total) * 100}%`, background: p.color }}
                      title={`${unassignedText(c.owner)} · ${seriesText(p.key)}: ${p.n}`}
                    >
                      {(p.n / c.total) > 0.14 && <span className="rp-stack-seg-n">{p.n}</span>}
                    </div>
                  ))}
                </div>
                <div className="rp-stack-lbl" title={unassignedText(c.owner)}>{unassignedText(c.owner)}</div>
              </div>
            ))}
          </div>
          <div className="rp-stack-legend">
            {used.map((s) => (
              <span className="rp-stack-key" key={s.key}>
                <i style={{ background: s.color }} />{seriesText(s.key)}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* The SKU chart is a plain count per owner — "phần SKU thì chỉ cần hiện số lượng" (Max, 2026-08-24).
   No split, no legend: the number sits above its own column. */
const OwnerCountChart = ({ title, note, owners, countFor }) => {
  const { t } = useTranslation();
  // Same order as the table above and as the chart beside it — see the note in OwnerStackChart.
  const cols = owners.map((owner) => ({ owner, n: countFor(owner) })).filter((c) => c.n > 0);
  const max = Math.max(...cols.map((c) => c.n), 1);
  return (
    <div className="rp-owner-chart">
      <div className="rp-owner-chart-t">{title}{!!note && <span className="rp-chart-note">{note}</span>}</div>
      {!cols.length ? <div className="rp-owner-empty">{t('xReport.owner.noData')}</div> : (
        <div className="rp-stack-plot">
          {cols.map((c) => (
            <div className="rp-stack-col" key={c.owner}>
              <div className="rp-bar-n">{c.n}</div>
              <div
                className="rp-stack-bar rp-bar-solid"
                style={{ height: `${Math.max(3, (c.n / max) * 100)}%` }}
                title={`${unassignedText(c.owner)}: ${t('xReport.skuN', { n: c.n })}`}
              />
              <div className="rp-stack-lbl" title={unassignedText(c.owner)}>{unassignedText(c.owner)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OwnerPortfolioSection = ({ projects, skus, categories, rounded }) => {
  const { t } = useTranslation();
  const owners = uniq(projects.map((p) => p.owner || UNASSIGNED)).sort();
  const series = ownerCategorySeries(categories);
  const projectCell = (owner, category) => projects.filter(
    (p) => (p.owner || UNASSIGNED) === owner && (category === null || p.category === category),
  ).length;
  const skuCell = (owner) => skus.filter((p) => (p.owner || UNASSIGNED) === owner).length;
  /* NO SAMPLE NOW MEANS WHAT THE CARD MEANS — Max, 2026-10-08: "xét lại điều kiện tính No sample
     ở trên theo cái ở dưới nhé. Cái ở dưới thì sẽ đúng hơn." He is right about what the words say:
     a SKU dropped last year has no sample and never will, and calling that "no sample YET" invites
     somebody to go and raise one.

     THE COST, MEASURED AND PUT TO HIM BEFORE IT WAS DONE, because the view he was looking at was
     filtered to one owner where the gap is 3. Unfiltered it is 141: the two columns used to be the
     CHART's two segments counted the same way, so created + none = TTL SKU on every row. They no
     longer do — 265 + 84 = 349 against a TTL of 490 — and the 141 that fall through both are the
     Complete, Dropped and Ship Finished SKUs that never had a sample. He chose this over scoping
     both columns to live SKUs, which would have held the arithmetic and moved Sample created from
     265 to 182, a column he had not asked about.

     SO THE HEADER SAYS WHICH POPULATION IT IS, and Section 4 still lists the finished ones as
     history — the row no longer adds up, and a reader has to be able to see why without arithmetic.
     `Sample created` is untouched: a Complete SKU that was sampled WAS sampled. */
  /* `owner === null` IS EVERY OWNER, the same shape `projectCell` already uses for its category
     argument — and it is not a convenience. The TTL row had its OWN copy of this test written
     inline (`skus.filter((p) => !skuHasSample(p, rounded)).length`), so the moment the column rule
     changed the total went on answering the old one: every owner's cell read the new figure and
     the row under them still said 225. Found by reading the rendered table rather than the diff.
     One counter, called by both. */
  const sampleCell = (owner, want) => skus.filter((p) => {
    if (owner !== null && (p.owner || UNASSIGNED) !== owner) return false;
    return want ? skuHasSample(p, rounded) : skuNeedsSample(p, rounded);
  }).length;
  return (
    <div className="rp-owner-sec">
      <div className="rp-sec">{t('xReport.owner.sec1')}</div>
      {/* One table, not two side by side: TTL SKU belongs next to TTL project (Max, 2026-08-23), and
          reading a second table to answer "and how many SKUs is that?" was the reason to move it. */}
      <div className="rp-owner-tbl-wrap">
        <table className="rp-table rp-owner-tbl">
          <thead>
            <tr>
              <th className="rp-owner-th-name">{t('xReport.col.prjOwner')}</th>
              {categories.map((c) => <th key={c} className="num rp-owner-th-cat">{c}</th>)}
              <th className="num rp-owner-th-ttl">{t('xReport.col.ttl')}<span className="rp-th-note">{t('xReport.thNote.project')}</span></th>
              {/* BETWEEN the two totals — Max, 2026-09-29, after seeing them ahead of both. It puts
                  the two parts immediately before the total they add up to, so TTL SKU is read as
                  their sum. That does separate TTL SKU from TTL project, which was deliberate on
                  2026-08-23 ("and how many SKUs is that?" answered by reading the two side by side);
                  raised with him and he placed them here anyway, so this supersedes that.
                  Named exactly as the chart's legend names them, so the table and the bars under it
                  cannot teach two words for one thing. */}
              <th className="num rp-owner-th-smp">{t('xReport.col.sample')}<span className="rp-th-note">{t('xReport.thNote.created')}</span></th>
              {/* "still open" and not "yet": the note is the whole difference between this column
                  and the one beside it, and it is the same wording the card's own sub-line uses, so
                  the two places cannot read as two rules. */}
              <th className="num rp-owner-th-smp">{t('xReport.col.noSample')}<span className="rp-th-note">{t('xReport.thNote.stillOpen')}</span></th>
              <th className="num rp-owner-th-ttl">{t('xReport.col.ttl')}<span className="rp-th-note">{t('xReport.thNote.sku')}</span></th>
            </tr>
          </thead>
          <tbody>
            {owners.map((owner) => (
              <tr key={owner}>
                <td>{unassignedText(owner)}</td>
                {categories.map((c) => {
                  const n = projectCell(owner, c);
                  return <td key={c} className="num">{n || '—'}</td>;
                })}
                <td className="num strong">{projectCell(owner, null)}</td>
                <td className="num">{sampleCell(owner, true) || '—'}</td>
                <td className="num">{sampleCell(owner, false) || '—'}</td>
                <td className="num strong">{skuCell(owner)}</td>
              </tr>
            ))}
            <tr className="rp-owner-ttl">
              <td>{t('xReport.col.ttl')}</td>
              {categories.map((c) => (
                <td key={c} className="num">{projects.filter((p) => p.category === c).length || '—'}</td>
              ))}
              <td className="num strong">{projects.length}</td>
              <td className="num strong">{sampleCell(null, true)}</td>
              <td className="num strong">{sampleCell(null, false)}</td>
              <td className="num strong">{skus.length}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="rp-owner-charts">
        <OwnerStackChart
          title={t('xReport.owner.chartProjects')}
          note={t('xReport.owner.chartProjectsNote')}
          owners={owners}
          series={series}
          rowsFor={(owner) => projects.filter((p) => (p.owner || UNASSIGNED) === owner).map((p) => p.category)}
        />
        {/* SPLIT BY WHETHER A SAMPLE EXISTS — Max, 2026-09-29. It was a plain count, which the TTL
            SKU column beside it already gives; what it could not answer is how much of an owner's
            load has not been started.
            IT FOLLOWS THE TABLE ABOVE IT — 2026-10-08. It used to count every SKU so the two
            segments added up to the TTL SKU column; when the No sample column took the card's rule,
            leaving the chart alone would have drawn 225 in red two inches under a cell reading 84.
            One fact under two numbers on one screen is the fault this report keeps being rebuilt to
            stop making.
            So the bars no longer reach TTL SKU, and the note under the title says what they do
            reach: the finished SKUs that were never sampled are in neither segment. */}
        <OwnerStackChart
          title={t('xReport.owner.chartSkus')}
          note={t('xReport.owner.chartSkusNote')}
          owners={owners}
          series={SAMPLE_SERIES}
          rowsFor={(owner) => skus
            .filter((p) => (p.owner || UNASSIGNED) === owner)
            /* A finished SKU that was never sampled belongs to neither segment now, so it is
               dropped rather than painted into one of them. */
            .map((p) => (skuHasSample(p, rounded) ? SAMPLE_YES
              : (skuNeedsSample(p, rounded) ? SAMPLE_NO : null)))
            .filter(Boolean)}
        />
      </div>
    </div>
  );
};

/* The cards the Project Tracker also draws read the tracker's own words (`st.pt*`), so the two
   screens cannot name one count two ways; the report's own three SKU cards and NO SAMPLE YET are
   `xReport.owner.*`. Drawn in capitals, as they always were. `id` is the stable React key. */
const OwnerKpiRows = ({ counts }) => {
  const { t } = useTranslation();
  const rows = [
    [
      { id: 'groups', label: t('st.ptGroups'), value: counts.groups },
      { id: 'prjDone', label: t('st.ptProjectComplete'), value: counts.projectComplete, tone: 'kv-conf', sub: t('st.ptProjectCompleteSub') },
      { id: 'prjDrop', label: t('st.ptProjectDropped'), value: counts.projectDropped, tone: 'kv-drop', sub: t('st.ptProjectDroppedSub') },
      { id: 'prjActive', label: t('st.ptProjectActive'), value: counts.projectActive, tone: 'kv-rnd', sub: t('st.ptProjectActiveSub') },
      { id: 'skus', label: t('st.ptSkus'), value: counts.skus },
      { id: 'skuDone', label: t('xReport.owner.completedSku'), value: counts.complete, tone: 'kv-conf' },
      { id: 'skuDrop', label: t('xReport.owner.droppedSku'), value: counts.dropped, tone: 'kv-drop' },
      { id: 'skuActive', label: t('xReport.owner.activeSku'), value: counts.active, tone: 'kv-rnd' },
      /* Max, 2026-09-29, asked for it here, after ACTIVE SKU. It counts EVERY SKU with no sample,
         counting the ones still OPEN, so it is the row count of Section 4 — one number, one list.
         It is smaller than the chart's red segment above and that is not a fault: 133 SKUs are
         Complete, Dropped or Ship Finished with no sample ever raised, which is history on a
         portfolio chart and would have doubled this card with work nobody has to do. */
      { id: 'noSample', label: t('xReport.owner.noSampleYet'), value: counts.noSample, tone: 'kv-rew', sub: t('xReport.owner.noSampleSub') },
    ],
    [
      /* Max, 2026-10-15: "thêm cả trong Project owner report" — the same card the tracker gained. */
      { id: 'priority', label: t('st.ptPriority'), value: counts.priority, tone: 'kv-pri' },
      { id: 'onTrack', label: t('st.ptOnTrack'), value: counts.onTrack, tone: 'kv-conf' },
      { id: 'concern', label: t('st.ptConcern'), value: counts.concern, tone: 'kv-pend' },
      { id: 'delayed', label: t('st.ptDelayed'), value: counts.delayed, tone: 'kv-rew' },
      { id: 'hold', label: t('st.ptOnHold'), value: counts.hold, tone: 'kv-drop' },
      { id: 'ship', label: t('st.ptShipFinished'), value: counts.shipFinished, tone: 'kv-ship' },
    ],
  ];
  return (
    <div className="rp-owner-kpis">
      {rows.map((row, i) => (
        <div className="rp-owner-kpi-row" key={i}>
          {row.map((c) => (
            <div className={`rp-owner-kpi ${c.tone || ''}`} key={c.id}>
              <div className="rp-owner-kpi-l">{c.label.toUpperCase()}</div>
              <div className="rp-owner-kpi-v">{c.value}</div>
              {!!c.sub && <div className="rp-owner-kpi-s">{c.sub}</div>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/* The project table both sections use. Written once on purpose: the "approved this week" table is the
   same table with the approval date on the end (Max, 2026-08-24, "nội dung bảng như bảng dưới luôn"),
   and two hand-written copies is exactly how the columns would drift apart.
   No Owner column — the tab is already filtered by owner and Section 1 counts by owner. SKU sits right
   after Category, before PIC (Max, 2026-08-24). */
const OwnerProjectTable = ({ rows, showOwner = false, showApproved = false, emptyText }) => {
  const { t } = useTranslation();
  return (
  <table className="rp-table rp-owner-status-tbl">
    {/* Widths live on the columns, not on th:nth-child: two of these columns are optional, and every
        nth-child rule silently pointed at the wrong column the moment one was added or removed. */}
    <colgroup>
      <col className="oc-id" />
      <col className="oc-name" />
      <col className="oc-cat" />
      <col className="oc-sku" />
      {showOwner && <col className="oc-owner" />}
      <col className="oc-pic" />
      <col className="oc-exf" />
      <col className="oc-weeks" />
      <col className="oc-pct" />
      <col className="oc-status" />
      {showApproved && <col className="oc-appr" />}
    </colgroup>
    <thead>
      <tr>
        <th>{t('xReport.col.prjId')}</th>
        <th>{t('xReport.col.project')}</th>
        <th>{t('xReport.col.category')}</th>
        <th className="num">{t('xReport.col.sku')}</th>
        {showOwner && <th>{t('xReport.col.prjOwner')}</th>}
        <th>{t('xReport.col.picAssigned')}<span className="rp-th-note">{t('xReport.thNote.devDesigner')}</span></th>
        <th>{t('xReport.col.exfDate')}<span className="rp-th-note">{t('xReport.thNote.exfRule')}</span></th>
        <th className="num">{t('xReport.col.weeksLeft')}</th>
        <th className="num">{t('xReport.col.progress')}<span className="rp-th-note">{t('xReport.thNote.avgSkus')}</span></th>
        <th>{t('xReport.col.status')}<span className="rp-th-note">{t('xReport.thNote.mostActive')}</span></th>
        {showApproved && <th>{t('xReport.col.approved')}<span className="rp-th-note">{t('xReport.thNote.dateBy')}</span></th>}
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.key}>
          <td className="mono">{r.groupId}</td>
          <td>{r.name}</td>
          <td>{r.category || '—'}</td>
          <td className="num">{r.skuCount}</td>
          {showOwner && <td>{unassignedText(r.owner || UNASSIGNED)}</td>}
          <td>{r.pic || '—'}</td>
          <td className="nw">{r.exf || 'TBA'}</td>
          <td className="num nw">{weeksLeftText(r.exf)}</td>
          <td className="num">{r.pct}%</td>
          <td><span className={`rp-pill ${ownerStatusClass(r.status)}`}>{valueLabel(r.status) || '—'}</span></td>
          {showApproved && (
            <td className="nw" title={r.approvedAt || ''}>
              {fmtApprovedDay(r.approvedAt)}{r.approvedBy ? ` · ${r.approvedBy}` : ''}
            </td>
          )}
        </tr>
      ))}
      {!rows.length && (
        <tr>
          <td colSpan={9 + (showOwner ? 1 : 0) + (showApproved ? 1 : 0)} className="rp-owner-empty">{emptyText}</td>
        </tr>
      )}
    </tbody>
  </table>
  );
};

/* One table per 20 rows, the same rule the Integrated Development Team tab uses (Max, 2026-08-24):
   under 20 rows one table, 20–40 two even tables, above that one per 20 — so 106 projects read as six
   blocks of ~18 instead of one endless grid, and each block fits a printed page. splitSampleRows is
   the dev tab's own helper, reused rather than copied so the two tabs cannot drift apart. */
const OWNER_PAGE_SIZE = 20;
const OwnerSplitTables = ({ rows, showOwner = false, showApproved = false, emptyText }) => {
  const { t } = useTranslation();
  if (!rows.length) {
    return <OwnerProjectTable rows={rows} showOwner={showOwner} showApproved={showApproved} emptyText={emptyText} />;
  }
  return (
    <>
      {splitSampleRows(rows, OWNER_PAGE_SIZE).map((chunk, idx) => (
        <div key={`t${idx}`} style={{ marginTop: idx ? 14 : 0 }}>
          {/* Only when there is more than one block — a single table needs no "1-19 of 19". */}
          {chunk.total > chunk.rows.length && (
            <div className="rp-subsec">
              <span style={{ fontWeight: 400 }}>{t('xReport.owner.chunk', { start: chunk.start, end: chunk.end, total: chunk.total })}</span>
            </div>
          )}
          <OwnerProjectTable
            rows={chunk.rows}
            showOwner={showOwner}
            showApproved={showApproved}
            emptyText={emptyText}
          />
        </div>
      ))}
    </>
  );
};

const OwnerApprovedSection = ({ rows, range, from, to, onFrom, onTo, onReset }) => {
  const { t } = useTranslation();
  return (
  <div className="rp-owner-sec">
    <div className="rp-sec">{t('xReport.owner.approvedSec', { range })}</div>
    {/* Its own dates, not the tab's filters: this table answers "what came in", the rest of the tab
        answers "what are we carrying" (Max, 2026-08-24). */}
    <div className="rp-appr-bar">
      <label className="rp-appr-l">{t('xReport.filter.from')}
        <input type="date" className="rp-appr-d" value={from} max={to || undefined} onChange={(e) => onFrom(e.target.value)} />
      </label>
      <label className="rp-appr-l">{t('xReport.filter.to')}
        <input type="date" className="rp-appr-d" value={to} min={from || undefined} onChange={(e) => onTo(e.target.value)} />
      </label>
      <button type="button" className="rp-filter-btn" onClick={onReset}>{t('xReport.filter.last7')}</button>
    </div>
    {/* The two numbers the period is read for, between the dates that set it and the rows that
        prove it (Max, 2026-08-25). The count text that used to sit in the bar said the same thing
        in smaller type, so it is gone. */}
    <div className="rp-owner-kpi-row rp-appr-kpis">
      <div className="rp-owner-kpi kv-conf">
        <div className="rp-owner-kpi-l">{t('xReport.owner.projectsApproved').toUpperCase()}</div>
        <div className="rp-owner-kpi-v">{rows.length}</div>
        <div className="rp-owner-kpi-s">{t('xReport.owner.inPeriod')}</div>
      </div>
      <div className="rp-owner-kpi kv-rnd">
        <div className="rp-owner-kpi-l">{t('xReport.owner.skusApproved').toUpperCase()}</div>
        <div className="rp-owner-kpi-v">{rows.reduce((sum, r) => sum + (Number(r.skuCount) || 0), 0)}</div>
        <div className="rp-owner-kpi-s">{t('xReport.owner.acrossProjects')}</div>
      </div>
    </div>
    <OwnerSplitTables rows={rows} showOwner showApproved emptyText={t('xReport.owner.noApproved')} />
  </div>
  );
};

const OwnerStatusSection = ({ rows, counts }) => {
  const { t } = useTranslation();
  return (
  <div className="rp-owner-sec">
    <div className="rp-sec">{t('xReport.owner.sec2')}</div>
    <OwnerKpiRows counts={counts} />
    <OwnerSplitTables rows={rows} emptyText={t('xReport.owner.noMatch')} />
  </div>
  );
};

/* SECTION 4 — the SKUs behind the card, so the number can be acted on rather than only read
   (Max, 2026-09-29). Same predicate as the chart and the card, so the row count IS the card.

   It lists the ones still OPEN. A SKU that is Complete, Dropped or Ship Finished with no sample is
   history; putting it here would bury the work under last year's finished collaborations, which is
   what 133 such rows did on the first build.
   Status still has a column, because On Hold and Concern are in here and they read differently from
   On Track. Sorted by owner then EXF, because the person reading their own name wants the nearest
   date first. */
/* SORTABLE, Max 2026-09-30. The columns and the keys they sort on, in one table, because the cell
   below has to answer for the same thing the header claims to order — the trap the Style column in
   Section 1-1 already records. `pic` and `project` are DERIVED for that reason: the cells print a
   joined pair and a group name, so the sort has to be built the same way and cannot just read a
   field. */
const OWNER_NO_SAMPLE_COLS = [
  { t: 'PRJ ID', k: 'groupId' },
  { t: 'Project', k: 'project' },
  { t: 'SKU ID', k: 'id' },
  { t: 'Item', k: 'item' },
  { t: 'Category', k: 'category' },
  { t: 'PRJ owner', k: 'owner' },
  // `note` is a dictionary key, drawn under the label in the reader's language.
  { t: 'PIC assigned', k: 'pic', note: 'xReport.thNote.devDesigner' },
  { t: 'EXF date', k: 'exfDate' },
  { t: 'Status', k: 'status' },
];
const ownerPicText = (r) => uniq([r.assigned, r.designer].filter(Boolean)).join(', ');
/* WHAT THE FUNNEL OFFERS IS WHAT THE CELL PRINTS, which is why these read the printed text rather
   than the field behind it — a menu listing group names under a column showing SKU names is a
   filter for a table nobody is looking at. Empty is left empty on purpose: `filterCellValues`
   turns that into its own "(Blanks)" entry, so "no owner" is tickable like any other value.
   Status is the only one that differs from the SORT getter, and deliberately: sorting wants the
   rank, a tick list wants the word. */
const OWNER_NO_SAMPLE_GETTERS = {
  groupId: (r) => r.groupId,
  project: (r) => r.groupName || r.name,
  id: (r) => r.id,
  item: (r) => r.item,
  category: (r) => r.category,
  owner: (r) => r.owner,
  pic: (r) => ownerPicText(r),
  exfDate: (r) => r.exfDate,
  status: (r) => r.status,
};
const ownerNoSampleValue = (r, key) => {
  /* STATUS SORTS BY HOW ACTIVE THE SKU IS, not alphabetically: the same rank the project fold uses
     (On Track · Concern · Delayed · On Hold · …). Alphabetical would open on Concern and bury On
     Track in the middle, which is not a question anybody clicking a status column is asking. */
  if (key === 'status') {
    const i = OWNER_STATUS_RANK.indexOf(String(r.status || '').trim());
    return i === -1 ? null : i;
  }
  const text = key === 'pic' ? ownerPicText(r)
    : key === 'project' ? String(r.groupName || r.name || '').trim()
      : String(r[key] ?? '').trim();
  /* 'TBA' and '—' are how this report writes "not known yet"; neither is a date or a name, and the
     comparator puts nulls last in both directions. */
  return text && text !== '—' && text !== '-' && text !== 'TBA' && text !== UNASSIGNED ? text : null;
};

const OwnerNoSampleSection = ({ rows }) => {
  const { t } = useTranslation();
  const [sort, toggleSort] = useSortToggle();
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  /* FILTER FIRST, THEN SORT. The other way round sorts rows that are about to be thrown away, and
     on a click that only narrows the list it would re-sort for nothing. */
  const kept = useMemo(
    () => rows.filter((r) => rowPassesColumnFilters(r, colFilters, OWNER_NO_SAMPLE_GETTERS)),
    [rows, colFilters],
  );
  /* Untouched until a header is clicked: with no key the comparator returns the array it was given,
     which is still owner-then-EXF — the order this section was built to open in. */
  const sorted = useMemo(() => reportSort(kept, sort, ownerNoSampleValue), [kept, sort]);
  /* The values a funnel offers are the ones still REACHABLE under the other columns' filters —
     `distinctColumnValues` skips the column being opened, so narrowing by owner leaves that
     owner's projects in the Project menu and takes everybody else's out. A menu that went on
     offering values no row can have is a menu that ticks to zero rows. */
  const filterFor = useCallback((key) => ({
    values: distinctColumnValues(rows, key, colFilters, OWNER_NO_SAMPLE_GETTERS),
    selected: colFilters[key] || [],
    open: !!openFilter && openFilter.key === key,
    anchor: openFilter && openFilter.key === key ? openFilter.anchor : null,
    isDate: key === 'exfDate',
    onOpen: (k, anchor) => setOpenFilter({ key: k, anchor }),
    onClose: () => setOpenFilter(null),
    /* `all` is the menu's FULL value list, not a boolean — read as a flag it is always truthy and
       every apply silently clears the filter instead of setting it. Found by driving the menu and
       watching 125 rows stay 125. The rule is the tracker's own, so the two screens behave the
       same: ticking everything is not a filter, and neither is ticking nothing. */
    onApply: (k, next, all) => {
      setColFilters((p) => {
        const out = { ...p };
        const isAll = all && all.length && next.length >= all.length;
        if (!next || !next.length || isAll) delete out[k];
        else out[k] = next;
        return out;
      });
      setOpenFilter(null);
    },
  }), [rows, colFilters, openFilter]);
  const anyFilter = Object.keys(colFilters).length > 0;
  return (
  <div className="rp-owner-sec">
    <div className="rp-sec">{t('xReport.owner.sec4')}</div>
    {!rows.length ? <p className="rp-empty">{t('xReport.owner.allSampled')}</p> : (
      <>
        {/* THE COUNT FOLLOWS THE FILTER, and says what it is counting when the two differ — a
            number that quietly drops from 82 to 9 with nothing on screen to say why is the same
            trap as a grid narrowed by a funnel nobody can see. Clearing is offered where the
            figure changed, not in a toolbar somewhere else. */}
        <div className="rp-owner-note">
          {anyFilter
            ? t('xReport.owner.nsCountFiltered', { n: sorted.length, total: rows.length })
            : t('xReport.skuN', { n: sorted.length })}
          {anyFilter && (
            <button type="button" className="rp-clearf" onClick={() => setColFilters({})}>{t('xReport.owner.clearFilters')}</button>
          )}
        </div>
        <table className="rp-table rp-owner-status-tbl">
          <thead>
            <tr>
              {OWNER_NO_SAMPLE_COLS.map((c) => (
                <th key={c.k}
                  className={`${sort.key === c.k ? 'rp-sorted' : ''} ${colFilters[c.k] ? 'rp-filtered' : ''}`.trim()}>
                  <SortHead label={headerText(c.t)} sortKey={c.k} sort={sort} onSort={toggleSort}
                    note={c.note ? <span className="rp-th-note">{t(c.note)}</span> : null}
                    filter={filterFor(c.k)} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.id}>
                <td className="nw">{r.groupId || '—'}</td>
                <td>{r.groupName || r.name || '—'}</td>
                <td className="nw">{r.id}</td>
                <td>{r.item || '—'}</td>
                <td>{r.category || '—'}</td>
                <td>{unassignedText(r.owner || UNASSIGNED)}</td>
                <td>{ownerPicText(r) || '—'}</td>
                <td className="nw">{r.exfDate || 'TBA'}</td>
                <td style={{ textAlign: 'center' }}>
                  {r.status ? <span className={`rp-pill ${ownerStatusClass(r.status)}`}>{valueLabel(r.status)}</span> : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )}
  </div>
  );
};

const STAT_DEFS = [
  { key: 'Ship Finished', color: '#2E75B6', tc: '#1F3864' },
  { key: 'On Track', color: '#70AD47', tc: '#276221' },
  { key: 'Concern', color: '#FFC000', tc: '#9C5700' },
  { key: 'Delayed', color: '#FF5252', tc: '#C00000' },
  { key: 'On Hold', color: '#9E9E9E', tc: '#595959' },
];

// Location is resolved from the sample (see rdLocationValue) and prefers the R&D room, so a row whose
// project sits under TS/MS but whose sample was made at R&D still reads "HEVN R&D". That makes it safe
// to pre-select here: only SKU whose samples are exclusively elsewhere drop out.
const EMPTY_RD_FILTERS = {
  status: [], proto: [], category: [], assigned: [], item: [], location: ['HEVN R&D'],
};

/* ---------- page ---------- */

/* THE GUIDE (Max 01/11: every module has one, ending on the manual — this report is in the Project Tracker handbook).
   It walks the Integrated Development Team report, so onBeforeTour switches back to it; the stats and filters are not
   drawn on the Ticket Station tab and the tour skips them there. */
const TOUR = [
  { selector: '.rp-team-switch', title: 'tourProjectReport.teamsT', body: 'tourProjectReport.teamsB' },
  { selector: '.rp-stats', title: 'tourProjectReport.statsT', body: 'tourProjectReport.statsB' },
  { selector: '.rp-filters', title: 'tourProjectReport.filtersT', body: 'tourProjectReport.filtersB' },
  { selector: '.rp-sec', title: 'tourProjectReport.sectionT', body: 'tourProjectReport.sectionB' },
  { selector: '.rp-export-btn', title: 'tourProjectReport.exportT', body: 'tourProjectReport.exportB' },
];

function ProjectReport() {
  const { t } = useTranslation();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [team, setTeam] = useState(() => {
    const t = new URLSearchParams(location.search).get('team');
    return ['inline', 'rd', 'special', 'owner'].includes(t) ? t : 'special';
  });
  // Sales OP team + Project Owner, the two filters Max asked for on this tab.
  const [ownerFilters, setOwnerFilters] = useState({ team: [], owner: [], status: [] });
  const [ownerApprRange, setOwnerApprRange] = useState(defaultApprovedRange);
  const [devFilter, setDevFilter] = useState({ special: [], inline: [] });
  const [statusFilter, setStatusFilter] = useState({ special: [], inline: [] });
  const [openFilter, setOpenFilter] = useState('');
  const [rdFilters, setRdFilters] = useState(EMPTY_RD_FILTERS);
  const [selectedDevs, setSelectedDevs] = useState([]);
  const [justSaved, setJustSaved] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [ticketFilters, setTicketFilters] = useState({ team: [], owner: [], dept: [], requester: [], code: [], status: [] });
  // Open on everything. See the note beside the date boxes for why this one does not default to a week.
  const [ticketRange, setTicketRange] = useState({ from: '', to: '' });
  const contentRef = useRef(null);
  const notesRef = useRef(notes);
  useEffect(() => { notesRef.current = notes; }, [notes]);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [bootRes, notesRes, ticketRes] = await Promise.all([
          fetch(`${API_BASE}/bootstrap`, { credentials: 'include' }),
          fetch(`${PROJECT_API_BASE}/report-notes`, { credentials: 'include' }),
          /* The tickets, for the Ticket Station tab. Its own request rather than a fourth thing in
             the bootstrap: every screen in the portal loads that payload, and the report is the only
             one that wants tickets. A failure here leaves the other three tabs working. */
          fetch(`${PROJECT_API_BASE}/tasks`, { credentials: 'include' }),
        ]);
        const boot = await bootRes.json();
        if (!bootRes.ok) throw new Error(boot.error || i18n.t('xReport.loadFailed'));
        const mappedRounds = (boot.rounds || []).map(mapRound);
        buildSampleQtyMaps(mappedRounds);
        setRounds(mappedRounds);
        setProjects((boot.projects || []).map(mapProject));
        if (notesRes.ok) {
          const notesData = await notesRes.json();
          setNotes(notesData.notes || {});
        }
        if (ticketRes.ok) {
          const td = await ticketRes.json();
          setTickets(td.tasks || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ---- Report for Project Owner ----
     Live projects only, the same rule the Project Tracker uses for its own cards: Pending and Rejected
     belong to Project Approval, and a SKU with no group predates the approval gate. */
  /* Team + Owner apply to everything on the tab. Approval state is a separate question: the tables and
     cards answer to the toggle, the charts never do. */
  const ownerScopedSkus = useMemo(() => projects.filter((p) => (
    (!ownerFilters.team.length || ownerFilters.team.includes(p.team || UNASSIGNED))
    && (!ownerFilters.owner.length || ownerFilters.owner.includes(p.owner || UNASSIGNED))
  )), [projects, ownerFilters]);
  /* Pending is out of this report entirely (Max, 2026-08-24) — the toggle is gone. Approved (plus the
     handful of rows that predate the approval flow and carry no status) is all that counts; Rejected
     work is nobody's plan until it is resubmitted, so it has never been counted here either. */
  const ownerApprovedSkus = useMemo(
    () => ownerScopedSkus.filter((p) => !p.approvalStatus || p.approvalStatus === 'Approved'),
    [ownerScopedSkus],
  );
  /* Status filters PROJECTS, not SKUs (Max, 2026-09-08). The column it answers to is "Status — most
     active SKU", which is a fact about the project, so ticking Concern keeps every SKU of the
     projects that are reported at Concern. Filtering the SKUs themselves would have been easier and wrong: PRJ-062
     would go on showing "3 SKU" in the header and one row underneath, and the SKU cards and charts
     below would count a project's Concern SKU while ignoring its other two.
     Derived from the APPROVED set, the same rows the table builds its own status from — computing it
     from a different set is how a filter and the column it filters start disagreeing. */
  const ownerFilteredSkus = useMemo(() => {
    if (!ownerFilters.status.length) return ownerApprovedSkus;
    const keep = new Set(ownerProjectRows(ownerApprovedSkus)
      .filter((r) => ownerFilters.status.includes(r.status || UNASSIGNED))
      .map((r) => r.key));
    return ownerApprovedSkus.filter((p) => keep.has(p.groupId || p.id || p.name));
  }, [ownerApprovedSkus, ownerFilters.status]);
  const ownerPendingSkuCount = useMemo(
    () => ownerScopedSkus.filter((p) => p.approvalStatus === 'Pending').length,
    [ownerScopedSkus],
  );
  const ownerRows = useMemo(() => ownerProjectRows(ownerFilteredSkus), [ownerFilteredSkus]);
  /* Tickets hang off the PRJ GROUP, and the owner is on the group, so the join is one map. Only the
     groups already on screen count — the tab's filters have narrowed them, and a ticket block that
     ignored that would contradict the tables above it. */
  const ownerTicketRows = useMemo(() => {
    const ownerOf = new Map(ownerRows.map((r) => [r.groupId, r.owner || UNASSIGNED]));
    const map = new Map();
    tickets.forEach((t) => {
      if (!ownerOf.has(t.projectId)) return;
      const key = ownerOf.get(t.projectId);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    return [...map.entries()].map(([key, list]) => {
      const ages = list.filter(TICKET_OPEN).map(ticketAgeDays).filter((d) => d !== null);
      return {
        key,
        raised: list.length,
        open: list.filter(TICKET_OPEN).length,
        overdue: list.filter(ticketOverdue).length,
        neverSent: list.filter((t) => TICKET_OPEN(t) && !t.sentAt).length,
        oldest: ages.length ? Math.max(...ages) : null,
      };
    }).sort((a, b) => b.open - a.open || b.raised - a.raised);
  }, [ownerRows, tickets]);
  // The cards come from the tracker's own counter, so this tab and the Project Tracker cannot disagree.
  const ownerRoundedSkus = useMemo(() => roundedSkuIds(rounds), [rounds]);
  /* The list Section 4 prints and the figure the card shows are the SAME array — the card reads its
     length. Counting them twice is how a card and the list under it come to disagree. */
  const ownerNoSampleRows = useMemo(() => ownerFilteredSkus
    .filter((p) => skuNeedsSample(p, ownerRoundedSkus))
    .sort((a, b) => String(a.owner || UNASSIGNED).localeCompare(String(b.owner || UNASSIGNED))
      || String(a.exfDate || '9999').localeCompare(String(b.exfDate || '9999'))
      || String(a.id).localeCompare(String(b.id), undefined, { numeric: true })),
  [ownerFilteredSkus, ownerRoundedSkus]);
  const ownerCounts = useMemo(() => ({
    ...projectKpiCounts(ownerFilteredSkus),
    noSample: ownerNoSampleRows.length,
  }), [ownerFilteredSkus, ownerNoSampleRows]);
  const ownerCategories = useMemo(
    () => uniq(ownerRows.map((r) => r.category)).sort(),
    [ownerRows],
  );
  /* "project đã được approve trong tuần" (Max, 2026-08-24), now with its own From/To. The default is a
     rolling 7 days ending today, not the Mon–Sun calendar week: a weekly report gets read on a Monday
     morning, when the calendar week is still empty. */
  const ownerApproved = useMemo(() => {
    const day = (v, endOfDay) => {
      if (!v) return null;
      const d = new Date(`${v}T${endOfDay ? '23:59:59' : '00:00:00'}`);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    const from = day(ownerApprRange.from, false);
    const to = day(ownerApprRange.to, true);
    const rows = ownerRows
      .filter((r) => {
        const t = r.approvedAt ? new Date(r.approvedAt) : null;
        if (!t || Number.isNaN(t.getTime())) return false;
        // An empty date box means "no bound on that side", not "match nothing".
        return (!from || t >= from) && (!to || t <= to);
      })
      .sort((a, b) => new Date(b.approvedAt) - new Date(a.approvedAt));
    const fmt = (d) => (d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : t('xReport.any'));
    return { rows, range: `${fmt(from)} – ${fmt(to)}` };
  }, [ownerRows, ownerApprRange, t]);
  /* Filter options come from EVERY project, not just the live ones. Built from the live list, the Owner
     menu would not offer Mia or Mai — all of their projects are pending — so the toggle could show their
     work while the filter denied they existed. */
  const ownerTeamValues = useMemo(() => uniq(projects.map((p) => p.team || UNASSIGNED)).sort(), [projects]);
  const ownerOwnerValues = useMemo(() => uniq(projects.map((p) => p.owner || UNASSIGNED)).sort(), [projects]);
  /* Excel's rule, the one the Sample Tracker's column menus already follow: a column's own filter is
     ignored when its list is built, so unticking a value does not make it vanish from the list you
     unticked it in. Team and Owner still scope it, or the menu would offer statuses that exist only
     in a team you have filtered out and answer with nothing. */
  const ownerStatusValues = useMemo(
    () => uniq(ownerProjectRows(ownerApprovedSkus).map((r) => r.status || UNASSIGNED)).sort(),
    [ownerApprovedSkus],
  );
  const handleOwnerFilterChange = useCallback((id, value, checked, values = []) => {
    setOwnerFilters((prev) => ({ ...prev, [id]: nextFilterSelection(prev[id], value, checked, values) }));
  }, []);
  const clearOneOwnerFilter = useCallback((id, checked) => {
    setOwnerFilters((prev) => ({ ...prev, [id]: checked ? [] : [FILTER_NONE] }));
  }, []);

  /* ---- Ticket Station tab (Max, 2026-09-14) ----
     Four filters, the same MultiFilter contract the Project Owner tab uses. Each list is built from
     the tickets left after the OTHER three filters — Excel's rule, already followed everywhere else
     here — so unticking a value never makes it disappear from the menu you unticked it in. */
  /* Sales OP Team and Project Owner, the two the Project Owner tab carries — Max, 2026-09-15.
     Both come off the TICKET itself: the ticket hangs on a PRJ group, and the group's team and
     owner are already stamped on the row by taskTrackerMapRow, so there is no second join here to
     keep in step with the one on the other tab. */
  const ticketMatches = useCallback((t, skip) => {
    const test = (id, v) => (id === skip
      || !ticketFilters[id].length
      || ticketFilters[id].includes(v || UNASSIGNED));
    /* The date range is never skipped. `skip` exists so a menu still offers the values its own
       choice would hide; the dates have no menu, so there is nothing to keep offering. An empty box
       is "no bound on that side", not "match nothing" — the same rule as the approved table. */
    const raised = t.createdAt ? new Date(t.createdAt) : null;
    const bound = (v, endOfDay) => {
      if (!v) return null;
      const d = new Date(`${v}T${endOfDay ? '23:59:59' : '00:00:00'}`);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    const from = bound(ticketRange.from, false);
    const to = bound(ticketRange.to, true);
    if (from || to) {
      // A ticket with no raised date cannot be placed in a period, so a period excludes it.
      if (!raised || Number.isNaN(raised.getTime())) return false;
      if ((from && raised < from) || (to && raised > to)) return false;
    }
    return test('team', t.teamName) && test('owner', t.ownerName)
      && test('dept', t.department) && test('requester', t.assignee)
      && test('code', t.taskType) && test('status', t.status || 'Pending');
  }, [ticketFilters, ticketRange]);
  const ticketRows = useMemo(() => tickets.filter((t) => ticketMatches(t, null)), [tickets, ticketMatches]);
  const ticketValues = useMemo(() => ({
    team: uniq(tickets.filter((t) => ticketMatches(t, 'team')).map((t) => t.teamName || UNASSIGNED)).sort(),
    owner: uniq(tickets.filter((t) => ticketMatches(t, 'owner')).map((t) => t.ownerName || UNASSIGNED)).sort(),
    dept: uniq(tickets.filter((t) => ticketMatches(t, 'dept')).map((t) => t.department || UNASSIGNED)).sort(),
    requester: uniq(tickets.filter((t) => ticketMatches(t, 'requester')).map((t) => t.assignee || UNASSIGNED)).sort(),
    code: uniq(tickets.filter((t) => ticketMatches(t, 'code')).map((t) => t.taskType || UNASSIGNED)).sort(),
    status: uniq(tickets.filter((t) => ticketMatches(t, 'status')).map((t) => t.status || 'Pending')).sort(),
  }), [tickets, ticketMatches]);
  const handleTicketFilterChange = useCallback((id, value, checked, values = []) => {
    // '__all__' is the Clear Filters button, which has no menu of its own to speak through.
    if (id === '__all__') {
      setTicketFilters({ team: [], owner: [], dept: [], requester: [], code: [], status: [] });
      // The dates are a filter like any other; a Clear that left them set would be a button
      // that says it cleared everything while a period quietly went on hiding rows.
      setTicketRange({ from: '', to: '' });
      return;
    }
    setTicketFilters((prev) => ({ ...prev, [id]: nextFilterSelection(prev[id], value, checked, values) }));
  }, []);
  const clearOneTicketFilter = useCallback((id, checked) => {
    setTicketFilters((prev) => ({ ...prev, [id]: checked ? [] : [FILTER_NONE] }));
  }, []);

  const teamKey = team === 'inline' ? 'inline' : 'special';
  /* SKU id -> the project it belongs to. `mapProject` RENAMES as it maps — the payload's
     `projectItem` becomes `name` and `subItem` becomes `item` — so reading `projectItem` off the
     mapped rows found nothing on every row and the column simply looked unchanged. Read off the
     shape this component actually holds.

     THE PROJECT IS THE GROUP, not the SKU row's own `project_name` — Max, 2026-09-24, on
     SMP-0284/85 printing "Morning Chair - Charcoal" over "Morning Chair - Charcoal". That column is
     per SKU and newer projects simply repeat the item in it: 72 of 510 SKUs carry a `project_name`
     identical to their own `item_name`, and 120 carry one that disagrees with the group they belong
     to. The PRJ row is what the tracker calls the project and what somebody recognises, so
     `groupName` leads; `name` is the fallback for a SKU with no group. */
  const projectNameBySku = useMemo(() => {
    const m = new Map();
    (projects || []).forEach((p) => {
      if (!p.id) return;
      m.set(String(p.id), String(p.groupName || p.name || '').trim());
    });
    return m;
  }, [projects]);
  const allSampleRows = useMemo(() => latestReportSampleRows(rounds, projectNameBySku),
    [rounds, projectNameBySku]);

  const activeProjects = useMemo(
    () => projects.filter((p) => p.status !== 'Dropped' && projectMatchesReportTeam(p, team)),
    [projects, team],
  );
  const allActiveProjects = useMemo(() => projects.filter((p) => p.status !== 'Dropped'), [projects]);

  const teamDevSelected = devFilter[teamKey];
  const teamStatusSelected = statusFilter[teamKey];
  const isDevSelected = useCallback(
    (dev) => !teamDevSelected.length || teamDevSelected.indexOf(dev || 'Unassigned') !== -1,
    [teamDevSelected],
  );
  const isStatusSelected = useCallback(
    (status) => !teamStatusSelected.length || teamStatusSelected.indexOf(status || 'Unassigned') !== -1,
    [teamStatusSelected],
  );
  // The stat bar reflects only the developer filter; the project sections also honour Status.
  const filterSection = useCallback(
    (list, includeStatus) => (list || []).filter((p) => (
      isDevSelected(teamSectionDeveloper(p)) && (!includeStatus || isStatusSelected(p.status))
    )),
    [isDevSelected, isStatusSelected],
  );

  // "special" reports over every active project; "inline" only over its own team slice.
  const sectionSource = team === 'special' ? allActiveProjects : activeProjects;
  const sectionActive = useMemo(() => filterSection(sectionSource, true), [filterSection, sectionSource]);
  const chartActive = useMemo(() => filterSection(sectionSource, false), [filterSection, sectionSource]);

  const devFilterValues = useMemo(() => {
    const values = [];
    const push = (d) => { if (d && values.indexOf(d) === -1) values.push(d); };
    if (team === 'special') {
      allSampleRows.forEach((r) => push(r.developer));
      specialPostHandoffProjects(allActiveProjects).forEach((p) => push(specialReportDeveloper(p)));
    }
    developmentProjects(sectionSource).concat(onHoldProjects(sectionSource)).forEach((p) => push(teamSectionDeveloper(p)));
    return values.sort();
  }, [team, allSampleRows, allActiveProjects, sectionSource]);

  // Project statuses that exist, then any round outcome that shows up in the sample list.
  const statusFilterValues = useMemo(() => {
    const values = STATUS_FILTER_BASE.filter((s) => sectionSource.some((p) => p.status === s));
    STATUS_FILTER_ROUND.forEach((s) => {
      if (values.indexOf(s) === -1 && allSampleRows.some((row) => row.status === s)) values.push(s);
    });
    return values;
  }, [sectionSource, allSampleRows]);

  const perDeveloperRows = useMemo(
    () => allSampleRows.filter((row) => isDevSelected(row.developer) && isStatusSelected(row.status)),
    [allSampleRows, isDevSelected, isStatusSelected],
  );

  const postHandoffList = useMemo(() => specialPostHandoffProjects(allActiveProjects)
    .filter((p) => team !== 'special' || isDevSelected(specialReportDeveloper(p)))
    .sort(sortByProjectName), [allActiveProjects, team, isDevSelected]);

  // The R&D report tracks work still in R&D's hands, so protos that are finished or parked awaiting
  // someone else's feedback are left out (Max, 2026-08-05). Excluding them here rather than in
  // rdFiltered also keeps the filter option lists and the header counts honest.
  // A sample can be made in the R&D room while its project is allocated to TS/MS/Others, so the
  // report is the UNION of the two: allocated to R&D, or has a sample whose Location is R&D. Keeping
  // the project grain also keeps the 18 SKU that are allocated to R&D but have no sample yet.
  const rdSampleInfo = useMemo(() => {
    const map = new Map();
    rounds.forEach((r) => {
      if (!r.project) return;
      const entry = map.get(r.project)
        || { qualifies: false, locs: [], remark: '', remarkRow: -1, roundStatus: '', roundLabel: '',
              roundRow: -1, rdRounds: [], latestLoc: '', latestRow: -1, latestLabel: '' };
      if (r.loc && entry.locs.indexOf(r.loc) === -1) entry.locs.push(r.loc);
      /* WHERE THE SAMPLE IS NOW — the latest round's room, whichever room that is. Max, 2026-09-17,
         on COOLER SHORT and D PACK TALL: "2 mã này sample đang nằm ở MS nhưng tại sao lại có ở
         trong báo cáo của R&D". Their P1 and P2 were built in the R&D room and their P3 went to MS,
         and the report was deciding by the SKU's Allocation field, which still said HEVN R&D. */
      {
        const lrow = Number(r.rowIndex) || 0;
        if (lrow >= entry.latestRow) {
          entry.latestRow = lrow;
          entry.latestLoc = String(r.loc || '').trim();
          entry.latestLabel = String(r.rnd || '').trim();
        }
      }
      const isRdRound = isRdAllocation({ allocation: r.loc }) && rdRoundLive(r, RD_ROUND_STATUSES);
      if (isRdRound) {
        entry.qualifies = true;
        // Latest R&D round wins, by creation order — the same "latest first" the remark below uses, so the
        // two columns keep describing the same round.
        const row = Number(r.rowIndex) || 0;
        if (row >= entry.roundRow) {
          entry.roundRow = row;
          entry.roundStatus = reportRoundStatus(r);
          entry.roundLabel = String(r.rnd || '').trim();
        }
        // Every live R&D round, kept so the remark can cover the option variants of the winning
        // round rather than only whichever of them was read last.
        entry.rdRounds.push({
          label: String(r.rnd || '').trim(),
          option: String(r.opt || '').trim(),
          remark: String(r.remarks || '').trim(),
          qty: r.qty,
          status: reportRoundStatus(r),
          row: Number(r.rowIndex) || 0,
        });
      }
      /* THE REMARK BELONGS TO THE ROUND THE ROW REPORTS, or there is none — Max, 2026-09-24: "sao
         lại lấy remark của location TS vậy". The rank below already put R&D rounds a million above
         the rest so they win; what it could not do was win with an EMPTY note, and there was a
         fallback underneath that took "the latest round with any remark at all".

         Soft Luggage L is what that fallback looks like from the outside. Its latest round is the
         Trial in the R&D room, which carries no note, so the row correctly reported Trial / Mock-up
         — and then printed "P2 is processing…", which is a note about P2, made at TS. One row
         describing two rounds in two rooms, with nothing on it saying so.

         An R&D round is now the ONLY source: if it has nothing to say the cell stays empty, which
         is the truth. Rounds elsewhere are read only while no R&D round has been seen at all, which
         is the orphan case below. */
      if (isRdRound) {
        const rank = Number(r.rowIndex) || 0;
        if (rank >= entry.remarkRow || entry.remarkRow < 0) {
          entry.remark = String(r.remarks || '').trim();
          entry.remarkRow = rank;
        }
      }
      map.set(r.project, entry);
    });
    return map;
  }, [rounds]);
  const rdProjects = useMemo(() => projects
    .filter((p) => {
      const info = rdSampleInfo.get(p.id);
      /* WHERE THE SAMPLE IS NOW decides, not the Allocation field — Max, 2026-09-17, choosing this
         over correcting the eleven rows by hand: "về 2 nhé". The round is what Jenny and Annie
         update as the work moves; Allocation is a box somebody sets once and then forgets, so a
         sample that went to MS for P3 stayed on R&D's report until somebody remembered to go back
         and change it. Measured at the time: ELEVEN live SKUs allocated to HEVN R&D whose latest
         round was already at MS or TS.

         A SKU WITH NO ROUNDS AT ALL still goes by Allocation — there is nothing else to go by, and
         those 18 are exactly the ones R&D is about to start. */
      /* A SKU WITH ROUNDS IS ON R&D'S PLATE ONLY WHILE ONE OF THEM IS — Max, 2026-10-29, on Beach Chair Mesh (re) -
         Egyptian Blue: "này xong mà báo cáo vẫn hiện". Its only round (Photo, R&D room) was Confirmed, yet the row
         stayed, printing "—" for the round, because the SKU's own Proto stage still read "Pending" and nobody updates
         that box. The rounds are what R&D update, so they decide: the latest round in the R&D room AND a live R&D round
         (info.qualifies). Nine such rows went (Egyptian Blue ×4, Field Cover ×4 …). */
      const belongs = info && info.latestRow >= 0
        ? isRdAllocation({ allocation: info.latestLoc }) && info.qualifies
        : isRdAllocation(p);
      /* THE ROUND R&D IS HOLDING, not the SKU's Proto box, says whether the work is finished — the same day, on Foldlite
         Chair (Delayed): its Proto read "Complete" after P10 was confirmed, so the row was hidden while an Exhibition
         Sample was In Production in the R&D room. rdProtoValue reads the round status first, but it was asked here
         BEFORE the row carried one; it is now handed the live round's status — when that round is being WORKED (an On Hold
         round is parked, and then the Proto box still decides: four SKUs set to "Waiting Feedback" whose R&D round is On
         Hold would otherwise have come back). */
      const working = info && info.roundStatus && info.roundStatus !== 'On Hold' ? info.roundStatus : '';
      const protoNow = rdProtoValue({ ...p, rdRoundStatus: working });
      // Only live projects belong in this report (Max, 2026-08-06): On Hold / Dropped / Ship Finished
      // are not R&D's move. Orphan samples are added separately below and keep no project status, so
      // this does not hide them.
      return RD_LIVE_STATUSES.indexOf(String(p.status || '').trim()) !== -1
        && belongs
        && RD_HIDDEN_PROTO.indexOf(protoNow) === -1;
    })
    // Resolve the row's Location from its sample: prefer the R&D room when one of its rounds was
    // made there, otherwise the first sample location, otherwise leave it to the project.
    .map((p) => {
      const info = rdSampleInfo.get(p.id) || {};
      const locs = info.locs || [];
      /* The room the LATEST round is in — the same one the belongs-here test above used. It used to
         prefer any R&D round in the sample's history, which is why the Location filter still caught
         a sample that had moved to MS: the row said R&D because it had once been there. */
      return {
        ...p,
        sampleLoc: info.latestLoc || locs[0] || '',
        sampleRemark: info.remark || '',
        rdRoundStatus: info.roundStatus || '',
        rdRoundLabel: info.roundLabel || '',
      };
    })
    /* One row per OPTION VARIANT of the round R&D is holding (Max, 2026-09-10: "tách dòng đi thay
       vì gộp lại"). A sample whose P1 was made as Egg shape and Straight shape is two samples on
       the bench, with their own quantities and their own remarks — folding them into one row meant
       showing one quantity explained by one of the two notes. A round with a single variant, which
       is 204 of the 206 bases, produces exactly the row it produced before. */
    /* AND THE PLAIN ROUND IS A VARIANT TOO — Max, 2026-10-15, on SMP-0316: its P1 (no option, 1 piece)
       and P1-Black (2 pieces) were folded into one row reading "Morning Chair Side Mesh - Charcoal ·
       2", the Black one's quantity under the plain one's name. Only rounds CARRYING an option were
       counted, so one optioned round beside a plain one never split. Every live R&D round of the
       label R&D is holding counts now, one row each, the latest of any two that share an option;
       each row carries its own quantity, remark and round state, and its Item reads with the
       option the way the Sample Tracker writes it ("…_Option: Black"). */
    .flatMap((p) => {
      const info = rdSampleInfo.get(p.id) || {};
      const byOption = new Map();
      (info.rdRounds || []).filter((x) => x.label === info.roundLabel).forEach((x) => {
        const cur = byOption.get(x.option);
        if (!cur || x.row >= cur.row) byOption.set(x.option, x);
      });
      const variants = [...byOption.values()].sort((a, b) => a.row - b.row);
      if (variants.length < 2) return [p];
      return variants.map((v) => ({
        ...p,
        variantOption: v.option,
        // Its own round's numbers, not the base's: the shared map keys by Base ID and cannot tell
        // two variants apart.
        sampleQtyOverride: (v.qty === null || v.qty === undefined || v.qty === '') ? 'TBA' : v.qty,
        sampleRemark: v.remark || '',
        rdRoundStatus: v.status || p.rdRoundStatus,
      }));
    }), [projects, rdSampleInfo]);

  // Samples made in the R&D room that are not linked to any project SKU would otherwise be invisible
  // here, because a row IS a project SKU. They are surfaced as flagged rows so R&D can see them and
  // ask DEV to add the missing link, instead of the report silently dropping real work.
  const rdOrphanSamples = useMemo(() => {
    const byBase = new Map();
    rounds.forEach((r) => {
      if (r.project) return;
      if (!isRdAllocation({ allocation: r.loc })) return;
      if (!rdRoundLive(r, RD_ORPHAN_STATUSES)) return;
      /* Latest round wins, the way the project path above already does it. Taking the FIRST one meant
         SMP-0145 was still shown as its P1 internal trial two weeks after P2 had been submitted. */
      const row = Number(r.rowIndex) || 0;
      const seen = byBase.get(r.base);
      if (!seen || row > seen.rowIndex) {
        byBase.set(r.base, {
          rowIndex: row,
          id: `SAMPLE:${r.base}`,
          name: '',
          item: r.style || r.base,
          itemCategory: '',
          typology: '',
          category: '',
          assigned: r.dev || '',
          status: '',
          stages: { proto: '' },
          samples: [r.base],
          allocation: r.loc,
          sampleLoc: r.loc,
          orphanSample: true,
          sampleBase: r.base,
          // No project means no proto stage, so the Proto column shows the round's own status.
          roundStatus: reportRoundStatus(r),
          sampleRemark: String(r.remarks || '').trim(),
        });
      }
    });
    return Array.from(byBase.values());
  }, [rounds]);
  // One predicate, with `skip` so a dropdown's own selection is ignored when building its options —
  // otherwise picking a value would immediately hide every other value in that same list.
  const rdMatches = useCallback((p, skip) => {
    const f = rdFilters;
    const on = (k, v) => k === skip || !f[k].length || f[k].indexOf(v) !== -1;
    return on('status', String(p.status || ''))
      && on('proto', rdProtoValue(p))
      && on('category', reportProjectCategory(p))
      && on('assigned', String(p.assigned || ''))
      && on('item', rdItemValue(p))
      && on('location', rdLocationValue(p));
  }, [rdFilters]);
  const rdAllRows = useMemo(() => rdProjects.concat(rdOrphanSamples), [rdProjects, rdOrphanSamples]);
  const rdFiltered = useMemo(() => rdAllRows.filter((p) => rdMatches(p, null)), [rdAllRows, rdMatches]);
  // Options follow what is actually left in the table, so the lists shrink as filters are applied.
  const rdOptions = useMemo(() => {
    const pick = (key, mapFn) => uniqReportValues(rdAllRows.filter((p) => rdMatches(p, key)).map(mapFn));
    return {
      status: pick('status', (p) => p.status),
      proto: pick('proto', rdProtoValue),
      category: pick('category', reportProjectCategory),
      assigned: pick('assigned', (p) => p.assigned),
      item: pick('item', rdItemValue),
      location: pick('location', rdLocationValue),
    };
  }, [rdAllRows, rdMatches]);

  const overviewDevs = useMemo(() => {
    const raw = allActiveProjects.filter((p) => {
      const statusOk = p.status === 'On Track' || p.status === 'Concern' || p.status === 'Delayed';
      return statusOk && !hasStageValue(p.stages.handoff);
    });
    const devs = uniq(raw.map((p) => p.assigned));
    specialPostHandoffProjects(allActiveProjects).forEach((p) => {
      const owner = stackChartOwner(p);
      if (owner && devs.indexOf(owner) === -1) devs.push(owner);
    });
    return devs.sort();
  }, [allActiveProjects]);

  useEffect(() => {
    setSelectedDevs(overviewDevs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewDevs.join('|')]);

  const handleToggleDev = useCallback((dev, allDevs) => {
    setSelectedDevs((prev) => {
      const idx = prev.indexOf(dev);
      if (idx > -1) {
        if (prev.length === 1) return prev;
        return prev.filter((d) => d !== dev);
      }
      return allDevs.filter((d) => prev.indexOf(d) > -1 || d === dev);
    });
  }, []);

  const handleNoteChange = useCallback((key, value) => {
    setNotes((prev) => ({ ...prev, [key]: value }));
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await fetch(`${PROJECT_API_BASE}/report-notes`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ notes: notesRef.current }),
        });
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 1800);
      } catch (e) { /* best-effort autosave */ }
    }, 1200);
  }, []);

  const toggleOpenFilter = useCallback((id) => {
    setOpenFilter((prev) => (prev === id ? '' : id));
  }, []);
  // Close an open filter menu when the click lands anywhere outside it.
  useEffect(() => {
    if (!openFilter) return undefined;
    const onDocMouseDown = (event) => {
      if (!event.target.closest || !event.target.closest('.rp-filter')) setOpenFilter('');
    };
    const onKeyDown = (event) => { if (event.key === 'Escape') setOpenFilter(''); };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openFilter]);
  // the team views share filter ids ("assigned"), so close any menu when switching tabs
  const switchTeam = useCallback((next) => {
    setTeam(next);
    setOpenFilter('');
  }, []);
  const handleTeamFilterChange = useCallback((id, value, checked, values = []) => {
    const setter = id === 'status' ? setStatusFilter : setDevFilter;
    setter((prev) => ({ ...prev, [teamKey]: nextFilterSelection(prev[teamKey], value, checked, values) }));
  }, [teamKey]);
  const clearOneTeamFilter = useCallback((id, checked) => {
    const setter = id === 'status' ? setStatusFilter : setDevFilter;
    setter((prev) => ({ ...prev, [teamKey]: checked ? [] : [FILTER_NONE] }));
  }, [teamKey]);
  const clearTeamFilters = useCallback(() => {
    setDevFilter((prev) => ({ ...prev, [teamKey]: [] }));
    setStatusFilter((prev) => ({ ...prev, [teamKey]: [] }));
    setOpenFilter('');
  }, [teamKey]);
  // checked = tick All (no filter); unchecked = tick nothing (the sentinel hides every row)
  // Pinned snapshots: the user filters, clicks Pin, and the current result is frozen below so a
  // second filter combination can be pinned next to it for comparison in one report.
  const [rdPinned, setRdPinned] = useState([]);
  const clearOneRdFilter = useCallback((id, checked) => {
    setRdFilters((prev) => ({ ...prev, [id]: checked ? [] : [FILTER_NONE] }));
  }, []);
  const handleRdFilterChange = useCallback((id, value, checked, values = []) => {
    setRdFilters((prev) => ({ ...prev, [id]: nextFilterSelection(prev[id], value, checked, values) }));
  }, []);
  const clearRdFilters = useCallback(() => {
    setRdFilters(EMPTY_RD_FILTERS);
    setOpenFilter('');
  }, []);
  // Rows the user has unticked. They stay visible and greyed in the live table — the tick only decides what
  // goes into the NEXT pinned snapshot, so a report can leave out rows without changing what anyone sees now.
  const [rdExcluded, setRdExcluded] = useState({});
  const toggleRdRow = useCallback((key) => {
    setRdExcluded((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  }, []);

  const toggleAllRdRows = useCallback(() => {
    const keys = projectSkuRows(rdFiltered).map(rdRowKey);
    const anyOn = keys.some((k) => !rdExcluded[k]);
    // If anything is still ticked, untick everything; otherwise tick everything back on.
    setRdExcluded(anyOn ? Object.fromEntries(keys.map((k) => [k, true])) : {});
  }, [rdFiltered, rdExcluded]);

  const pinRdView = useCallback(() => {
    const sorted = rdFiltered.slice().sort((a, b) => (
      String(a.name || '').localeCompare(String(b.name || '')) || String(a.item || '').localeCompare(String(b.item || ''))
    ));
    const skuRows = projectSkuRows(sorted).filter((row) => !rdExcluded[rdRowKey(row)]);
    // Project count comes from the rows that survived, not from `sorted` — otherwise the header would claim
    // projects the snapshot does not contain.
    const keptProjects = uniq(skuRows.map(({ p }) => p.groupId || p.id || p.name));
    setRdPinned((prev) => prev.concat([{
      key: `pin-${prev.length + 1}-${skuRows.length}-${prev.reduce((a, x) => a + x.skuRows.length, 0)}`,
      label: describeRdFilters(rdFilters),
      projects: keptProjects.length,
      skuRows,
    }]));
    setOpenFilter('');
  }, [rdFiltered, rdFilters, rdExcluded]);
  const removeRdPinned = useCallback((key) => {
    setRdPinned((prev) => prev.filter((x) => x.key !== key));
  }, []);

  // Which pins go into the next merge. New pins join the selection, so ticking is only needed to LEAVE one out.
  const [rdPinPicked, setRdPinPicked] = useState({});
  const togglePinPicked = useCallback((key) => {
    setRdPinPicked((prev) => ({ ...prev, [key]: prev[key] === false }));
  }, []);
  const pinIsPicked = useCallback((key) => rdPinPicked[key] !== false, [rdPinPicked]);
  const pickedPins = useMemo(() => rdPinned.filter((pin) => rdPinPicked[pin.key] !== false), [rdPinned, rdPinPicked]);

  /**
   * Merge the ticked pins into ONE new pin and drop the originals.
   *
   * The result is an ordinary pinned snapshot, so it can be ticked and merged again with something else —
   * which is the point: a report is built up in steps. The sources are removed rather than kept, otherwise
   * the next merge would fold a merged pin together with the very rows it already contains.
   *
   * Rows are de-duplicated: overlapping filters (say "Amy" and "All rows") would list the same SKU twice and
   * double every total, and with no "From" column there would be nothing on screen to reveal it.
   */
  const mergePickedPins = useCallback(() => {
    if (pickedPins.length < 2) return;
    const seen = new Set();
    const rows = [];
    let duplicates = 0;
    pickedPins.forEach((pin) => {
      pin.skuRows.forEach((row) => {
        const key = rdRowKey(row);
        if (seen.has(key)) { duplicates += 1; return; }
        seen.add(key);
        rows.push({ ...row, rowKey: key });
      });
    });
    const mergedKeys = new Set(pickedPins.map((pin) => pin.key));
    const label = t('xReport.rd.merged', { n: pickedPins.length, labels: pickedPins.map((pin) => pin.label).join(' + ') });
    setRdPinned((prev) => prev.filter((pin) => !mergedKeys.has(pin.key)).concat([{
      key: `merge-${Date.now()}-${rows.length}`,
      label: duplicates ? t('xReport.rd.mergedDup', { label, n: duplicates }) : label,
      projects: uniq(rows.map(({ p }) => p.groupId || p.id || p.name)).length,
      skuRows: rows,
    }]));
    setRdPinPicked((prev) => {
      const next = { ...prev };
      mergedKeys.forEach((key) => delete next[key]);
      return next;
    });
    setOpenFilter('');
  }, [pickedPins, t]);

  const counts = {};
  STAT_DEFS.forEach((s) => { counts[s.key] = 0; });
  chartActive.forEach((p) => { if (counts[p.status] !== undefined) counts[p.status] += 1; });
  const maxCount = Math.max(...STAT_DEFS.map((s) => counts[s.key]), 1);

  const today = new Date();
  const ds = today.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const teamDef = REPORT_TEAMS[team] || REPORT_TEAMS.special;
  const teamTitle = t(teamDef.labelKey);
  const shared = { notes, justSaved, onNoteChange: handleNoteChange };

  return (
    <div className="pt-report-page">
      {/* The wrap was widened to 1720 so Section 1-1's thirteen headers could carry a sort control
          without breaking words in half. The Project Owner report has no such column and was fine at
          1380 — widening it only stretched its Project column across the screen (Max, 2026-09-10).
          So the width follows the report being read, rather than one number for all three. */}
      {/* The Ticket Station tab reads at the Project Owner width — Max, 2026-09-15. Same shape of
          content: short key columns and counted columns, no prose. */}
      <div className={`rp-wrap${team === 'owner' || team === 'tickets' ? ' rp-wrap-owner' : ''}`}>
        <div className="rp-toolbar">
          <div className="rp-team-switch">
            <button type="button" className={`rp-team-btn${team === 'special' ? ' active' : ''}`} onClick={() => switchTeam('special')}>{t('xReport.tab.special')}</button>
            <button type="button" className={`rp-team-btn${team === 'rd' ? ' active' : ''}`} onClick={() => switchTeam('rd')}>{t('xReport.tab.rd')}</button>
            <button type="button" className={`rp-team-btn${team === 'owner' ? ' active' : ''}`} onClick={() => switchTeam('owner')}>{t('xReport.tab.owner')}</button>
            <button type="button" className={`rp-team-btn${team === 'tickets' ? ' active' : ''}`} onClick={() => switchTeam('tickets')}>{t('xReport.tab.tickets')}</button>
            <button type="button" className="rp-export-btn" onClick={() => exportVisibleReportExcel(contentRef.current, teamTitle, teamDef.title)}>{t('xReport.exportExcel')}</button>
          </div>
          <ModuleHelp manual="project" steps={TOUR} tourKey="projectReport" ready={!loading && !error} onBeforeTour={() => switchTeam('special')} />
        </div>
        {loading ? (
          <div className="rp-loading">{t('xReport.loading')}</div>
        ) : error ? (
          <div style={{ color: '#C00000', fontWeight: 700 }}>{t('xReport.loadError', { error })}</div>
        ) : team === 'tickets' ? (
          <div ref={contentRef}>
            <TicketReportView
              tickets={ticketRows}
              filters={ticketFilters}
              values={ticketValues}
              openFilter={openFilter}
              onToggleOpen={toggleOpenFilter}
              onFilter={handleTicketFilterChange}
              onClear={clearOneTicketFilter}
              generated={ds}
              range={ticketRange}
              onRange={setTicketRange}
            />
          </div>
        ) : team === 'owner' ? (
          <div ref={contentRef}>
            <h1 className="rp-title">{teamTitle}</h1>
            <div className="rp-sub">
              {t('xReport.subOwner', {
                date: ds,
                projects: ownerRows.length,
                skus: ownerFilteredSkus.length,
                owners: uniq(ownerRows.map((r) => r.owner || UNASSIGNED)).length,
              })}
              {/* Approved only — say how much is left out, or the totals look wrong against the tracker. */}
              {!!ownerPendingSkuCount && ` · ${t('xReport.pendingNotCounted', { n: ownerPendingSkuCount })}`}
            </div>
            <div className="rp-filters">
              <MultiFilter id="team" label={t('xReport.filter.allSopTeams')} values={ownerTeamValues} selected={ownerFilters.team} open={openFilter === 'team'} onToggleOpen={toggleOpenFilter} onChange={handleOwnerFilterChange} onClear={clearOneOwnerFilter} labelOf={unassignedText} />
              <MultiFilter id="owner" label={t('xReport.filter.allOwners')} values={ownerOwnerValues} selected={ownerFilters.owner} open={openFilter === 'owner'} onToggleOpen={toggleOpenFilter} onChange={handleOwnerFilterChange} onClear={clearOneOwnerFilter} labelOf={unassignedText} />
              <MultiFilter id="status" label={t('xReport.filter.allStatuses')} values={ownerStatusValues} selected={ownerFilters.status} open={openFilter === 'status'} onToggleOpen={toggleOpenFilter} onChange={handleOwnerFilterChange} onClear={clearOneOwnerFilter} labelOf={(v) => valueLabel(unassignedText(v))} />
              <button type="button" className="rp-filter-btn" onClick={() => setOwnerFilters({ team: [], owner: [], status: [] })}>{t('xReport.filter.clear')}</button>
            </div>
            <OwnerApprovedSection
              rows={ownerApproved.rows}
              range={ownerApproved.range}
              from={ownerApprRange.from}
              to={ownerApprRange.to}
              onFrom={(v) => setOwnerApprRange((p) => ({ ...p, from: v }))}
              onTo={(v) => setOwnerApprRange((p) => ({ ...p, to: v }))}
              onReset={() => setOwnerApprRange(defaultApprovedRange())}
            />
            <br />
            <OwnerPortfolioSection
              projects={ownerRows}
              skus={ownerFilteredSkus}
              categories={ownerCategories}
              rounded={ownerRoundedSkus}
            />
            <br />
            <OwnerStatusSection rows={ownerRows} counts={ownerCounts} />
            <OwnerTicketSection rows={ownerTicketRows} />
            <OwnerNoSampleSection rows={ownerNoSampleRows} />
          </div>
        ) : team === 'rd' ? (
          <div ref={contentRef}>
            <h1 className="rp-title">{teamTitle}</h1>
            <div className="rp-sub">
              {t('xReport.subRd', {
                date: ds, shown: projectCount(rdFiltered), total: projectCount(rdAllRows), skus: skuCount(rdFiltered),
              })}
            </div>
            <div className="rp-filters">
              <MultiFilter id="status" label={t('xReport.filter.allStatus')} values={rdOptions.status} selected={rdFilters.status} open={openFilter === 'status'} onToggleOpen={toggleOpenFilter} onChange={handleRdFilterChange} onClear={clearOneRdFilter} labelOf={valueLabel} />
              <MultiFilter id="proto" label={t('xReport.filter.allProto')} values={rdOptions.proto} selected={rdFilters.proto} open={openFilter === 'proto'} onToggleOpen={toggleOpenFilter} onChange={handleRdFilterChange} onClear={clearOneRdFilter} labelOf={valueLabel} />
              <MultiFilter id="category" label={t('xReport.filter.allCategory')} values={rdOptions.category} selected={rdFilters.category} open={openFilter === 'category'} onToggleOpen={toggleOpenFilter} onChange={handleRdFilterChange} onClear={clearOneRdFilter} />
              <MultiFilter id="assigned" label={t('xReport.filter.allAssigned')} values={rdOptions.assigned} selected={rdFilters.assigned} open={openFilter === 'assigned'} onToggleOpen={toggleOpenFilter} onChange={handleRdFilterChange} onClear={clearOneRdFilter} />
              <MultiFilter id="item" label={t('xReport.filter.allItem')} values={rdOptions.item} selected={rdFilters.item} open={openFilter === 'item'} onToggleOpen={toggleOpenFilter} onChange={handleRdFilterChange} onClear={clearOneRdFilter} />
              <MultiFilter id="location" label={t('xReport.filter.allLocation')} values={rdOptions.location} selected={rdFilters.location} open={openFilter === 'location'} onToggleOpen={toggleOpenFilter} onChange={handleRdFilterChange} onClear={clearOneRdFilter} />
              <button type="button" className="rp-filter-btn" onClick={clearRdFilters}>{t('xReport.filter.clear')}</button>
              <button type="button" className="rp-filter-btn rp-pin-btn" onClick={pinRdView} disabled={!rdFiltered.length}>
                📌 {t('xReport.rd.pinView')}
              </button>
              {rdPinned.length > 1 && (
                <button
                  type="button"
                  className="rp-filter-btn"
                  disabled={pickedPins.length < 2}
                  title={pickedPins.length < 2 ? t('xReport.rd.mergeNeed') : t('xReport.rd.mergeDo')}
                  onClick={mergePickedPins}
                >
                  {t('xReport.rd.mergeSelected', { n: pickedPins.length })}
                </button>
              )}
              {!!rdPinned.length && (
                <button type="button" className="rp-filter-btn" onClick={() => { setRdPinned([]); setRdPinPicked({}); }}>
                  {t('xReport.rd.clearPinned', { n: rdPinned.length })}
                </button>
              )}
            </div>
            <RdTeamSection
              list={rdFiltered}
              totalCount={projectCount(rdAllRows)}
              excluded={rdExcluded}
              onToggleRow={toggleRdRow}
              onToggleAll={toggleAllRdRows}
            />

            {/* Pinned snapshots. They use the same .rp-sec / .rp-table markup as the live table, so
                the Export Excel button picks them up automatically without extra work. */}
            {rdPinned.map((pin, idx) => (
              <div key={pin.key} className="rp-pinned">
                <div className="rp-sec rp-sec-pinned">
                  {rdPinned.length > 1 && (
                    <label className="rp-pin-pick" title={t('xReport.rd.includeInMerge')}>
                      <input type="checkbox" checked={pinIsPicked(pin.key)} onChange={() => togglePinPicked(pin.key)} />
                    </label>
                  )}
                  {t('xReport.rd.pinned', { n: idx + 1, label: pin.label })}
                  <span style={{ fontWeight: 400, fontSize: 12 }}>
                    &nbsp;{t('xReport.projSku', { projects: pin.projects, skus: pin.skuRows.length })}
                  </span>
                  <button type="button" className="rp-pin-remove" title={t('xReport.rd.removeSnapshot')}
                    onClick={() => removeRdPinned(pin.key)}>&times;</button>
                </div>
                <RdRowsTable skuRows={pin.skuRows} />
              </div>
            ))}
          </div>
        ) : (
          <div ref={contentRef}>
            <h1 className="rp-title">{teamTitle}</h1>
            <div className="rp-sub">
              {t('xReport.subDev', { date: ds, projects: projectCount(activeProjects), skus: skuCount(activeProjects) })}
            </div>
            <div className="rp-stats">
              {STAT_DEFS.map((s) => {
                const n = counts[s.key];
                const h = Math.max(4, Math.round((n / maxCount) * 60));
                return (
                  <div className="rp-stat" key={s.key}>
                    <div className="sv" style={{ color: s.tc }}>{n}</div>
                    <div className="bar" style={{ width: 40, height: h, background: s.color }} />
                    <div className="sl">{valueLabel(s.key)}</div>
                  </div>
                );
              })}
            </div>
            <div className="rp-filters">
              <MultiFilter id="assigned" label={t('xReport.filter.allAssigned')} values={devFilterValues} selected={teamDevSelected} open={openFilter === 'assigned'} onToggleOpen={toggleOpenFilter} onChange={handleTeamFilterChange} onClear={clearOneTeamFilter} labelOf={devText} />
              <MultiFilter id="status" label={t('xReport.filter.allStatus')} values={statusFilterValues} selected={teamStatusSelected} open={openFilter === 'status'} onToggleOpen={toggleOpenFilter} onChange={handleTeamFilterChange} onClear={clearOneTeamFilter} labelOf={valueLabel} />
              <button type="button" className="rp-filter-btn" onClick={clearTeamFilters}>{t('xReport.filter.clear')}</button>
            </div>
            <PerDeveloperSection rows={perDeveloperRows} />
            <br />
            <DevelopmentSection active={sectionActive} team={team} {...shared} />
            <br />
            {team !== 'inline' && <PostHandoffSection list={postHandoffList} {...shared} />}
            <br />
            <OnHoldSection active={sectionActive} team={team} {...shared} />
            <br />
            <OverviewSection active={allActiveProjects} devs={overviewDevs} selectedDevs={selectedDevs} onToggleDev={handleToggleDev} rounds={rounds} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectReport;
